// ATLAS option tree — PERSONAL sector (P).
// 13 Endeavours (8 PERSONAL_PRESETS + 5 TELOS personal visions) × 3 Goals ×
// 3 Aims × 3 Today × 3 Steps. First entry at every level = the most common
// answer (auto-filled). See ./types.ts for the shape.
import type { TreeEndeavour } from './types';

export const PERSONAL_TREE: TreeEndeavour[] = [
  // ───────────────────────── 1. Having a strong sense of who I am
  {
    e: 'Having a strong sense of who I am', deity: 'aidos',
    w: 'I\'m building a strong sense of who I am, so my choices come from what I value and not from pressure.',
    k: 'I\'ll know I\'m there when I can name my values and act on them even when a choice costs me something.',
    g: [
      {
        t: 'Name my five core values and live by them this month',
        w: 'Name my five core values and live by them this month, so I have a strong sense of who I am.',
        k: 'I\'ll know I\'m done when my five values are written down and I\'ve made real choices by them all month.',
        a: [
          {
            t: 'Read my values list each morning', c: 'daily · 2 min',
            p: 'Identity is built by what you repeatedly do.',
            w: 'Read my values list each morning, so I live by my five core values this month.',
            k: 'I\'ll know I\'m done when I\'ve read my values list on every morning this week.',
            d: [
              {
                t: "First draft of my five core values written", s: ['List 15 words that describe what matters to me', 'Circle the five I would defend under pressure', 'Write one sentence on why each one matters'],
                w: 'Write my first draft of five core values, so I have a list to read each morning.',
                k: 'I\'ll know I\'m done when five values are written and each has a sentence on why it matters.',
                sw: [
                  'List 15 words that describe what matters to me, so I have raw material for my first draft of five values.',
                  'Circle the five I would defend under pressure, so my draft holds only values I truly stand by.',
                  'Write one sentence on why each one matters, so my draft of five core values is complete.',
                ],
                sk: [
                  'I\'ll know I\'m done when 15 words are written down.',
                  'I\'ll know I\'m done when exactly five words are circled.',
                  'I\'ll know I\'m done when each of the five values has its sentence.',
                ],
              },
              {
                t: "Values card placed where I see it on waking", s: ['Copy the five values onto a card', 'Place the card where I see it on waking', 'Read the card aloud once'],
                w: 'Put the values where I will see them, so I can read them each morning.',
                k: 'I\'ll know I\'m done when a card with my five values sits where I\'ll see it on waking.',
                sw: [
                  'Copy the five values onto a card, so my values are on something I can see and carry.',
                  'Place the card where I see it on waking, so I see my values first thing every morning.',
                  'Read the card aloud once, so my morning reading habit starts today.',
                ],
                sk: [
                  'I\'ll know I\'m done when the card shows all five values.',
                  'I\'ll know I\'m done when the card is in place by my bed or phone.',
                  'I\'ll know I\'m done when I\'ve read all five values aloud.',
                ],
              },
              {
                t: "One fitting and one clashing plan item marked", s: ['Open today\'s calendar or task list', 'Mark one item that fits a value', 'Mark one item that does not fit and decide on it'],
                w: 'Check today\'s plan against my values, so my morning reading shapes what I actually do today.',
                k: 'I\'ll know I\'m done when I\'ve marked one item that fits a value and one that doesn\'t, and decided on it.',
                sw: [
                  'Open today\'s calendar or task list, so I can hold my plan up against my values.',
                  'Mark one item that fits a value, so I see my values already at work in today\'s plan.',
                  'Mark one item that does not fit and decide on it, so my day matches my values.',
                ],
                sk: [
                  'I\'ll know I\'m done when today\'s plan is open in front of me.',
                  'I\'ll know I\'m done when one item is marked with the value it fits.',
                  'I\'ll know I\'m done when I\'ve marked the item and chosen to keep, change or drop it.',
                ],
              },
            ],
          },
          {
            t: 'Test one real choice against my values', c: '3×/week',
            p: 'What you avoid defines you more than what you pursue.',
            w: 'Test one real choice against my values, so I live by them this month and don\'t only list them.',
            k: 'I\'ll know I\'m done when I\'ve tested a real choice against my values on three days this week.',
            d: [
              {
                t: "Today's decision named, checked against a value and chosen", s: ['Name the decision in one line', 'Ask which value it serves or breaks', 'Choose and note the result'],
                w: 'Test one decision I face today, so I check a real choice against my values.',
                k: 'I\'ll know I\'m done when the decision is named, checked against a value and chosen.',
                sw: [
                  'Name the decision in one line, so I know exactly which choice I\'m testing.',
                  'Ask which value it serves or breaks, so I can test the choice against my values.',
                  'Choose and note the result, so today\'s decision rests on my values.',
                ],
                sk: [
                  'I\'ll know I\'m done when the decision is written in one line.',
                  'I\'ll know I\'m done when I\'ve written which value it serves or breaks.',
                  'I\'ll know I\'m done when I\'ve chosen and written the outcome in one line.',
                ],
              },
              {
                t: "What I would do differently on a past choice written", s: ['Recall one choice I regret', 'Name the value it broke', 'Write what I would do now'],
                w: 'Review a past choice that felt wrong, so I learn how my values would have guided it.',
                k: 'I\'ll know I\'m done when I\'ve written what I would do differently now.',
                sw: [
                  'Recall one choice I regret, so I have a real past choice to test against my values.',
                  'Name the value it broke, so I see which value I need to protect.',
                  'Write what I would do now, so I know how to act on that value next time.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve picked one specific choice.',
                  'I\'ll know I\'m done when the broken value is written next to the choice.',
                  'I\'ll know I\'m done when a replacement action is written down.',
                ],
              },
              {
                t: "The action my value asked for taken, despite the cost", s: ['Spot one moment today that tests a value', 'Take the action the value asks for', 'Note how it felt afterwards'],
                w: 'Act on a value when it costs me something, so I test my values on a choice that matters.',
                k: 'I\'ll know I\'m done when I\'ve taken the action my value asked for, even at a cost.',
                sw: [
                  'Spot one moment today that tests a value, so I know where to act on my values.',
                  'Take the action the value asks for, so I act on my value even when it costs me.',
                  'Note how it felt afterwards, so I see what living by a value gives and costs me.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve named one such moment.',
                  'I\'ll know I\'m done when the action is done.',
                  'I\'ll know I\'m done when one line about how it felt is written.',
                ],
              },
            ],
          },
          {
            t: 'Talk through one value with someone I trust', c: 'weekly',
            p: 'A value never spoken aloud is only a wish.',
            w: 'Talk through one value with someone I trust, so I live my values out loud and not only in private.',
            k: 'I\'ll know I\'m done when I\'ve had the conversation this week and written down one change to make.',
            d: [
              {
                t: "A person and a value picked, 20 minutes requested", s: ['Pick one person who knows me well', 'Pick the value I most want to discuss', 'Message them asking for 20 minutes'],
                w: 'Choose the person and the value, so I can talk one value through with someone I trust.',
                k: 'I\'ll know I\'m done when a person and a value are picked and my request for 20 minutes is sent.',
                sw: [
                  'Pick one person who knows me well, so I have someone honest to talk with.',
                  'Pick the value I most want to discuss, so our talk covers a value that matters most to me.',
                  'Message them asking for 20 minutes, so the conversation is actually set up.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve written their name.',
                  'I\'ll know I\'m done when the value is written down.',
                  'I\'ll know I\'m done when the message is sent.',
                ],
              },
              {
                t: "Value conversation held, with notes on what they said", s: ['Share why the value matters to me', 'Ask where they see me live it or not', 'Write down what they said'],
                w: 'Have the conversation, so I talk one value through with someone I trust.',
                k: 'I\'ll know I\'m done when we\'ve talked and I have notes on what they said.',
                sw: [
                  'Share why the value matters to me, so they understand what I\'m talking through.',
                  'Ask where they see me live it or not, so I hear how my value shows up to others.',
                  'Write down what they said, so I keep what I learned from the talk.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve explained the value aloud.',
                  'I\'ll know I\'m done when they\'ve answered.',
                  'I\'ll know I\'m done when their answer is in my notes.',
                ],
              },
              {
                t: "One change picked and placed in tomorrow's plan", s: ['Reread my notes from the conversation', 'Pick one change to make this week', 'Add the change to tomorrow\'s plan'],
                w: 'Act on what they told me, so the talk turns into a change in how I live my value.',
                k: 'I\'ll know I\'m done when one change is picked and sits in tomorrow\'s plan.',
                sw: [
                  'Reread my notes from the conversation, so I remember exactly what they said.',
                  'Pick one change to make this week, so I act on what they told me.',
                  'Add the change to tomorrow\'s plan, so I do the change and don\'t only intend it.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read my notes through once.',
                  'I\'ll know I\'m done when one change is written down.',
                  'I\'ll know I\'m done when the change is in tomorrow\'s calendar or list.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Write a one-page account of who I am and who I am becoming',
        w: 'Write a one-page account of who I am and who I am becoming, so I have a strong sense of who I am.',
        k: 'I\'ll know I\'m done when a one-page account is saved and I\'d stand behind every line.',
        a: [
          {
            t: 'Write 15 minutes on one part of my story', c: '3×/week',
            p: 'You can only manage what you\'re willing to see clearly.',
            w: 'Write 15 minutes on one part of my story, so I build up a one-page account of who I am.',
            k: 'I\'ll know I\'m done when I\'ve written for 15 minutes on three days this week.',
            d: [
              {
                t: "15 minutes of my origin story written, three lines underlined", s: ['Set a 15 minute timer', 'Write the people and places that shaped me', 'Underline the three lines that matter most'],
                w: 'Write about where I come from, so my story has its roots on the page.',
                k: 'I\'ll know I\'m done when 15 minutes are written and my three key lines are underlined.',
                sw: [
                  'Set a 15 minute timer, so I write about where I come from without drifting.',
                  'Write the people and places that shaped me, so my roots are on the page.',
                  'Underline the three lines that matter most, so I know what to carry into my one-page account.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the 15 minutes are used and the page names people and places.',
                  'I\'ll know I\'m done when three lines are underlined.',
                ],
              },
              {
                t: "One honest paragraph on my strengths and flaws written", s: ['List five strengths others point out', 'List three habits I want to change', 'Write one honest paragraph on both'],
                w: 'Write about my strengths and flaws, so my story holds an honest view of who I am.',
                k: 'I\'ll know I\'m done when one paragraph covers both my strengths and my flaws.',
                sw: [
                  'List five strengths others point out, so I have the strengths for my honest paragraph.',
                  'List three habits I want to change, so my story includes my flaws as well.',
                  'Write one honest paragraph on both, so I\'ve written about my strengths and flaws.',
                ],
                sk: [
                  'I\'ll know I\'m done when five strengths are listed.',
                  'I\'ll know I\'m done when three habits are listed.',
                  'I\'ll know I\'m done when the paragraph is written.',
                ],
              },
              {
                t: "One trait to practise this week chosen", s: ['Describe myself one year from now', 'List three traits that person shows daily', 'Pick one trait to practise this week'],
                w: 'Write about who I am becoming, so my story looks forward as well as back.',
                k: 'I\'ll know I\'m done when I\'ve chosen one trait to practise this week.',
                sw: [
                  'Describe myself one year from now, so I can see who I\'m becoming.',
                  'List three traits that person shows daily, so who I\'m becoming is made of daily habits.',
                  'Pick one trait to practise this week, so I start becoming that person now.',
                ],
                sk: [
                  'I\'ll know I\'m done when a description of me a year from now is written.',
                  'I\'ll know I\'m done when three traits are listed.',
                  'I\'ll know I\'m done when one trait is chosen and written down.',
                ],
              },
            ],
          },
          {
            t: 'Ask two people how they see me', c: 'weekly',
            p: 'Others often see what you cannot.',
            w: 'Ask two people how they see me, so my account includes what I can\'t see in myself.',
            k: 'I\'ll know I\'m done when two people have replied and I\'ve written down what repeats.',
            d: [
              {
                t: "Two people chosen and my questions sent", s: ['Pick two people who see me often', 'Write three questions about my character', 'Send the questions by message'],
                w: 'Choose who to ask and what to ask, so I get two honest views of me.',
                k: 'I\'ll know I\'m done when two people are chosen and my questions are sent.',
                sw: [
                  'Pick two people who see me often, so the feedback comes from people who really know me.',
                  'Write three questions about my character, so I ask clear questions about who I am.',
                  'Send the questions by message, so both people have what they need to answer.',
                ],
                sk: [
                  'I\'ll know I\'m done when two names are written down.',
                  'I\'ll know I\'m done when three questions are written.',
                  'I\'ll know I\'m done when both messages are sent.',
                ],
              },
              {
                t: "Both answers read, with surprises and repeats noted", s: ['Read each answer once without replying', 'Highlight what surprised me', 'Note what repeats across both answers'],
                w: 'Read the answers without defending myself, so I take in how others really see me.',
                k: 'I\'ll know I\'m done when I\'ve read both answers and noted what surprised me and what repeats.',
                sw: [
                  'Read each answer once without replying, so I take in their view without arguing.',
                  'Highlight what surprised me, so I see where others see me differently.',
                  'Note what repeats across both answers, so I find what two people agree on.',
                ],
                sk: [
                  'I\'ll know I\'m done when both answers are read and no reply is sent.',
                  'I\'ll know I\'m done when the surprises are highlighted.',
                  'I\'ll know I\'m done when the repeated points are written down.',
                ],
              },
              {
                t: "Two new lines added, one old line cut", s: ['Open my one-page draft', 'Add two lines drawn from the feedback', 'Cut one line that no longer fits'],
                w: 'Fold their feedback into my page, so my account includes how others see me.',
                k: 'I\'ll know I\'m done when two new lines are in my draft and one old line is cut.',
                sw: [
                  'Open my one-page draft, so I can add their feedback to it.',
                  'Add two lines drawn from the feedback, so my page reflects how others see me.',
                  'Cut one line that no longer fits, so my page stays true to the feedback.',
                ],
                sk: [
                  'I\'ll know I\'m done when the draft is open.',
                  'I\'ll know I\'m done when two new lines are in the draft.',
                  'I\'ll know I\'m done when one line is deleted.',
                ],
              },
            ],
          },
          {
            t: 'Edit my one-page account', c: 'weekly',
            p: 'Growth requires honest discomfort, not positive thinking.',
            w: 'Edit my one-page account, so it ends up a true, finished page I\'d stand behind.',
            k: 'I\'ll know I\'m done when the final one-page account is saved.',
            d: [
              {
                t: "Draft cut to one page with no vague sentences", s: ['Open the full draft', 'Delete every vague sentence', 'Check that it fits on one page'],
                w: 'Cut the draft down to one page, so I edit my account into something short enough to reread.',
                k: 'I\'ll know I\'m done when the draft fits on one page with no vague sentences.',
                sw: [
                  'Open the full draft, so I can cut it down to one page.',
                  'Delete every vague sentence, so only clear lines remain on my page.',
                  'Check that it fits on one page, so my account really is one page long.',
                ],
                sk: [
                  'I\'ll know I\'m done when the full draft is open.',
                  'I\'ll know I\'m done when no vague sentence is left.',
                  'I\'ll know I\'m done when the page count shows one page.',
                ],
              },
              {
                t: "Every false-sounding line on my page rewritten honestly", s: ['Read the page aloud slowly', 'Mark any line that feels untrue', 'Rewrite each marked line honestly'],
                w: 'Read it aloud and mark false lines, so my account says only what is true of me.',
                k: 'I\'ll know I\'m done when every marked line has been rewritten honestly.',
                sw: [
                  'Read the page aloud slowly, so I can hear which lines ring false.',
                  'Mark any line that feels untrue, so I know exactly what to fix.',
                  'Rewrite each marked line honestly, so my page is true.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read the whole page aloud.',
                  'I\'ll know I\'m done when each untrue line is marked.',
                  'I\'ll know I\'m done when no marked line is left unrewritten.',
                ],
              },
              {
                t: "Final page saved with a monthly reminder set", s: ['Read the page one last time', 'Save it where I can find it', 'Set a monthly reminder to reread it'],
                w: 'Save the final version, so my edited account is kept and reread.',
                k: 'I\'ll know I\'m done when the final page is saved and a monthly reminder is set.',
                sw: [
                  'Read the page one last time, so I know the final version is right.',
                  'Save it where I can find it, so my final account is kept safe.',
                  'Set a monthly reminder to reread it, so my account stays in front of me.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read it through once more.',
                  'I\'ll know I\'m done when the file is saved in a place I\'ll remember.',
                  'I\'ll know I\'m done when the monthly reminder is set.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Say no to three things that do not fit who I am',
        w: 'Say no to three things that do not fit who I am, so I protect my strong sense of who I am.',
        k: 'I\'ll know I\'m done when I\'ve turned down three things that clashed with who I am.',
        a: [
          {
            t: 'Spot one request that clashes with my values', c: '3×/week',
            p: 'Every yes is a no to something else.',
            w: 'Spot one request that clashes with my values, so I can say no to things that don\'t fit who I am.',
            k: 'I\'ll know I\'m done when I\'ve spotted a clashing request on three days this week.',
            d: [
              {
                t: "My answer ready for any value-clashing request", s: ['List who may ask something of me today', 'Mark any request that clashes with my values', 'Decide my answer before it comes'],
                w: 'Spot today\'s likely requests, so I notice clashing requests before they land.',
                k: 'I\'ll know I\'m done when I\'ve decided my answer to any request that clashes with my values.',
                sw: [
                  'List who may ask something of me today, so I see the requests coming.',
                  'Mark any request that clashes with my values, so I know which requests to refuse.',
                  'Decide my answer before it comes, so I\'m ready to say no.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list of people is written.',
                  'I\'ll know I\'m done when each clashing request is marked.',
                  'I\'ll know I\'m done when my answer is decided.',
                ],
              },
              {
                t: "My short, kind no used on one small request", s: ['Write a short, polite no', 'Say it aloud twice', 'Use it on one small request'],
                w: 'Practise saying no kindly, so I can turn down a clashing request without a fight.',
                k: 'I\'ll know I\'m done when I\'ve used my short no on one small request.',
                sw: [
                  'Write a short, polite no, so I have words ready for saying no kindly.',
                  'Say it aloud twice, so the words come easily when I need them.',
                  'Use it on one small request, so I\'ve practised saying no for real.',
                ],
                sk: [
                  'I\'ll know I\'m done when the no is written.',
                  'I\'ll know I\'m done when I\'ve said it aloud twice.',
                  'I\'ll know I\'m done when I\'ve said no to one small request.',
                ],
              },
              {
                t: "This week's refusals reviewed, noting which felt right", s: ['List the requests I declined this week', 'Note which refusals felt right', 'Note which I would handle differently'],
                w: 'Review the no\'s I gave, so I see which requests clash with my values.',
                k: 'I\'ll know I\'m done when I\'ve noted which refusals felt right and which I\'d change.',
                sw: [
                  'List the requests I declined this week, so I can review my refusals.',
                  'Note which refusals felt right, so I learn which no\'s match my values.',
                  'Note which I would handle differently, so my next no is better.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when the right ones are marked.',
                  'I\'ll know I\'m done when each change is written.',
                ],
              },
            ],
          },
          {
            t: 'Drop one commitment that no longer fits', c: 'weekly',
            p: 'Outgrown commitments cost more than they give.',
            w: 'Drop one commitment that no longer fits, so I say no to something that isn\'t who I am anymore.',
            k: 'I\'ll know I\'m done when I\'ve told the people involved and the commitment is off my calendar.',
            d: [
              {
                t: "Every regular commitment listed and marked drain or match", s: ['Write down every regular commitment', 'Mark the ones that drain me', 'Mark the ones that match my values'],
                w: 'List my current commitments, so I can see which one no longer fits.',
                k: 'I\'ll know I\'m done when every regular commitment is written and marked as a drain or a match.',
                sw: [
                  'Write down every regular commitment, so I can see all I\'ve agreed to.',
                  'Mark the ones that drain me, so I see which may no longer fit.',
                  'Mark the ones that match my values, so I know which are worth keeping.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is complete.',
                  'I\'ll know I\'m done when the draining ones are marked.',
                  'I\'ll know I\'m done when the matching ones are marked.',
                ],
              },
              {
                t: "One commitment to drop chosen, with a reason and date", s: ['Pick the most draining one', 'Write why it no longer fits', 'Decide the date I will step away'],
                w: 'Choose one commitment to drop, so I decide what to let go of this week.',
                k: 'I\'ll know I\'m done when one commitment is chosen with a reason and a date.',
                sw: [
                  'Pick the most draining one, so I choose the commitment to drop.',
                  'Write why it no longer fits, so my reason for dropping it is clear.',
                  'Decide the date I will step away, so the drop has a deadline.',
                ],
                sk: [
                  'I\'ll know I\'m done when one commitment is chosen.',
                  'I\'ll know I\'m done when the reason is written.',
                  'I\'ll know I\'m done when the date is set.',
                ],
              },
              {
                t: "The people involved told and the freed time cleared", s: ['Write a short, respectful message', 'Send it or say it in person', 'Free the time it opens in my calendar'],
                w: 'Tell the people involved, so the commitment is really dropped.',
                k: 'I\'ll know I\'m done when they know and the time is free in my calendar.',
                sw: [
                  'Write a short, respectful message, so I can tell them clearly and kindly.',
                  'Send it or say it in person, so the people involved know.',
                  'Free the time it opens in my calendar, so the dropped commitment stays dropped.',
                ],
                sk: [
                  'I\'ll know I\'m done when the message is written.',
                  'I\'ll know I\'m done when they have been told.',
                  'I\'ll know I\'m done when the slot is cleared in my calendar.',
                ],
              },
            ],
          },
          {
            t: 'Do one thing that feels truly like me', c: '2×/week',
            p: 'Identity is built by what you repeatedly do.',
            w: 'Do one thing that feels truly like me, so I keep what fits who I am while I say no to what doesn\'t.',
            k: 'I\'ll know I\'m done when I\'ve done something that feels like me on two days this week.',
            d: [
              {
                t: "A me-like activity named and scheduled this week", s: ['Recall the last time I felt fully myself', 'Name the activity involved', 'Schedule it for this week'],
                w: 'Pick an activity that feels like me, so I know what to do that feels truly like me.',
                k: 'I\'ll know I\'m done when the activity is named and scheduled for this week.',
                sw: [
                  'Recall the last time I felt fully myself, so I find what feels like me.',
                  'Name the activity involved, so I know what to pick.',
                  'Schedule it for this week, so it actually happens.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve recalled one moment.',
                  'I\'ll know I\'m done when the activity is named.',
                  'I\'ll know I\'m done when it\'s in my calendar.',
                ],
              },
              {
                t: "30 minutes of my me-like activity done, feelings noted", s: ['Clear the time and my phone', 'Do the activity with full attention', 'Note how I feel when I stop'],
                w: 'Do it for at least 30 minutes, so I do one thing that feels truly like me today.',
                k: 'I\'ll know I\'m done when 30 minutes are done and I\'ve noted how I feel.',
                sw: [
                  'Clear the time and my phone, so I can do the activity for 30 minutes.',
                  'Do the activity with full attention, so it counts as doing something truly me.',
                  'Note how I feel when I stop, so I learn what feeling like myself is like.',
                ],
                sk: [
                  'I\'ll know I\'m done when my phone is away and the time is clear.',
                  'I\'ll know I\'m done when I\'ve done it for 30 minutes.',
                  'I\'ll know I\'m done when one line about how I feel is written.',
                ],
              },
              {
                t: "Next week's slot blocked and prepared", s: ['Block the same slot next week', 'Tell one person it is not available', 'Prepare anything I need in advance'],
                w: 'Protect the time for next week, so I keep doing what feels like me.',
                k: 'I\'ll know I\'m done when next week\'s slot is blocked and prepared.',
                sw: [
                  'Block the same slot next week, so the time is held for me.',
                  'Tell one person it is not available, so nobody books over my time.',
                  'Prepare anything I need in advance, so next week\'s session starts without friction.',
                ],
                sk: [
                  'I\'ll know I\'m done when the slot is blocked in my calendar.',
                  'I\'ll know I\'m done when I\'ve told them.',
                  'I\'ll know I\'m done when what I need is ready.',
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // ───────────────────────── 2. Having a regular creative or expressive practice
  {
    e: 'Having a regular creative or expressive practice', deity: 'pyda',
    w: 'I\'m building a regular creative practice, so I have a daily outlet that is mine and my ideas become real things.',
    k: 'I\'ll know I\'m there when making something is a steady part of my week and I finish and share what I make.',
    g: [
      {
        t: 'Make something creative on 12 days this month',
        w: 'Make something creative on 12 days this month, so I have a regular creative practice.',
        k: 'I\'ll know I\'m done when I\'ve made something on 12 different days this month.',
        a: [
          {
            t: 'Create for 20 minutes at a fixed time', c: 'daily · 20 min',
            p: 'Show up at the same hour and the work will follow.',
            w: 'Create for 20 minutes at a fixed time, so I make something on 12 days this month.',
            k: 'I\'ll know I\'m done when I\'ve created for 20 minutes at my fixed time on most days this week.',
            d: [
              {
                t: "20 minutes of making done", s: ['Clear a small workspace', 'Set a 20 minute timer', 'Make something without editing it'],
                w: 'Do today\'s 20 minute session, so I create at my fixed time.',
                k: 'I\'ll know I\'m done when I\'ve made something for 20 minutes by tonight.',
                sw: [
                  'Clear a small workspace, so I can start creating without hunting for things.',
                  'Set a 20 minute timer, so my session has a clear start and end.',
                  'Make something without editing it, so I use the full 20 minutes making and not fixing.',
                ],
                sk: [
                  'I\'ll know I\'m done when my workspace is clear.',
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the timer rings and I have something made.',
                ],
              },
              {
                t: "Creative start time set and session begun on time", s: ['Pick the exact start time', 'Silence my phone for the session', 'Start the timer on time'],
                w: 'Protect the creative slot, so I create at the same time every day.',
                k: 'I\'ll know I\'m done when my start time is set and my session starts on time.',
                sw: [
                  'Pick the exact start time, so my creative slot is fixed.',
                  'Silence my phone for the session, so nothing pulls me out of my 20 minutes.',
                  'Start the timer on time, so I begin at my fixed time.',
                ],
                sk: [
                  'I\'ll know I\'m done when the time is written down.',
                  'I\'ll know I\'m done when my phone is silent.',
                  'I\'ll know I\'m done when the timer starts at the set minute.',
                ],
              },
              {
                t: "One small piece finished and saved", s: ['Choose a piece that takes under 20 minutes', 'Reach the end, rough is fine', 'Save it in my creative folder'],
                w: 'Finish one small piece, so my 20 minutes ends with something complete.',
                k: 'I\'ll know I\'m done when one small piece is finished and saved.',
                sw: [
                  'Choose a piece that takes under 20 minutes, so I can finish it in one session.',
                  'Reach the end, rough is fine, so I finish a small piece today.',
                  'Save it in my creative folder, so my finished piece is kept with my other work.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve named the piece.',
                  'I\'ll know I\'m done when the piece has an ending.',
                  'I\'ll know I\'m done when the piece is in my creative folder.',
                ],
              },
            ],
          },
          {
            t: 'Set out tomorrow\'s materials each evening', c: 'daily · 5 min',
            p: 'Remove friction and the habit runs itself.',
            w: 'Set out tomorrow\'s materials each evening, so starting my next session takes no effort.',
            k: 'I\'ll know I\'m done when my materials were set out on every evening this week.',
            d: [
              {
                t: "Tomorrow's creative tool laid out and ready", s: ['Pick the tool I will use tomorrow', 'Lay it out where I will work', 'Charge or sharpen anything that needs it'],
                w: 'Set out materials for tomorrow, so tomorrow\'s session starts with everything ready.',
                k: 'I\'ll know I\'m done when tomorrow\'s tool is laid out and ready to use.',
                sw: [
                  'Pick the tool I will use tomorrow, so I know what to lay out.',
                  'Lay it out where I will work, so I can start the moment I sit down.',
                  'Charge or sharpen anything that needs it, so nothing stops me tomorrow.',
                ],
                sk: [
                  'I\'ll know I\'m done when the tool is chosen.',
                  'I\'ll know I\'m done when the tool is on my workspace.',
                  'I\'ll know I\'m done when everything works.',
                ],
              },
              {
                t: "Workspace cleared with one missing supply ordered or bought", s: ['Clear the desk or table', 'List any supplies I am missing', 'Order or buy one missing item'],
                w: 'Tidy and restock the workspace, so my space is ready for each evening\'s setup.',
                k: 'I\'ll know I\'m done when the workspace is clear and one missing item is on order or bought.',
                sw: [
                  'Clear the desk or table, so my workspace is ready to use.',
                  'List any supplies I am missing, so I know what to restock.',
                  'Order or buy one missing item, so my workspace is better stocked.',
                ],
                sk: [
                  'I\'ll know I\'m done when the surface is clear.',
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when the item is ordered or bought.',
                ],
              },
              {
                t: "Tomorrow's first step written and left on my workspace", s: ['Look at what I made today', 'Write the next step in one line', 'Place the note on my workspace'],
                w: 'Decide tomorrow\'s first move, so tomorrow\'s session starts with a clear first step.',
                k: 'I\'ll know I\'m done when tomorrow\'s first step is written and sits on my workspace.',
                sw: [
                  'Look at what I made today, so I see where to pick up tomorrow.',
                  'Write the next step in one line, so I know exactly how to begin tomorrow.',
                  'Place the note on my workspace, so I see my first move the moment I sit down.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve reviewed today\'s work.',
                  'I\'ll know I\'m done when the step is written in one line.',
                  'I\'ll know I\'m done when the note is on my workspace.',
                ],
              },
            ],
          },
          {
            t: 'Share one piece of work with someone', c: 'weekly',
            p: 'Creation needs a witness.',
            w: 'Share one piece of work with someone, so my creating has a witness and keeps going.',
            k: 'I\'ll know I\'m done when I\'ve sent one piece to someone this week and noted their reaction.',
            d: [
              {
                t: "One piece to share picked and given a small fix", s: ['Review this week\'s work', 'Pick the piece I like best', 'Make one small fix to it'],
                w: 'Choose the piece to share, so I know which work I\'m showing.',
                k: 'I\'ll know I\'m done when one piece is picked and given one small fix.',
                sw: [
                  'Review this week\'s work, so I can choose from everything I made.',
                  'Pick the piece I like best, so I share work I\'m proud of.',
                  'Make one small fix to it, so the piece is ready to share.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve looked over this week\'s pieces.',
                  'I\'ll know I\'m done when one piece is chosen.',
                  'I\'ll know I\'m done when the fix is made.',
                ],
              },
              {
                t: "A person, message and working file ready to share", s: ['Name one friendly person to show', 'Write a one-line message to go with it', 'Check the file or photo opens properly'],
                w: 'Pick the person or place to share it, so my piece has somewhere to go.',
                k: 'I\'ll know I\'m done when I have a person, a message and a file that opens.',
                sw: [
                  'Name one friendly person to show, so my piece goes to someone safe.',
                  'Write a one-line message to go with it, so they know what I\'m sharing.',
                  'Check the file or photo opens properly, so they can see the piece clearly.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve written their name.',
                  'I\'ll know I\'m done when the message is written.',
                  'I\'ll know I\'m done when the file opens without trouble.',
                ],
              },
              {
                t: "Piece sent, with one lesson from the reaction written", s: ['Send the piece', 'Read their reply without defending it', 'Write down one thing I learned'],
                w: 'Send it and note the reaction, so my work gets a witness and I learn from it.',
                k: 'I\'ll know I\'m done when the piece is sent and I\'ve written one thing I learned.',
                sw: [
                  'Send the piece, so my work reaches someone.',
                  'Read their reply without defending it, so I take in how my work lands.',
                  'Write down one thing I learned, so my sharing teaches me something.',
                ],
                sk: [
                  'I\'ll know I\'m done when the piece is sent.',
                  'I\'ll know I\'m done when I\'ve read their reply through.',
                  'I\'ll know I\'m done when one line is written.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Finish one complete piece of work I am proud of',
        w: 'Finish one complete piece of work I am proud of, so I have a regular creative practice that ends in finished work.',
        k: 'I\'ll know I\'m done when one complete piece is finished, saved and I\'m proud of it.',
        a: [
          {
            t: 'Work on my main piece in three sessions', c: '3×/week',
            p: 'Finishing is a skill learned only by finishing.',
            w: 'Work on my main piece in three sessions, so I finish one piece I\'m proud of this month.',
            k: 'I\'ll know I\'m done when I\'ve worked on my main piece in three sessions this week.',
            d: [
              {
                t: "Main piece described, split into five parts, finish date set", s: ['Describe the piece in two sentences', 'Break it into five parts', 'Set a finish date'],
                w: 'Plan the piece, so my three sessions have a clear direction.',
                k: 'I\'ll know I\'m done when the piece is described, split into five parts and given a finish date.',
                sw: [
                  'Describe the piece in two sentences, so I know exactly what I\'m making.',
                  'Break it into five parts, so the work is split into doable steps.',
                  'Set a finish date, so the piece has a deadline.',
                ],
                sk: [
                  'I\'ll know I\'m done when two sentences are written.',
                  'I\'ll know I\'m done when five parts are listed.',
                  'I\'ll know I\'m done when the date is written down.',
                ],
              },
              {
                t: "30 minutes on my main piece done, stopping point noted", s: ['Open the project', 'Work on the next part for 30 minutes', 'Note where I stopped'],
                w: 'Work on the next part, so my main piece moves forward today.',
                k: 'I\'ll know I\'m done when 30 minutes are worked and I\'ve noted where I stopped.',
                sw: [
                  'Open the project, so I can start straight away.',
                  'Work on the next part for 30 minutes, so my main piece moves forward.',
                  'Note where I stopped, so I can pick up easily next session.',
                ],
                sk: [
                  'I\'ll know I\'m done when the project is open.',
                  'I\'ll know I\'m done when 30 minutes have passed.',
                  'I\'ll know I\'m done when the stopping point is written.',
                ],
              },
              {
                t: "Finished parts marked and finish date confirmed or adjusted", s: ['Compare the piece to my plan', 'Mark the parts that are done', 'Adjust the finish date if needed'],
                w: 'Review progress against the plan, so I stay on track to finish my main piece.',
                k: 'I\'ll know I\'m done when finished parts are marked and the finish date is confirmed or adjusted.',
                sw: [
                  'Compare the piece to my plan, so I see how far along I am.',
                  'Mark the parts that are done, so I can see progress.',
                  'Adjust the finish date if needed, so my plan stays realistic.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve checked each part of the plan.',
                  'I\'ll know I\'m done when the done parts are marked.',
                  'I\'ll know I\'m done when the date is confirmed or changed.',
                ],
              },
            ],
          },
          {
            t: 'Get one honest critique of the work', c: 'weekly',
            p: 'Growth requires honest discomfort, not positive thinking.',
            w: 'Get one honest critique of the work, so I improve my piece before I finish it.',
            k: 'I\'ll know I\'m done when I\'ve received a critique and applied its biggest fix this week.',
            d: [
              {
                t: "Critique request and my work sent", s: ['Pick someone whose taste I trust', 'Ask what works and what does not', 'Send them the work'],
                w: 'Ask for a critique, so someone whose taste I trust looks at my work.',
                k: 'I\'ll know I\'m done when my request and the work are sent.',
                sw: [
                  'Pick someone whose taste I trust, so I get a critique worth hearing.',
                  'Ask what works and what does not, so I get an honest critique.',
                  'Send them the work, so they can give me their critique.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve named the person.',
                  'I\'ll know I\'m done when my question is written.',
                  'I\'ll know I\'m done when the work is sent.',
                ],
              },
              {
                t: "The one highest-impact fix picked from the feedback", s: ['Read the feedback twice', 'List the points I agree with', 'Pick the one fix with the most effect'],
                w: 'Take the critique in, so I know what to fix in my piece.',
                k: 'I\'ll know I\'m done when I\'ve picked the one fix with the most effect.',
                sw: [
                  'Read the feedback twice, so I understand it fully.',
                  'List the points I agree with, so I separate useful feedback from the rest.',
                  'Pick the one fix with the most effect, so my next change counts.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read it twice.',
                  'I\'ll know I\'m done when the points are listed.',
                  'I\'ll know I\'m done when one fix is chosen.',
                ],
              },
              {
                t: "The biggest fix made and saved as a new version", s: ['Open the work', 'Make the chosen change', 'Save it as a new version'],
                w: 'Apply the biggest fix, so my piece improves from the critique.',
                k: 'I\'ll know I\'m done when the change is made and saved as a new version.',
                sw: [
                  'Open the work, so I can make the fix.',
                  'Make the chosen change, so the biggest fix is done.',
                  'Save it as a new version, so my improved piece is kept.',
                ],
                sk: [
                  'I\'ll know I\'m done when the work is open.',
                  'I\'ll know I\'m done when the change is made.',
                  'I\'ll know I\'m done when the new version is saved.',
                ],
              },
            ],
          },
          {
            t: 'Cut or polish one section', c: '2×/week',
            p: 'Craft is mostly taking away.',
            w: 'Cut or polish one section, so my piece gets better as it nears the finish.',
            k: 'I\'ll know I\'m done when I\'ve polished or cut a section on two days this week.',
            d: [
              {
                t: "Weakest section marked, with a decision to cut or rework", s: ['Read through the whole piece', 'Mark the section that drags', 'Decide whether to cut or rework it'],
                w: 'Pick the weakest section, so I know what to improve first.',
                k: 'I\'ll know I\'m done when the weakest section is marked and I\'ve decided to cut or rework it.',
                sw: [
                  'Read through the whole piece, so I can judge every section.',
                  'Mark the section that drags, so I know where the weakness is.',
                  'Decide whether to cut or rework it, so I know my next move.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read it to the end.',
                  'I\'ll know I\'m done when the section is marked.',
                  'I\'ll know I\'m done when the decision is written.',
                ],
              },
              {
                t: "Weak section reworked and compared with the old version", s: ['Set a 30 minute timer', 'Rework only the marked section', 'Compare it with the old version'],
                w: 'Polish for 30 minutes, so the weakest section gets better today.',
                k: 'I\'ll know I\'m done when the marked section is reworked and compared with the old version.',
                sw: [
                  'Set a 30 minute timer, so my polishing has a limit.',
                  'Rework only the marked section, so I improve just what needs it.',
                  'Compare it with the old version, so I see the section really improved.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the section is reworked.',
                  'I\'ll know I\'m done when I\'ve compared both versions.',
                ],
              },
              {
                t: "Whole piece read through, roughest join fixed", s: ['Read from start to finish', 'Note any rough joins between sections', 'Fix the roughest join'],
                w: 'Read the whole piece through, so I check the polished section fits the whole.',
                k: 'I\'ll know I\'m done when I\'ve read the whole piece and fixed the roughest join.',
                sw: [
                  'Read from start to finish, so I see the piece as a whole.',
                  'Note any rough joins between sections, so I find what doesn\'t fit.',
                  'Fix the roughest join, so the polished section sits well in the piece.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve reached the end.',
                  'I\'ll know I\'m done when rough joins are noted.',
                  'I\'ll know I\'m done when the join is fixed.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Try a new medium or style and complete one piece in it',
        w: 'Try a new medium or style and complete one piece in it, so my creative practice keeps growing.',
        k: 'I\'ll know I\'m done when one finished piece exists in a medium or style that is new to me.',
        a: [
          {
            t: 'Practise a new technique for 20 minutes', c: '3×/week',
            p: 'Play comes before skill.',
            w: 'Practise a new technique for 20 minutes, so I\'m ready to complete a piece in a new medium.',
            k: 'I\'ll know I\'m done when I\'ve practised the new technique on three days this week.',
            d: [
              {
                t: "One technique chosen, basic materials gathered", s: ['List three techniques I am curious about', 'Pick the one I can start today', 'Gather the basic materials'],
                w: 'Choose the technique to try, so I know what new technique I\'m practising.',
                k: 'I\'ll know I\'m done when one technique is chosen and its basic materials are gathered.',
                sw: [
                  'List three techniques I am curious about, so I have options to pick from.',
                  'Pick the one I can start today, so I begin practising without delay.',
                  'Gather the basic materials, so I can start the technique right away.',
                ],
                sk: [
                  'I\'ll know I\'m done when three are listed.',
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when the materials are in front of me.',
                ],
              },
              {
                t: "One tutorial finished and my result saved", s: ['Find a short beginner tutorial', 'Follow it step by step', 'Save the result, however rough'],
                w: 'Follow one tutorial start to finish, so I learn the technique step by step.',
                k: 'I\'ll know I\'m done when I\'ve finished the tutorial and saved my result.',
                sw: [
                  'Find a short beginner tutorial, so I have a guide for the technique.',
                  'Follow it step by step, so I learn the technique properly.',
                  'Save the result, however rough, so I keep a record of my first try.',
                ],
                sk: [
                  'I\'ll know I\'m done when the tutorial is chosen.',
                  'I\'ll know I\'m done when I\'ve reached its last step.',
                  'I\'ll know I\'m done when the result is saved.',
                ],
              },
              {
                t: "Technique tried from memory, with forgotten parts noted", s: ['Close the tutorial', 'Try the technique from memory', 'Note what I forgot'],
                w: 'Repeat the technique without the tutorial, so I own the technique and don\'t just copy it.',
                k: 'I\'ll know I\'m done when I\'ve tried it from memory and noted what I forgot.',
                sw: [
                  'Close the tutorial, so I rely on memory this time.',
                  'Try the technique from memory, so I test what I\'ve learned.',
                  'Note what I forgot, so I know what to practise next.',
                ],
                sk: [
                  'I\'ll know I\'m done when the tutorial is closed.',
                  'I\'ll know I\'m done when I\'ve made an attempt.',
                  'I\'ll know I\'m done when the gaps are written.',
                ],
              },
            ],
          },
          {
            t: 'Study one artist or writer I admire', c: 'weekly',
            p: 'Learn from the best by taking their work apart.',
            w: 'Study one artist or writer I admire, so I borrow craft for my new medium or style.',
            k: 'I\'ll know I\'m done when I\'ve studied one creator and borrowed one technique this week.',
            d: [
              {
                t: "A creator named, three pieces found, 30 minutes set aside", s: ['Name a creator whose work moves me', 'Find three of their pieces', 'Set aside 30 minutes to study them'],
                w: 'Choose who to study, so I know whose work I\'m learning from.',
                k: 'I\'ll know I\'m done when I\'ve named a creator, found three pieces and set aside 30 minutes.',
                sw: [
                  'Name a creator whose work moves me, so I study someone I admire.',
                  'Find three of their pieces, so I have work to study.',
                  'Set aside 30 minutes to study them, so the study actually happens.',
                ],
                sk: [
                  'I\'ll know I\'m done when the creator is named.',
                  'I\'ll know I\'m done when three pieces are found.',
                  'I\'ll know I\'m done when the 30 minutes are in my calendar.',
                ],
              },
              {
                t: "What makes a piece work, plus a technique to borrow", s: ['Look at the piece closely', 'Write what makes it work', 'Note one technique I could borrow'],
                w: 'Take one piece apart, so I understand what makes the work succeed.',
                k: 'I\'ll know I\'m done when I\'ve written what makes it work and one technique to borrow.',
                sw: [
                  'Look at the piece closely, so I notice how it\'s made.',
                  'Write what makes it work, so I can name its craft.',
                  'Note one technique I could borrow, so I know what to try myself.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve examined it for several minutes.',
                  'I\'ll know I\'m done when my notes are written.',
                  'I\'ll know I\'m done when one technique is noted.',
                ],
              },
              {
                t: "Borrowed technique used in a small piece and compared", s: ['Choose the technique to borrow', 'Use it in a small piece of my own', 'Compare the result with the original'],
                w: 'Copy one technique into my own work, so I learn from the artist by using what they do.',
                k: 'I\'ll know I\'m done when I\'ve used the technique in a small piece and compared it with the original.',
                sw: [
                  'Choose the technique to borrow, so I know what to copy.',
                  'Use it in a small piece of my own, so I practise the technique myself.',
                  'Compare the result with the original, so I see what I\'ve learned.',
                ],
                sk: [
                  'I\'ll know I\'m done when the technique is chosen.',
                  'I\'ll know I\'m done when the small piece is made.',
                  'I\'ll know I\'m done when I\'ve compared the two.',
                ],
              },
            ],
          },
          {
            t: 'Do one exercise outside my comfort zone', c: '2×/week',
            p: 'Comfort repeats itself; discomfort grows you.',
            w: 'Do one exercise outside my comfort zone, so I stretch into a new medium or style.',
            k: 'I\'ll know I\'m done when I\'ve done an uncomfortable exercise on two days this week.',
            d: [
              {
                t: "Avoided things listed, smallest version chosen, timer set", s: ['List what I avoid making', 'Pick the smallest version of it', 'Set a 15 minute timer'],
                w: 'Pick an exercise that scares me a little, so I choose a safe way to stretch.',
                k: 'I\'ll know I\'m done when I\'ve listed what I avoid, chosen the smallest version and set a timer.',
                sw: [
                  'List what I avoid making, so I find where my comfort zone ends.',
                  'Pick the smallest version of it, so the exercise feels doable.',
                  'Set a 15 minute timer, so the exercise has a clear limit.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when one small exercise is chosen.',
                  'I\'ll know I\'m done when the timer is set.',
                ],
              },
              {
                t: "A fast, deliberately bad version made and kept", s: ['Decide that this one may be bad', 'Make it fast without stopping', 'Keep it without judging it'],
                w: 'Make a bad version on purpose, so I get past my fear of doing it badly.',
                k: 'I\'ll know I\'m done when a fast, bad version exists and I\'ve kept it.',
                sw: [
                  'Decide that this one may be bad, so I can start without pressure.',
                  'Make it fast without stopping, so I do the exercise before fear steps in.',
                  'Keep it without judging it, so I accept the stretch and move on.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve said it aloud or written it.',
                  'I\'ll know I\'m done when the version is made.',
                  'I\'ll know I\'m done when the version is saved with no edits.',
                ],
              },
              {
                t: "New-style piece finished, dated and filed", s: ['Pick the best of my attempts', 'Spend 30 minutes finishing it', 'Date it and file it with my work'],
                w: 'Finish the new-style piece, so I complete a piece outside my comfort zone.',
                k: 'I\'ll know I\'m done when the piece is finished, dated and filed.',
                sw: [
                  'Pick the best of my attempts, so I finish the strongest one.',
                  'Spend 30 minutes finishing it, so the piece is completed.',
                  'Date it and file it with my work, so it is kept with my other pieces.',
                ],
                sk: [
                  'I\'ll know I\'m done when one attempt is chosen.',
                  'I\'ll know I\'m done when 30 minutes are worked and the piece is finished.',
                  'I\'ll know I\'m done when it\'s dated and filed.',
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // ───────────────────────── 3. Having inner calm and emotional regulation
  {
    e: 'Having inner calm and emotional regulation', deity: 'frosy',
    w: 'I\'m building inner calm and emotional regulation, so stress and strong feelings don\'t run my day or my relationships.',
    k: 'I\'ll know I\'m there when I pause before reacting, return to calm quickly and name my feelings as they come.',
    g: [
      {
        t: 'Pause before reacting in 20 triggering moments this month',
        w: 'Pause before reacting in 20 triggering moments this month, so I build inner calm and emotional regulation.',
        k: 'I\'ll know I\'m done when I\'ve paused before reacting in 20 triggering moments this month.',
        a: [
          {
            t: 'Take one slow breath before I respond when triggered', c: 'daily',
            p: 'You can only manage what you\'re willing to see clearly.',
            w: 'Take one slow breath before I respond when triggered, so I pause in 20 triggering moments this month.',
            k: 'I\'ll know I\'m done when I\'ve taken a breath before responding in every trigger this week.',
            d: [
              {
                t: "At least one trigger met with a breath before responding", s: ['Pick a trigger likely to arise today', 'Take one slow breath when it hits', 'Respond only after the breath'],
                w: 'Practise the pause in a small moment, so I take one slow breath before I respond today.',
                k: 'I\'ll know I\'m done when I\'ve breathed before responding in at least one trigger today.',
                sw: [
                  'Pick a trigger likely to arise today, so I\'m ready to practise the pause.',
                  'Take one slow breath when it hits, so I pause before I respond.',
                  'Respond only after the breath, so my reply comes from a calmer place.',
                ],
                sk: [
                  'I\'ll know I\'m done when the trigger is named.',
                  'I\'ll know I\'m done when I\'ve taken the breath in the moment.',
                  'I\'ll know I\'m done when I\'ve replied after the breath.',
                ],
              },
              {
                t: "Biggest trigger named, first response chosen and rehearsed", s: ['Name the situation that sets me off most', 'Decide the first thing I will do instead', 'Rehearse it once in my head'],
                w: 'Prepare for my biggest trigger, so I\'m ready to pause when it comes.',
                k: 'I\'ll know I\'m done when I\'ve named the situation, chosen my first response and rehearsed it once.',
                sw: [
                  'Name the situation that sets me off most, so I know what to prepare for.',
                  'Decide the first thing I will do instead, so I have a calm response ready.',
                  'Rehearse it once in my head, so the response comes more easily in the moment.',
                ],
                sk: [
                  'I\'ll know I\'m done when the situation is written.',
                  'I\'ll know I\'m done when my first response is written.',
                  'I\'ll know I\'m done when I\'ve run through it once.',
                ],
              },
              {
                t: "Today's pauses and reactions listed, with what helped noted", s: ['List the moments I paused', 'List the moments I reacted', 'Note what helped me pause'],
                w: 'Review how today\'s pauses went, so I keep taking one slow breath before I respond.',
                k: 'I\'ll know I\'m done when I\'ve listed my pauses and reactions and noted what helped.',
                sw: [
                  'List the moments I paused, so I see what\'s working.',
                  'List the moments I reacted, so I see where I still need practice.',
                  'Note what helped me pause, so I can repeat it tomorrow.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when one helpful thing is written down.',
                ],
              },
            ],
          },
          {
            t: 'Do a 5 minute breathing session', c: 'daily · 5 min',
            p: 'A settled body makes a steadier mind.',
            w: 'Do a 5 minute breathing session, so my body is settled enough to pause when triggered.',
            k: 'I\'ll know I\'m done when I\'ve done a 5 minute breathing session on every day this week.',
            d: [
              {
                t: "5 minutes of slow breathing done this morning", s: ['Sit upright and set a 5 minute timer', 'Breathe in for 4 counts, out for 6', 'Notice my shoulders drop'],
                w: 'Do the morning breathing session, so I start the day settled.',
                k: 'I\'ll know I\'m done when I\'ve breathed slowly for 5 minutes this morning.',
                sw: [
                  'Sit upright and set a 5 minute timer, so the session starts cleanly.',
                  'Breathe in for 4 counts, out for 6, so my body settles.',
                  'Notice my shoulders drop, so I feel the settling happen.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the timer ends.',
                  'I\'ll know I\'m done when I\'ve noticed my shoulders relax.',
                ],
              },
              {
                t: "2-minute midday breathing done, stress rated", s: ['Step away from my screen', 'Breathe slowly for 2 minutes', 'Rate my stress from 1 to 10'],
                w: 'Do a midday reset, so I settle my body in the middle of the day.',
                k: 'I\'ll know I\'m done when I\'ve breathed for 2 minutes and rated my stress.',
                sw: [
                  'Step away from my screen, so I can reset properly.',
                  'Breathe slowly for 2 minutes, so my stress comes down.',
                  'Rate my stress from 1 to 10, so I see how the reset worked.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'m away from my screen.',
                  'I\'ll know I\'m done when 2 minutes have passed.',
                  'I\'ll know I\'m done when I\'ve written a number.',
                ],
              },
              {
                t: "5 minutes of evening breathing done, one worry released", s: ['Dim the lights and sit down', 'Breathe in for 4, out for 6 for 5 minutes', 'Let go of one worry from today'],
                w: 'Do a wind-down session in the evening, so I end the day settled.',
                k: 'I\'ll know I\'m done when I\'ve finished 5 minutes of slow breathing and let go of one worry.',
                sw: [
                  'Dim the lights and sit down, so the evening session starts calmly.',
                  'Breathe in for 4, out for 6 for 5 minutes, so my body winds down.',
                  'Let go of one worry from today, so I end the day lighter.',
                ],
                sk: [
                  'I\'ll know I\'m done when the lights are low and I\'m seated.',
                  'I\'ll know I\'m done when 5 minutes are done.',
                  'I\'ll know I\'m done when I\'ve named the worry and set it down.',
                ],
              },
            ],
          },
          {
            t: 'Log my triggers and reactions', c: 'weekly',
            p: 'An unexamined pattern will repeat until it\'s named.',
            w: 'Log my triggers and reactions, so I learn what sets me off and pause before reacting.',
            k: 'I\'ll know I\'m done when I\'ve kept my trigger log this week and found the pattern.',
            d: [
              {
                t: "Trigger log created with my last trigger in it", s: ['Open a note called Triggers', 'Add columns: trigger, reaction, better response', 'Log the last trigger I remember'],
                w: 'Start a trigger log, so I have a place to record what sets me off.',
                k: 'I\'ll know I\'m done when the log exists and holds my last trigger.',
                sw: [
                  'Open a note called Triggers, so my log has a home.',
                  'Add columns: trigger, reaction, better response, so every entry is clear.',
                  'Log the last trigger I remember, so the log has its first entry.',
                ],
                sk: [
                  'I\'ll know I\'m done when the note exists.',
                  'I\'ll know I\'m done when the three columns are in place.',
                  'I\'ll know I\'m done when one row is filled in.',
                ],
              },
              {
                t: "Every tense moment today logged", s: ['Recall each time I felt tense today', 'Write what set it off', 'Write what I did next'],
                w: 'Add today\'s triggers to the log, so my log reflects what happened today.',
                k: 'I\'ll know I\'m done when every tense moment today is in the log.',
                sw: [
                  'Recall each time I felt tense today, so I catch every trigger.',
                  'Write what set it off, so I can see the cause.',
                  'Write what I did next, so I see my reaction.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve gone through the day.',
                  'I\'ll know I\'m done when each cause is written.',
                  'I\'ll know I\'m done when each reaction is written.',
                ],
              },
              {
                t: "Repeating trigger circled, with one new response written", s: ['Read the log from this week', 'Circle the trigger that repeats', 'Write one way to handle it differently'],
                w: 'Find the pattern in the log, so I know which trigger to handle differently.',
                k: 'I\'ll know I\'m done when the repeating trigger is circled and one new response is written.',
                sw: [
                  'Read the log from this week, so I can spot the pattern.',
                  'Circle the trigger that repeats, so I know which one matters most.',
                  'Write one way to handle it differently, so I have a plan for the next time.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read every entry.',
                  'I\'ll know I\'m done when one trigger is circled.',
                  'I\'ll know I\'m done when one new response is written.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Build a daily calming routine I follow 5 days a week',
        w: 'Build a daily calming routine I follow 5 days a week, so I have inner calm and emotional regulation.',
        k: 'I\'ll know I\'m done when I\'ve followed my calming routine on at least 5 days a week all month.',
        a: [
          {
            t: 'Meditate for 10 minutes', c: 'daily · 10 min',
            p: 'Calm is a skill, not a mood.',
            w: 'Meditate for 10 minutes, so my daily calming routine has a core.',
            k: 'I\'ll know I\'m done when I\'ve meditated for 10 minutes on at least 5 days this week.',
            d: [
              {
                t: "10 minutes of meditation sat in full", s: ['Choose a quiet spot', 'Set a 10 minute timer', 'Return to my breath whenever I drift'],
                w: 'Sit for a 10 minute session, so I meditate today.',
                k: 'I\'ll know I\'m done when I\'ve sat for the full 10 minutes by tonight.',
                sw: [
                  'Choose a quiet spot, so I can sit undisturbed.',
                  'Set a 10 minute timer, so I know when to stop.',
                  'Return to my breath whenever I drift, so the 10 minutes is real practice.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'m in the spot.',
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the timer ends.',
                ],
              },
              {
                t: "Guided meditation finished, with how I feel noted", s: ['Open a free guided meditation', 'Follow it without pausing', 'Write one line on how I feel after'],
                w: 'Use a guided session, so I get help to meditate for 10 minutes.',
                k: 'I\'ll know I\'m done when the guided session has finished and I\'ve noted how I feel.',
                sw: [
                  'Open a free guided meditation, so I have a voice to follow.',
                  'Follow it without pausing, so I complete the full session.',
                  'Write one line on how I feel after, so I see what it does for me.',
                ],
                sk: [
                  'I\'ll know I\'m done when the session is open.',
                  'I\'ll know I\'m done when it ends.',
                  'I\'ll know I\'m done when the line is written.',
                ],
              },
              {
                t: "Unguided sit completed until the timer ended", s: ['Sit upright and close my eyes', 'Count ten breaths, then start over', 'Stop when the timer ends'],
                w: 'Sit without any guide, so I can meditate for 10 minutes on my own.',
                k: 'I\'ll know I\'m done when I\'ve sat unguided until the timer ended.',
                sw: [
                  'Sit upright and close my eyes, so I settle into the session.',
                  'Count ten breaths, then start over, so my attention has an anchor.',
                  'Stop when the timer ends, so I finish the session in full.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'m seated with my eyes closed.',
                  'I\'ll know I\'m done when I\'ve counted at least one full set of ten.',
                  'I\'ll know I\'m done when the timer rings.',
                ],
              },
            ],
          },
          {
            t: 'Take a screen-free break', c: 'daily · 15 min',
            p: 'A mind without gaps never settles.',
            w: 'Take a screen-free break, so my daily calming routine includes a real gap in the day.',
            k: 'I\'ll know I\'m done when I\'ve taken a 15 minute screen-free break on at least 5 days this week.',
            d: [
              {
                t: "15-minute screen-free walk done", s: ['Leave my phone behind or on silent', 'Walk for 15 minutes', 'Notice three things I can hear'],
                w: 'Take a screen-free walk, so I get 15 minutes away from screens.',
                k: 'I\'ll know I\'m done when I\'ve walked for 15 minutes without a screen.',
                sw: [
                  'Leave my phone behind or on silent, so nothing interrupts the walk.',
                  'Walk for 15 minutes, so I take a real screen-free break.',
                  'Notice three things I can hear, so my mind settles on the present.',
                ],
                sk: [
                  'I\'ll know I\'m done when my phone is left behind or silent.',
                  'I\'ll know I\'m done when 15 minutes have passed.',
                  'I\'ll know I\'m done when I\'ve named three sounds.',
                ],
              },
              {
                t: "A drink finished sitting away from screens", s: ['Make a tea or coffee', 'Sit away from screens', 'Drink it slowly until it is done'],
                w: 'Sit quietly with a drink, so I take a calm break away from screens.',
                k: 'I\'ll know I\'m done when I\'ve finished my drink away from screens.',
                sw: [
                  'Make a tea or coffee, so I have something to sit with.',
                  'Sit away from screens, so my break is screen-free.',
                  'Drink it slowly until it is done, so the break lasts.',
                ],
                sk: [
                  'I\'ll know I\'m done when the drink is made.',
                  'I\'ll know I\'m done when I\'m seated with no screen in reach.',
                  'I\'ll know I\'m done when the cup is empty.',
                ],
              },
              {
                t: "One simple task done slowly and fully by hand", s: ['Pick a simple task like washing up', 'Do it slowly and fully', 'Notice how my mind settles'],
                w: 'Do one slow task by hand, so I take a calm break that uses my hands.',
                k: 'I\'ll know I\'m done when a simple task is done slowly and fully.',
                sw: [
                  'Pick a simple task like washing up, so I have a slow task ready.',
                  'Do it slowly and fully, so the task becomes my screen-free break.',
                  'Notice how my mind settles, so I see the break working.',
                ],
                sk: [
                  'I\'ll know I\'m done when the task is chosen.',
                  'I\'ll know I\'m done when the task is finished.',
                  'I\'ll know I\'m done when I\'ve noticed my state.',
                ],
              },
            ],
          },
          {
            t: 'Wind down properly before bed', c: 'daily · 20 min',
            p: 'How you end the day sets up the next one.',
            w: 'Wind down properly before bed, so my calming routine ends each day well.',
            k: 'I\'ll know I\'m done when I\'ve done my wind-down routine on at least 5 nights this week.',
            d: [
              {
                t: "Wind-down started on time: alarm rung, phone away, lights dim", s: ['Set an alarm for wind-down time', 'Put my phone on charge away from bed', 'Dim the lights'],
                w: 'Start the wind-down at a fixed time, so my evening wind-down begins on cue.',
                k: 'I\'ll know I\'m done when the alarm rang, my phone is charging away from bed and the lights are dim.',
                sw: [
                  'Set an alarm for wind-down time, so I start at the same time every night.',
                  'Put my phone on charge away from bed, so screens don\'t follow me into bed.',
                  'Dim the lights, so my body knows the day is ending.',
                ],
                sk: [
                  'I\'ll know I\'m done when the alarm is set.',
                  'I\'ll know I\'m done when my phone is charging across the room.',
                  'I\'ll know I\'m done when the lights are low.',
                ],
              },
              {
                t: "Reading, stretching and slow breaths done in bed", s: ['Read a few pages of a book', 'Stretch gently for five minutes', 'Take three slow breaths in bed'],
                w: 'Do a calming activity, so my wind-down actually calms me.',
                k: 'I\'ll know I\'m done when I\'ve read, stretched and breathed in bed.',
                sw: [
                  'Read a few pages of a book, so my mind slows down.',
                  'Stretch gently for five minutes, so my body relaxes.',
                  'Take three slow breaths in bed, so I fall asleep calm.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read a few pages.',
                  'I\'ll know I\'m done when five minutes are done.',
                  'I\'ll know I\'m done when I\'ve taken the three breaths.',
                ],
              },
              {
                t: "Tomorrow's tasks and worries on paper, notebook closed", s: ['Write tomorrow\'s top three tasks', 'Write any worry on paper', 'Close the notebook until morning'],
                w: 'Clear my mind for tomorrow, so I wind down without worries on my mind.',
                k: 'I\'ll know I\'m done when tomorrow\'s tasks and worries are on paper and the notebook is closed.',
                sw: [
                  'Write tomorrow\'s top three tasks, so I don\'t carry them in my head.',
                  'Write any worry on paper, so it stops circling in my mind.',
                  'Close the notebook until morning, so I leave the worries alone until tomorrow.',
                ],
                sk: [
                  'I\'ll know I\'m done when three tasks are written.',
                  'I\'ll know I\'m done when each worry is written.',
                  'I\'ll know I\'m done when the notebook is closed.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Name and work through one recurring difficult emotion',
        w: 'Name and work through one recurring difficult emotion, so I build inner calm and emotional regulation.',
        k: 'I\'ll know I\'m done when I can name my recurring emotion, say what sets it off and have a way to respond.',
        a: [
          {
            t: 'Name my emotion three times a day', c: 'daily',
            p: 'Name it before it names you.',
            w: 'Name my emotion three times a day, so I can see my recurring difficult emotion clearly.',
            k: 'I\'ll know I\'m done when I\'ve named my emotion at three set times on most days this week.',
            d: [
              {
                t: "My feeling named at all three check-ins", s: ['Set three reminders for today', 'At each, ask what I am feeling', 'Name it in one word'],
                w: 'Check in with myself at set times, so I name my emotion three times today.',
                k: 'I\'ll know I\'m done when I\'ve named my feeling at all three reminders today.',
                sw: [
                  'Set three reminders for today, so I check in at set times.',
                  'At each, ask what I am feeling, so I notice my emotion.',
                  'Name it in one word, so my feeling has a clear label.',
                ],
                sk: [
                  'I\'ll know I\'m done when three reminders are set.',
                  'I\'ll know I\'m done when I\'ve asked at each reminder.',
                  'I\'ll know I\'m done when each feeling has a word.',
                ],
              },
              {
                t: "The most exact word for my feeling written down", s: ['Find a list of emotion words', 'Choose the most exact word for now', 'Write it in my notes'],
                w: 'Be more precise about my feeling, so I name my emotion exactly.',
                k: 'I\'ll know I\'m done when the most exact word is written in my notes.',
                sw: [
                  'Find a list of emotion words, so I have more than a few words to choose from.',
                  'Choose the most exact word for now, so my label fits what I feel.',
                  'Write it in my notes, so I keep a record of how I feel.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is open.',
                  'I\'ll know I\'m done when one word is chosen.',
                  'I\'ll know I\'m done when the word is in my notes.',
                ],
              },
              {
                t: "Where the emotion sits in my body described", s: ['Close my eyes for a minute', 'Locate the feeling in my body', 'Describe it in one sentence'],
                w: 'Find where the emotion sits in my body, so I recognise it earlier next time.',
                k: 'I\'ll know I\'m done when I\'ve described where I feel it in one sentence.',
                sw: [
                  'Close my eyes for a minute, so I can tune in to my body.',
                  'Locate the feeling in my body, so I know where it shows up.',
                  'Describe it in one sentence, so I can recognise it again.',
                ],
                sk: [
                  'I\'ll know I\'m done when a minute has passed.',
                  'I\'ll know I\'m done when I\'ve found the spot.',
                  'I\'ll know I\'m done when the sentence is written.',
                ],
              },
            ],
          },
          {
            t: 'Write about the emotion for 10 minutes', c: '2×/week',
            p: 'Feelings put into words lose their grip.',
            w: 'Write about the emotion for 10 minutes, so I understand my recurring difficult emotion.',
            k: 'I\'ll know I\'m done when I\'ve written about my emotion for 10 minutes on two days this week.',
            d: [
              {
                t: "10 minutes written on the emotion's trigger and wants", s: ['Set a 10 minute timer', 'Write what triggered the emotion', 'Write what I think it wants from me'],
                w: 'Write about what the emotion wants, so I understand why it keeps coming back.',
                k: 'I\'ll know I\'m done when I\'ve written for 10 minutes about its trigger and what it wants.',
                sw: [
                  'Set a 10 minute timer, so my writing has a limit.',
                  'Write what triggered the emotion, so I see its cause.',
                  'Write what I think it wants from me, so I understand its message.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the trigger is written.',
                  'I\'ll know I\'m done when my answer is written.',
                ],
              },
              {
                t: "An early memory linked to today's emotion in writing", s: ['Recall the earliest time I felt this', 'Write what was happening then', 'Note how today connects to it'],
                w: 'Write about when it first appeared, so I understand where the emotion comes from.',
                k: 'I\'ll know I\'m done when I\'ve linked an early memory to today in writing.',
                sw: [
                  'Recall the earliest time I felt this, so I find its roots.',
                  'Write what was happening then, so I see the context.',
                  'Note how today connects to it, so I see the link between then and now.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve picked a memory.',
                  'I\'ll know I\'m done when the memory is written.',
                  'I\'ll know I\'m done when the link is written.',
                ],
              },
              {
                t: "My best response written, with one phrase to remember", s: ['Describe the situation again', 'Write how my best self would respond', 'Pick one phrase to remember'],
                w: 'Write the response I would be proud of, so I know how to handle this emotion next time.',
                k: 'I\'ll know I\'m done when I\'ve written my best response and picked one phrase to remember.',
                sw: [
                  'Describe the situation again, so I have the scene to respond to.',
                  'Write how my best self would respond, so I picture a response I\'d be proud of.',
                  'Pick one phrase to remember, so I can recall my response in the moment.',
                ],
                sk: [
                  'I\'ll know I\'m done when the scene is written.',
                  'I\'ll know I\'m done when the response is written.',
                  'I\'ll know I\'m done when one phrase is chosen.',
                ],
              },
            ],
          },
          {
            t: 'Talk it through with someone', c: 'weekly',
            p: 'Burdens shared become lighter.',
            w: 'Talk it through with someone, so I don\'t carry my recurring difficult emotion alone.',
            k: 'I\'ll know I\'m done when I\'ve talked it through with someone this week and chosen one thing to try.',
            d: [
              {
                t: "A trusted person picked, asked and a time agreed", s: ['Pick someone calm and trusted', 'Message them to ask for a conversation', 'Agree on a time'],
                w: 'Choose who I will talk to, so I have someone lined up to talk it through with.',
                k: 'I\'ll know I\'m done when a person is picked, asked and a time agreed.',
                sw: [
                  'Pick someone calm and trusted, so I talk with someone safe.',
                  'Message them to ask for a conversation, so the talk is set up.',
                  'Agree on a time, so the conversation will happen.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve named the person.',
                  'I\'ll know I\'m done when the message is sent.',
                  'I\'ll know I\'m done when the time is set.',
                ],
              },
              {
                t: "Emotion conversation held, listening without defending", s: ['Describe the emotion and what triggers it', 'Ask for their view, not advice', 'Listen without defending'],
                w: 'Have the conversation, so I talk my emotion through with someone I trust.',
                k: 'I\'ll know I\'m done when we\'ve talked and I have listened without defending.',
                sw: [
                  'Describe the emotion and what triggers it, so they understand what I\'m facing.',
                  'Ask for their view, not advice, so I hear how it looks from outside.',
                  'Listen without defending, so I take in what they say.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve explained it aloud.',
                  'I\'ll know I\'m done when they\'ve shared their view.',
                  'I\'ll know I\'m done when they\'ve finished speaking and I haven\'t argued.',
                ],
              },
              {
                t: "One thing to try placed in tomorrow's plan", s: ['Review what they said', 'Pick one thing to try this week', 'Write it in tomorrow\'s plan'],
                w: 'Turn the talk into one action, so the conversation changes how I handle my emotion.',
                k: 'I\'ll know I\'m done when one thing to try is in tomorrow\'s plan.',
                sw: [
                  'Review what they said, so I recall the key points.',
                  'Pick one thing to try this week, so I act on the talk.',
                  'Write it in tomorrow\'s plan, so I actually try it.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve gone through my notes or memory.',
                  'I\'ll know I\'m done when one thing is written.',
                  'I\'ll know I\'m done when it\'s in tomorrow\'s plan.',
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // ───────────────────────── 4. Having a meaningful personal growth practice
  {
    e: 'Having a meaningful personal growth practice', deity: 'meta',
    w: 'I\'m building a meaningful personal growth practice, so I keep learning and becoming a better version of myself.',
    k: 'I\'ll know I\'m there when I read, practise and review regularly and can point to how I\'ve grown.',
    g: [
      {
        t: 'Read one book on personal growth and apply three ideas',
        w: 'Read one book on personal growth and apply three ideas, so I build a meaningful growth practice.',
        k: 'I\'ll know I\'m done when I\'ve finished the book and tried three of its ideas in real life.',
        a: [
          {
            t: 'Read for 20 minutes', c: 'daily · 20 min',
            p: 'Growth requires honest discomfort, not positive thinking.',
            w: 'Read for 20 minutes, so I finish a book on personal growth this month.',
            k: 'I\'ll know I\'m done when I\'ve read for 20 minutes on every day this week.',
            d: [
              {
                t: "20 minutes of reading done", s: ['Open my book to the bookmark', 'Set a 20 minute timer', 'Read without checking my phone'],
                w: 'Read my 20 minutes, so I make daily progress through my book.',
                k: 'I\'ll know I\'m done when I\'ve read for 20 minutes today.',
                sw: [
                  'Open my book to the bookmark, so I start right where I left off.',
                  'Set a 20 minute timer, so I read for the full time.',
                  'Read without checking my phone, so my 20 minutes is real reading.',
                ],
                sk: [
                  'I\'ll know I\'m done when the book is open at my page.',
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the timer ends.',
                ],
              },
              {
                t: "A book picked and its first chapter read", s: ['List three books I want to read', 'Pick the one that fits my current need', 'Read the first chapter'],
                w: 'Choose and start the book, so I have a book to read for 20 minutes a day.',
                k: 'I\'ll know I\'m done when I\'ve picked a book and finished its first chapter.',
                sw: [
                  'List three books I want to read, so I have choices.',
                  'Pick the one that fits my current need, so I read the book that helps most now.',
                  'Read the first chapter, so my book is started.',
                ],
                sk: [
                  'I\'ll know I\'m done when three titles are listed.',
                  'I\'ll know I\'m done when one book is chosen.',
                  'I\'ll know I\'m done when the first chapter is finished.',
                ],
              },
              {
                t: "The three best lines marked and copied into my notes", s: ['Read with a pen or highlighter', 'Mark the three best lines', 'Copy them into my notes'],
                w: 'Read and underline, so I keep the best ideas from today\'s reading.',
                k: 'I\'ll know I\'m done when the three best lines are marked and copied into my notes.',
                sw: [
                  'Read with a pen or highlighter, so I can mark what stands out.',
                  'Mark the three best lines, so I find the ideas worth keeping.',
                  'Copy them into my notes, so I keep them after the book is closed.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read with a pen in hand.',
                  'I\'ll know I\'m done when three lines are marked.',
                  'I\'ll know I\'m done when the three lines are in my notes.',
                ],
              },
            ],
          },
          {
            t: 'Write down one idea from each reading session', c: '3×/week',
            p: 'Knowledge you do not record is knowledge you lose.',
            w: 'Write down one idea from each reading session, so I keep what I read and can apply three ideas.',
            k: 'I\'ll know I\'m done when I\'ve written an idea after each reading session this week.',
            d: [
              {
                t: "Today's idea written with an example from my life", s: ['Pick the idea that stood out', 'Write it in my own words', 'Add one example from my life'],
                w: 'Capture today\'s idea, so I keep what I read today.',
                k: 'I\'ll know I\'m done when today\'s idea is written with an example from my life.',
                sw: [
                  'Pick the idea that stood out, so I capture the one that matters most.',
                  'Write it in my own words, so I make sure I understand it.',
                  'Add one example from my life, so the idea connects to my real situation.',
                ],
                sk: [
                  'I\'ll know I\'m done when one idea is chosen.',
                  'I\'ll know I\'m done when the idea is written.',
                  'I\'ll know I\'m done when the example is written.',
                ],
              },
              {
                t: "Practical ideas marked and unusable ones crossed out", s: ['Reread this week\'s notes', 'Mark the ideas that are practical', 'Cross out those I will not use'],
                w: 'Rate the ideas I have captured, so I find the ones worth applying.',
                k: 'I\'ll know I\'m done when the practical ideas are marked and the unusable ones are crossed out.',
                sw: [
                  'Reread this week\'s notes, so I see all my ideas together.',
                  'Mark the ideas that are practical, so I find ideas I can use.',
                  'Cross out those I will not use, so my list holds only useful ideas.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read them all.',
                  'I\'ll know I\'m done when the practical ones are marked.',
                  'I\'ll know I\'m done when the rest are crossed out.',
                ],
              },
              {
                t: "One idea turned into a question and answered briefly", s: ['Choose one idea', 'Rewrite it as a question about me', 'Answer the question in two sentences'],
                w: 'Turn an idea into a question, so I connect an idea to my own life.',
                k: 'I\'ll know I\'m done when I\'ve answered the question about me in two sentences.',
                sw: [
                  'Choose one idea, so I know which to work with.',
                  'Rewrite it as a question about me, so the idea speaks to my life.',
                  'Answer the question in two sentences, so I see what the idea means for me.',
                ],
                sk: [
                  'I\'ll know I\'m done when one idea is chosen.',
                  'I\'ll know I\'m done when the question is written.',
                  'I\'ll know I\'m done when two sentences are written.',
                ],
              },
            ],
          },
          {
            t: 'Apply one idea in real life', c: 'weekly',
            p: 'An idea you do not use is only entertainment.',
            w: 'Apply one idea in real life, so I turn my reading into three applied ideas this month.',
            k: 'I\'ll know I\'m done when I\'ve tried one idea from my book in real life this week.',
            d: [
              {
                t: "One idea chosen to apply, with my plan written", s: ['Reread my idea notes', 'Pick the one that is easiest to test', 'Write exactly how I will use it'],
                w: 'Choose the idea to apply, so I know exactly which idea I\'m trying.',
                k: 'I\'ll know I\'m done when one idea is chosen and my plan for using it is written.',
                sw: [
                  'Reread my idea notes, so I can pick from all of them.',
                  'Pick the one that is easiest to test, so I can try it quickly.',
                  'Write exactly how I will use it, so I can act on it without guessing.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read my notes.',
                  'I\'ll know I\'m done when one idea is picked.',
                  'I\'ll know I\'m done when the plan is written.',
                ],
              },
              {
                t: "Idea used, with what happened and what to change noted", s: ['Do what I wrote down', 'Note what happened', 'Note what I would change'],
                w: 'Use the idea today, so I test the idea in real life.',
                k: 'I\'ll know I\'m done when I\'ve used the idea and noted what happened and what I\'d change.',
                sw: [
                  'Do what I wrote down, so the idea is put into action.',
                  'Note what happened, so I record the result.',
                  'Note what I would change, so I improve the next try.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve done it.',
                  'I\'ll know I\'m done when the result is written.',
                  'I\'ll know I\'m done when one change is written.',
                ],
              },
              {
                t: "Keep-or-drop decided on the idea, lesson written", s: ['Compare the result with what I expected', 'Decide whether to keep the idea', 'Write the lesson in one line'],
                w: 'Review the result, so I learn whether the idea is worth keeping.',
                k: 'I\'ll know I\'m done when I\'ve decided to keep or drop the idea and written the lesson.',
                sw: [
                  'Compare the result with what I expected, so I see what the idea really did.',
                  'Decide whether to keep the idea, so I know if it belongs in my life.',
                  'Write the lesson in one line, so I remember what I learned.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve compared them.',
                  'I\'ll know I\'m done when the decision is made.',
                  'I\'ll know I\'m done when the line is written.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Learn one new skill to a basic working level',
        w: 'Learn one new skill to a basic working level, so I build a meaningful growth practice.',
        k: 'I\'ll know I\'m done when I can use the new skill on a small real task without help.',
        a: [
          {
            t: 'Practise the skill for 20 minutes', c: '4×/week',
            p: 'Small, steady practice beats rare, heroic effort.',
            w: 'Practise the skill for 20 minutes, so I reach a basic working level in my new skill.',
            k: 'I\'ll know I\'m done when I\'ve practised for 20 minutes on four days this week.',
            d: [
              {
                t: "20 minutes of skill practice done", s: ['Set a 20 minute timer', 'Practise only the next small piece', 'Stop when the timer ends'],
                w: 'Do today\'s practice session, so my skill improves a little today.',
                k: 'I\'ll know I\'m done when I\'ve practised for 20 minutes today.',
                sw: [
                  'Set a 20 minute timer, so my practice has a clear limit.',
                  'Practise only the next small piece, so I make real progress and don\'t sprawl.',
                  'Stop when the timer ends, so I keep each session short and repeatable.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when I\'ve worked on that piece.',
                  'I\'ll know I\'m done when the timer rings and I stop.',
                ],
              },
              {
                t: "Hardest part repeated slowly, one improvement noted", s: ['Pick the part I find hardest', 'Slow it down and repeat it', 'Note one small improvement'],
                w: 'Practise something hard on purpose, so my weakest part gets stronger.',
                k: 'I\'ll know I\'m done when I\'ve repeated the hardest part slowly and noted one improvement.',
                sw: [
                  'Pick the part I find hardest, so I work where it counts.',
                  'Slow it down and repeat it, so I get it right step by step.',
                  'Note one small improvement, so I see my progress.',
                ],
                sk: [
                  'I\'ll know I\'m done when the part is chosen.',
                  'I\'ll know I\'m done when I\'ve repeated it several times.',
                  'I\'ll know I\'m done when one improvement is written.',
                ],
              },
              {
                t: "A real task done with the skill, struggles noted", s: ['Pick a small real task that needs it', 'Do the task using the skill', 'Note what I still struggle with'],
                w: 'Use the skill on a real task, so I practise it where it matters.',
                k: 'I\'ll know I\'m done when the real task is done and I\'ve noted what I still struggle with.',
                sw: [
                  'Pick a small real task that needs it, so I have a real use for the skill.',
                  'Do the task using the skill, so I use it for real.',
                  'Note what I still struggle with, so I know what to practise next.',
                ],
                sk: [
                  'I\'ll know I\'m done when the task is chosen.',
                  'I\'ll know I\'m done when the task is finished.',
                  'I\'ll know I\'m done when the gaps are written.',
                ],
              },
            ],
          },
          {
            t: 'Follow one lesson or course module', c: '2×/week',
            p: 'A good map shortens the road.',
            w: 'Follow one lesson or course module, so I learn my new skill in a clear order.',
            k: 'I\'ll know I\'m done when I\'ve completed two lessons this week.',
            d: [
              {
                t: "A beginner course chosen and its link saved", s: ['Search for beginner lessons', 'Pick one with clear steps', 'Save the link where I can find it'],
                w: 'Choose the course or teacher, so I have a clear guide for learning the skill.',
                k: 'I\'ll know I\'m done when a beginner course is chosen and its link is saved.',
                sw: [
                  'Search for beginner lessons, so I find options.',
                  'Pick one with clear steps, so I follow a path I can complete.',
                  'Save the link where I can find it, so I can return to it easily.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve seen several options.',
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when the link is saved.',
                ],
              },
              {
                t: "One lesson and its exercise finished", s: ['Watch or read the lesson fully', 'Take notes as I go', 'Do the exercise at the end'],
                w: 'Complete one lesson, so I move one step forward in my course.',
                k: 'I\'ll know I\'m done when I\'ve finished the lesson and its end exercise.',
                sw: [
                  'Watch or read the lesson fully, so I take in the whole lesson.',
                  'Take notes as I go, so I keep what I learn.',
                  'Do the exercise at the end, so I practise what I learned.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve reached the lesson\'s end.',
                  'I\'ll know I\'m done when my notes are written.',
                  'I\'ll know I\'m done when the exercise is done.',
                ],
              },
              {
                t: "Lesson explained aloud, my open questions listed", s: ['Reread my notes', 'Explain the lesson aloud in two minutes', 'Write the questions I still have'],
                w: 'Review what I learned, so the lesson sticks.',
                k: 'I\'ll know I\'m done when I\'ve explained the lesson aloud and listed my open questions.',
                sw: [
                  'Reread my notes, so I refresh what I learned.',
                  'Explain the lesson aloud in two minutes, so I check that I understand it.',
                  'Write the questions I still have, so I know what to look up next.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read my notes through.',
                  'I\'ll know I\'m done when I\'ve explained it for two minutes.',
                  'I\'ll know I\'m done when the questions are written.',
                ],
              },
            ],
          },
          {
            t: 'Test myself on what I learned', c: 'weekly',
            p: 'What you cannot recall you have not learned.',
            w: 'Test myself on what I learned, so I know my new skill is really sticking.',
            k: 'I\'ll know I\'m done when I\'ve taken my weekly test and fixed the gaps it showed.',
            d: [
              {
                t: "Five test questions written, notes covered", s: ['List five things I should now know', 'Turn them into questions', 'Cover my notes'],
                w: 'Write a short test for myself, so I can check what I\'ve learned.',
                k: 'I\'ll know I\'m done when five questions are written and my notes are covered.',
                sw: [
                  'List five things I should now know, so my test covers what matters.',
                  'Turn them into questions, so I can test myself.',
                  'Cover my notes, so I test memory and not reading.',
                ],
                sk: [
                  'I\'ll know I\'m done when five items are listed.',
                  'I\'ll know I\'m done when five questions are written.',
                  'I\'ll know I\'m done when my notes are out of sight.',
                ],
              },
              {
                t: "Test answered and wrong answers marked", s: ['Answer each question from memory', 'Check my answers against my notes', 'Mark what I got wrong'],
                w: 'Take the test, so I find out what I really know.',
                k: 'I\'ll know I\'m done when I\'ve answered all questions and marked the wrong ones.',
                sw: [
                  'Answer each question from memory, so I test what I really recall.',
                  'Check my answers against my notes, so I see what\'s right.',
                  'Mark what I got wrong, so I know what to fix.',
                ],
                sk: [
                  'I\'ll know I\'m done when every question has an answer.',
                  'I\'ll know I\'m done when each answer is checked.',
                  'I\'ll know I\'m done when the wrong answers are marked.',
                ],
              },
              {
                t: "Missed parts practised and one missed question answered right", s: ['Review the questions I missed', 'Practise those parts for 15 minutes', 'Retest one missed question'],
                w: 'Fix the gaps, so my weak spots become strengths.',
                k: 'I\'ll know I\'m done when I\'ve practised the missed parts and answered a missed question correctly.',
                sw: [
                  'Review the questions I missed, so I understand my gaps.',
                  'Practise those parts for 15 minutes, so the gaps close.',
                  'Retest one missed question, so I confirm the gap is closed.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve gone over each one.',
                  'I\'ll know I\'m done when 15 minutes are done.',
                  'I\'ll know I\'m done when I\'ve answered it correctly.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Complete a monthly review and set one improvement for next month',
        w: 'Complete a monthly review and set one improvement for next month, so my growth practice keeps improving.',
        k: 'I\'ll know I\'m done when my monthly review is written and one improvement for next month is scheduled.',
        a: [
          {
            t: 'Do a 20 minute weekly review', c: 'weekly',
            p: 'What gets reviewed gets better.',
            w: 'Do a 20 minute weekly review, so my monthly review is built from what really happened.',
            k: 'I\'ll know I\'m done when I\'ve completed my 20 minute weekly review this week.',
            d: [
              {
                t: "Three wins listed, each with why it worked", s: ['Open my calendar and notes for the week', 'List three wins', 'Write why each one worked'],
                w: 'Review what went well, so I know what to keep doing.',
                k: 'I\'ll know I\'m done when three wins are listed with why each worked.',
                sw: [
                  'Open my calendar and notes for the week, so I can see what really happened.',
                  'List three wins, so I see what went well.',
                  'Write why each one worked, so I can repeat it.',
                ],
                sk: [
                  'I\'ll know I\'m done when both are open.',
                  'I\'ll know I\'m done when three wins are listed.',
                  'I\'ll know I\'m done when a reason is written for each.',
                ],
              },
              {
                t: "Three slips listed with real reasons, one picked to fix", s: ['List three things that slipped', 'Write the real reason for each', 'Pick one to fix next week'],
                w: 'Review what went badly, so I know what to fix.',
                k: 'I\'ll know I\'m done when three slips are listed, each with its real reason, and one is picked.',
                sw: [
                  'List three things that slipped, so I see where I fell short.',
                  'Write the real reason for each, so I fix the cause and not the symptom.',
                  'Pick one to fix next week, so I focus on one improvement.',
                ],
                sk: [
                  'I\'ll know I\'m done when three items are listed.',
                  'I\'ll know I\'m done when each has a reason.',
                  'I\'ll know I\'m done when one is chosen.',
                ],
              },
              {
                t: "One habit with its time and place in my calendar", s: ['Choose one habit to strengthen', 'Write when and where I will do it', 'Add it to my calendar'],
                w: 'Plan next week\'s improvement, so my review leads to action.',
                k: 'I\'ll know I\'m done when one habit, its time and its place are in my calendar.',
                sw: [
                  'Choose one habit to strengthen, so I know what to improve.',
                  'Write when and where I will do it, so it has a clear slot.',
                  'Add it to my calendar, so it actually happens.',
                ],
                sk: [
                  'I\'ll know I\'m done when one habit is chosen.',
                  'I\'ll know I\'m done when the time and place are written.',
                  'I\'ll know I\'m done when it\'s in my calendar.',
                ],
              },
            ],
          },
          {
            t: 'Track one growth habit each day', c: 'daily · 1 min',
            p: 'Identity is built by what you repeatedly do.',
            w: 'Track one growth habit each day, so my monthly review has real data.',
            k: 'I\'ll know I\'m done when I\'ve marked my habit tracker every day this week.',
            d: [
              {
                t: "Growth habit chosen, with its done-definition and tracker", s: ['Pick one habit that matters most', 'Define exactly what counts as done', 'Make a simple tracker in my notes'],
                w: 'Choose the habit to track, so I know what I\'m measuring.',
                k: 'I\'ll know I\'m done when the habit, its definition of done and a tracker exist.',
                sw: [
                  'Pick one habit that matters most, so I track what counts.',
                  'Define exactly what counts as done, so I can mark it honestly.',
                  'Make a simple tracker in my notes, so I have a place to mark it daily.',
                ],
                sk: [
                  'I\'ll know I\'m done when one habit is chosen.',
                  'I\'ll know I\'m done when the definition is written.',
                  'I\'ll know I\'m done when the tracker exists.',
                ],
              },
              {
                t: "Today marked done or missed, with a reason if missed", s: ['Open the tracker', 'Mark done or missed honestly', 'Note why if I missed'],
                w: 'Mark today\'s result, so my tracker is up to date.',
                k: 'I\'ll know I\'m done when today is marked done or missed, with a reason if missed.',
                sw: [
                  'Open the tracker, so I can record today.',
                  'Mark done or missed honestly, so my data is true.',
                  'Note why if I missed, so I learn from slips.',
                ],
                sk: [
                  'I\'ll know I\'m done when the tracker is open.',
                  'I\'ll know I\'m done when today is marked.',
                  'I\'ll know I\'m done when the reason is written, or I did not miss.',
                ],
              },
              {
                t: "This week's streak counted and compared, with one change written", s: ['Count days done this week', 'Compare with last week', 'Write one change to improve it'],
                w: 'Count the streak, so I can see how my habit is going.',
                k: 'I\'ll know I\'m done when I\'ve counted this week, compared it with last week and written one change.',
                sw: [
                  'Count days done this week, so I know how I did.',
                  'Compare with last week, so I see the trend.',
                  'Write one change to improve it, so next week is better.',
                ],
                sk: [
                  'I\'ll know I\'m done when I have a number.',
                  'I\'ll know I\'m done when I\'ve compared the two.',
                  'I\'ll know I\'m done when one change is written.',
                ],
              },
            ],
          },
          {
            t: 'Ask someone for feedback on my growth', c: 'weekly',
            p: 'You see your intentions; others see your effects.',
            w: 'Ask someone for feedback on my growth, so my monthly review includes an outside view.',
            k: 'I\'ll know I\'m done when someone has answered and I\'ve acted on one point.',
            d: [
              {
                t: "A person picked and my progress question sent", s: ['Pick someone who sees me regularly', 'Write one question about my progress', 'Message them the question'],
                w: 'Pick the person and the question, so I ask the right person about my growth.',
                k: 'I\'ll know I\'m done when I\'ve picked a person, written a question and sent it.',
                sw: [
                  'Pick someone who sees me regularly, so I get an informed view.',
                  'Write one question about my progress, so I ask something clear.',
                  'Message them the question, so they can answer.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve named the person.',
                  'I\'ll know I\'m done when the question is written.',
                  'I\'ll know I\'m done when it\'s sent.',
                ],
              },
              {
                t: "Feedback read, useful parts highlighted, thanks sent", s: ['Read their answer without replying', 'Highlight what is useful', 'Thank them in a short message'],
                w: 'Take in their answer, so I learn how my growth looks from outside.',
                k: 'I\'ll know I\'m done when I\'ve read their answer, highlighted the useful parts and thanked them.',
                sw: [
                  'Read their answer without replying, so I take it in first.',
                  'Highlight what is useful, so I find what to act on.',
                  'Thank them in a short message, so they feel heard and might help again.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read it through.',
                  'I\'ll know I\'m done when useful parts are highlighted.',
                  'I\'ll know I\'m done when the thank-you is sent.',
                ],
              },
              {
                t: "First small action on the feedback done", s: ['Choose one point from the feedback', 'Write the first small action', 'Do that action today'],
                w: 'Act on the feedback, so their view changes what I do.',
                k: 'I\'ll know I\'m done when I\'ve done the first small action today.',
                sw: [
                  'Choose one point from the feedback, so I focus on one thing.',
                  'Write the first small action, so I know how to begin.',
                  'Do that action today, so the feedback turns into change.',
                ],
                sk: [
                  'I\'ll know I\'m done when one point is chosen.',
                  'I\'ll know I\'m done when the action is written.',
                  'I\'ll know I\'m done when the action is done.',
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // ───────────────────────── 5. Having time carved out just for me
  {
    e: 'Having time carved out just for me', deity: 'pyda',
    w: 'I\'m building time carved out just for me, so I stay rested and whole and can show up well for everyone else.',
    k: 'I\'ll know I\'m there when I have protected personal time every week and I use it without guilt.',
    g: [
      {
        t: 'Protect 3 hours a week of time that is only mine',
        w: 'Protect 3 hours a week of time that is only mine, so I have time carved out just for me.',
        k: 'I\'ll know I\'m done when I\'ve kept 3 hours a week of personal time on each week of the month.',
        a: [
          {
            t: 'Block three sessions of personal time in my calendar', c: 'weekly',
            p: 'Time not protected is time taken.',
            w: 'Block three sessions of personal time in my calendar, so 3 hours a week are protected for me.',
            k: 'I\'ll know I\'m done when three personal sessions are blocked in next week\'s calendar.',
            d: [
              {
                t: "Three one-hour slots marked, least-interrupted one picked", s: ['Open next week\'s calendar', 'Mark three slots of one hour', 'Pick the least likely to be interrupted'],
                w: 'Find the free slots, so I know where my personal time can go.',
                k: 'I\'ll know I\'m done when three one-hour slots are marked and the least interrupted is picked.',
                sw: [
                  'Open next week\'s calendar, so I can see where time is free.',
                  'Mark three slots of one hour, so I have places for my personal time.',
                  'Pick the least likely to be interrupted, so my best slot is protected first.',
                ],
                sk: [
                  'I\'ll know I\'m done when next week\'s calendar is open.',
                  'I\'ll know I\'m done when three slots are marked.',
                  'I\'ll know I\'m done when one slot is chosen.',
                ],
              },
              {
                t: "Three Me events in my calendar with reminders", s: ['Create three calendar events named Me', 'Set a reminder for each', 'Mark them as busy'],
                w: 'Block the time, so my personal time is protected in my calendar.',
                k: 'I\'ll know I\'m done when three Me events are in my calendar with reminders.',
                sw: [
                  'Create three calendar events named Me, so the time is held.',
                  'Set a reminder for each, so I don\'t forget my own time.',
                  'Mark them as busy, so no one books over them.',
                ],
                sk: [
                  'I\'ll know I\'m done when three events exist.',
                  'I\'ll know I\'m done when each has a reminder.',
                  'I\'ll know I\'m done when all three show as busy.',
                ],
              },
              {
                t: "People who expect my time told when I'm unavailable", s: ['List who might expect my time', 'Tell them when I am unavailable', 'Offer another time if they need me'],
                w: 'Tell the people who need to know, so they respect my blocked time.',
                k: 'I\'ll know I\'m done when the people who might expect my time have been told when I\'m unavailable.',
                sw: [
                  'List who might expect my time, so I know who to tell.',
                  'Tell them when I am unavailable, so they know my time is blocked.',
                  'Offer another time if they need me, so my boundary stays kind.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when each person has been told.',
                  'I\'ll know I\'m done when I\'ve offered an alternative to those who need it.',
                ],
              },
            ],
          },
          {
            t: 'Use the protected time without interruption', c: '3×/week',
            p: 'Rest you defend is rest you get.',
            w: 'Use the protected time without interruption, so my three hours a week really are mine.',
            k: 'I\'ll know I\'m done when I\'ve used my protected time without interruption on three days this week.',
            d: [
              {
                t: "My chosen activity begun at the blocked time", s: ['Stop what I am doing', 'Silence my phone', 'Begin the activity I chose'],
                w: 'Start the session on time, so my personal time begins as planned.',
                k: 'I\'ll know I\'m done when my chosen activity has begun at the blocked time.',
                sw: [
                  'Stop what I am doing, so I can switch to my own time.',
                  'Silence my phone, so nothing pulls me away.',
                  'Begin the activity I chose, so my session truly starts.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve put the other task down.',
                  'I\'ll know I\'m done when my phone is silent.',
                  'I\'ll know I\'m done when I\'m doing the activity.',
                ],
              },
              {
                t: "Session finished with no interruption ending it early", s: ['Decide what counts as an emergency', 'Say I am busy until a set time', 'Return to the activity'],
                w: 'Handle interruptions firmly, so my session stays mine.',
                k: 'I\'ll know I\'m done when no interruption has ended my session early.',
                sw: [
                  'Decide what counts as an emergency, so I know when to break my time.',
                  'Say I am busy until a set time, so others know when I\'m free again.',
                  'Return to the activity, so the interruption doesn\'t end my time.',
                ],
                sk: [
                  'I\'ll know I\'m done when the rule is written.',
                  'I\'ll know I\'m done when I\'ve said it to anyone who interrupted.',
                  'I\'ll know I\'m done when I\'m back at the activity.',
                ],
              },
              {
                t: "Whole session spent on my activity, not chores or email", s: ['Leave chores and email for later', 'Write any reminders on paper', 'Keep going until the session ends'],
                w: 'Protect the session from creeping tasks, so my personal time isn\'t eaten by chores.',
                k: 'I\'ll know I\'m done when I\'ve spent the whole session on my own activity, not on chores or email.',
                sw: [
                  'Leave chores and email for later, so they can\'t take my time.',
                  'Write any reminders on paper, so I can put them aside and stay in my session.',
                  'Keep going until the session ends, so I get the whole hour.',
                ],
                sk: [
                  'I\'ll know I\'m done when they\'re left untouched.',
                  'I\'ll know I\'m done when each stray thought is on paper.',
                  'I\'ll know I\'m done when the blocked time has run out.',
                ],
              },
            ],
          },
          {
            t: 'Stop one thing that eats my free time', c: 'weekly',
            p: 'You do not find time; you take it from something else.',
            w: 'Stop one thing that eats my free time, so I have more time that is only mine.',
            k: 'I\'ll know I\'m done when I\'ve cut one time drain and used the freed minutes this week.',
            d: [
              {
                t: "Screen-time report checked and one time drain chosen", s: ['Check my phone screen-time report', 'Note the top three time drains', 'Choose the one I will cut'],
                w: 'Find where my time goes, so I know which drain to cut.',
                k: 'I\'ll know I\'m done when I\'ve checked my screen-time report and chosen one drain to cut.',
                sw: [
                  'Check my phone screen-time report, so I see where time really goes.',
                  'Note the top three time drains, so I know the biggest costs.',
                  'Choose the one I will cut, so I focus on one drain.',
                ],
                sk: [
                  'I\'ll know I\'m done when the report is open.',
                  'I\'ll know I\'m done when three are written.',
                  'I\'ll know I\'m done when one is chosen.',
                ],
              },
              {
                t: "Limit set on the drain, app off my home screen", s: ['Set an app limit or timer', 'Move the app off my home screen', 'Tell myself what I will do instead'],
                w: 'Set a limit on the drain, so it stops eating my free time.',
                k: 'I\'ll know I\'m done when the limit is set and the app is off my home screen.',
                sw: [
                  'Set an app limit or timer, so there\'s a hard stop.',
                  'Move the app off my home screen, so it\'s harder to open without thinking.',
                  'Tell myself what I will do instead, so the freed time has a use.',
                ],
                sk: [
                  'I\'ll know I\'m done when the limit is on.',
                  'I\'ll know I\'m done when the app is moved.',
                  'I\'ll know I\'m done when the replacement is written.',
                ],
              },
              {
                t: "Saved minutes counted and spent on something of mine", s: ['Note the minutes I saved today', 'Spend them on something I chose', 'Record how it felt'],
                w: 'Use the freed time, so the minutes I save go to something I chose.',
                k: 'I\'ll know I\'m done when the saved minutes are counted and spent on something of mine.',
                sw: [
                  'Note the minutes I saved today, so I see the gain.',
                  'Spend them on something I chose, so the time goes to me.',
                  'Record how it felt, so I remember why the cut was worth it.',
                ],
                sk: [
                  'I\'ll know I\'m done when I have a number.',
                  'I\'ll know I\'m done when I\'ve used them.',
                  'I\'ll know I\'m done when one line is written.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Do one thing I love at least once a week',
        w: 'Do one thing I love at least once a week, so I have time carved out just for me.',
        k: 'I\'ll know I\'m done when I\'ve done something I love in each week of the month.',
        a: [
          {
            t: 'List things I enjoy and pick one for the week', c: 'weekly',
            p: 'Joy needs a place in the diary.',
            w: 'List things I enjoy and pick one for the week, so I know what I\'ll do for myself.',
            k: 'I\'ll know I\'m done when this week\'s activity is chosen and in my calendar.',
            d: [
              {
                t: "Ten things I enjoy listed, three least-recent starred", s: ['Set a 5 minute timer', 'Write ten things that make me happy', 'Star the three I did least recently'],
                w: 'List ten things I enjoy, so I have a pool of things to pick from.',
                k: 'I\'ll know I\'m done when ten things are written and the three I did least recently are starred.',
                sw: [
                  'Set a 5 minute timer, so I write quickly without overthinking.',
                  'Write ten things that make me happy, so my list is full.',
                  'Star the three I did least recently, so I pick what I\'ve missed.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when ten items are written.',
                  'I\'ll know I\'m done when three are starred.',
                ],
              },
              {
                t: "One enjoyable activity chosen and in my calendar", s: ['Choose one starred activity', 'Note what it needs to happen', 'Put it in my calendar'],
                w: 'Pick this week\'s activity, so I know what I\'m doing for myself.',
                k: 'I\'ll know I\'m done when one activity is chosen and in my calendar.',
                sw: [
                  'Choose one starred activity, so I commit to one.',
                  'Note what it needs to happen, so I know what to arrange.',
                  'Put it in my calendar, so it will actually happen.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when its needs are written.',
                  'I\'ll know I\'m done when it\'s in my calendar.',
                ],
              },
              {
                t: "Everything for my activity ready, with a reminder set", s: ['Gather anything I need', 'Book or order if required', 'Set a reminder for the day before'],
                w: 'Prepare for it, so nothing stops me doing the activity.',
                k: 'I\'ll know I\'m done when everything needed is ready and a reminder is set.',
                sw: [
                  'Gather anything I need, so I\'m ready to go.',
                  'Book or order if required, so the activity can go ahead.',
                  'Set a reminder for the day before, so I don\'t forget it.',
                ],
                sk: [
                  'I\'ll know I\'m done when the items are together.',
                  'I\'ll know I\'m done when the booking or order is made.',
                  'I\'ll know I\'m done when the reminder is set.',
                ],
              },
            ],
          },
          {
            t: 'Do the activity fully present', c: 'weekly · 1 hr',
            p: 'Half-attention gives half the rest.',
            w: 'Do the activity fully present, so I get real rest from the thing I love.',
            k: 'I\'ll know I\'m done when I\'ve done my chosen activity for a full hour this week with my full attention.',
            d: [
              {
                t: "A full hour of my activity done, phone away", s: ['Go to the place or open the activity', 'Leave my phone out of reach', 'Stay with it for a full hour'],
                w: 'Do my chosen activity, so I spend an hour on something I love.',
                k: 'I\'ll know I\'m done when I\'ve done the activity for a full hour with my phone out of reach.',
                sw: [
                  'Go to the place or open the activity, so I begin.',
                  'Leave my phone out of reach, so I stay present.',
                  'Stay with it for a full hour, so I get the full benefit.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve started.',
                  'I\'ll know I\'m done when my phone is away.',
                  'I\'ll know I\'m done when an hour has passed.',
                ],
              },
              {
                t: "A mid-activity pause done, with one line about it written", s: ['Pause halfway through', 'Notice my mood and energy', 'Write one line about it afterwards'],
                w: 'Notice how I feel while doing it, so I see what the activity gives me.',
                k: 'I\'ll know I\'m done when I\'ve paused mid-way and written one line afterwards.',
                sw: [
                  'Pause halfway through, so I check in with myself.',
                  'Notice my mood and energy, so I see what the activity does for me.',
                  'Write one line about it afterwards, so I remember how it felt.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve paused once.',
                  'I\'ll know I\'m done when I\'ve named my mood and energy.',
                  'I\'ll know I\'m done when the line is written.',
                ],
              },
              {
                t: "The full hour finished despite any guilt", s: ['Notice any guilty thoughts', 'Remind myself this time is planned', 'Finish the full hour'],
                w: 'Keep it going without guilt, so I stay present for the whole hour.',
                k: 'I\'ll know I\'m done when I\'ve stayed with the activity for the full hour despite any guilt.',
                sw: [
                  'Notice any guilty thoughts, so they don\'t steer me.',
                  'Remind myself this time is planned, so I can let the guilt go.',
                  'Finish the full hour, so I give myself all of my time.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve noticed them.',
                  'I\'ll know I\'m done when I\'ve told myself so.',
                  'I\'ll know I\'m done when the hour is over.',
                ],
              },
            ],
          },
          {
            t: 'Take a short solo break each day', c: 'daily · 15 min',
            p: 'Small daily rests prevent large collapses.',
            w: 'Take a short solo break each day, so even busy days include time for me.',
            k: 'I\'ll know I\'m done when I\'ve taken a 15 minute solo break on every day this week.',
            d: [
              {
                t: "15 minutes alone with nothing needed done", s: ['Pick a time with no demands', 'Go somewhere I can be alone', 'Do nothing needed for 15 minutes'],
                w: 'Take a solo break, so I have 15 minutes alone today.',
                k: 'I\'ll know I\'m done when I\'ve done nothing needed for 15 minutes alone.',
                sw: [
                  'Pick a time with no demands, so my break won\'t be interrupted.',
                  'Go somewhere I can be alone, so I get real solitude.',
                  'Do nothing needed for 15 minutes, so the break is a true rest.',
                ],
                sk: [
                  'I\'ll know I\'m done when the time is chosen.',
                  'I\'ll know I\'m done when I\'m alone.',
                  'I\'ll know I\'m done when 15 minutes have passed.',
                ],
              },
              {
                t: "15 minutes of music, a walk or a drink enjoyed", s: ['Choose music, a walk or a drink', 'Do it for 15 minutes', 'Return to the day refreshed'],
                w: 'Do a pleasant solo activity, so my break refreshes me.',
                k: 'I\'ll know I\'m done when I\'ve enjoyed 15 minutes of music, a walk or a drink.',
                sw: [
                  'Choose music, a walk or a drink, so I pick something I like.',
                  'Do it for 15 minutes, so the break is long enough to help.',
                  'Return to the day refreshed, so the break pays off.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve chosen.',
                  'I\'ll know I\'m done when 15 minutes are done.',
                  'I\'ll know I\'m done when I\'m back to my tasks.',
                ],
              },
              {
                t: "Tomorrow's break time, activity and reminder set", s: ['Pick tomorrow\'s time', 'Pick tomorrow\'s activity', 'Set a reminder'],
                w: 'Plan tomorrow\'s break, so my daily solo break keeps going.',
                k: 'I\'ll know I\'m done when tomorrow\'s time, activity and reminder are set.',
                sw: [
                  'Pick tomorrow\'s time, so the break has a slot.',
                  'Pick tomorrow\'s activity, so I know what I\'ll do.',
                  'Set a reminder, so I don\'t skip it.',
                ],
                sk: [
                  'I\'ll know I\'m done when the time is chosen.',
                  'I\'ll know I\'m done when the activity is chosen.',
                  'I\'ll know I\'m done when the reminder is set.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Take one half-day or full day entirely for myself',
        w: 'Take one half-day or full day entirely for myself, so I have time carved out just for me.',
        k: 'I\'ll know I\'m done when I\'ve spent a half-day or full day entirely on myself this month.',
        a: [
          {
            t: 'Choose and book the day', c: 'once this month',
            p: 'A day off is built, not found.',
            w: 'Choose and book the day, so my day for myself is really on the calendar.',
            k: 'I\'ll know I\'m done when the date is blocked and my duties are covered.',
            d: [
              {
                t: "A low-commitment day chosen and blocked whole", s: ['Look at my calendar for the month', 'Choose a day with fewest commitments', 'Block the whole day'],
                w: 'Pick the date, so my day for myself has a place in the month.',
                k: 'I\'ll know I\'m done when a low-commitment day is chosen and blocked for the whole day.',
                sw: [
                  'Look at my calendar for the month, so I can see what\'s free.',
                  'Choose a day with fewest commitments, so the day is easy to protect.',
                  'Block the whole day, so nothing else gets booked in it.',
                ],
                sk: [
                  'I\'ll know I\'m done when the month is open.',
                  'I\'ll know I\'m done when one day is chosen.',
                  'I\'ll know I\'m done when the day is blocked.',
                ],
              },
              {
                t: "Each duty on that day covered or moved and confirmed", s: ['List duties that fall on that day', 'Ask someone to cover or move them', 'Confirm their reply'],
                w: 'Arrange cover for my duties, so I can take the day without worry.',
                k: 'I\'ll know I\'m done when each duty on that day is covered or moved and confirmed.',
                sw: [
                  'List duties that fall on that day, so I know what needs cover.',
                  'Ask someone to cover or move them, so my duties are handled.',
                  'Confirm their reply, so I know the cover is real.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when I\'ve asked.',
                  'I\'ll know I\'m done when each reply is confirmed.',
                ],
              },
              {
                t: "Three ideas written, with anything needed booked", s: ['Write three things I might enjoy', 'Book or buy anything needed', 'Leave the rest open'],
                w: 'Plan loosely what I will do, so my day is ready but not packed.',
                k: 'I\'ll know I\'m done when three ideas are written and anything needed is booked.',
                sw: [
                  'Write three things I might enjoy, so I have options.',
                  'Book or buy anything needed, so nothing blocks me on the day.',
                  'Leave the rest open, so the day stays free.',
                ],
                sk: [
                  'I\'ll know I\'m done when three ideas are written.',
                  'I\'ll know I\'m done when bookings or purchases are made.',
                  'I\'ll know I\'m done when no more is scheduled.',
                ],
              },
            ],
          },
          {
            t: 'Prepare so the day stays free', c: 'weekly',
            p: 'Prepare today so tomorrow can be free.',
            w: 'Prepare so the day stays free, so nothing pulls me out of my day for myself.',
            k: 'I\'ll know I\'m done when my inbox, practical needs and boundaries are ready for the day.',
            d: [
              {
                t: "Urgent messages answered, tasks moved, out-of-office on", s: ['Answer urgent messages', 'Move non-urgent tasks to another day', 'Set an out-of-office reply'],
                w: 'Clear my inbox and tasks ahead of the day, so work doesn\'t follow me into my day off.',
                k: 'I\'ll know I\'m done when urgent messages are answered, other tasks moved and an out-of-office reply is on.',
                sw: [
                  'Answer urgent messages, so nothing important is left hanging.',
                  'Move non-urgent tasks to another day, so they don\'t pull on my day.',
                  'Set an out-of-office reply, so others know I\'m away.',
                ],
                sk: [
                  'I\'ll know I\'m done when no urgent message is waiting.',
                  'I\'ll know I\'m done when they\'re rescheduled.',
                  'I\'ll know I\'m done when the reply is on.',
                ],
              },
              {
                t: "Meals planned, devices charged, clothes laid out", s: ['Plan meals for the day', 'Charge devices and pack what I need', 'Set my clothes out the evening before'],
                w: 'Sort the practical basics, so the day runs smoothly.',
                k: 'I\'ll know I\'m done when meals are planned, devices charged and clothes laid out.',
                sw: [
                  'Plan meals for the day, so I don\'t have to think about food.',
                  'Charge devices and pack what I need, so I\'m ready to go.',
                  'Set my clothes out the evening before, so the morning is easy.',
                ],
                sk: [
                  'I\'ll know I\'m done when meals are planned.',
                  'I\'ll know I\'m done when everything is charged and packed.',
                  'I\'ll know I\'m done when clothes are laid out.',
                ],
              },
              {
                t: "Others told I'm unavailable, work messages off-limits", s: ['Tell others I am unavailable', 'Decide that I will not check work messages', 'Remind myself of the plan the night before'],
                w: 'Protect the day from last-minute requests, so my day stays mine.',
                k: 'I\'ll know I\'m done when others know I\'m unavailable and I\'ve decided not to check work messages.',
                sw: [
                  'Tell others I am unavailable, so they don\'t ask me for things.',
                  'Decide that I will not check work messages, so work stays outside my day.',
                  'Remind myself of the plan the night before, so I\'m ready to keep it.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve told them.',
                  'I\'ll know I\'m done when the decision is written.',
                  'I\'ll know I\'m done when I\'ve reread my plan.',
                ],
              },
            ],
          },
          {
            t: 'Reflect on how the day went', c: 'once this month',
            p: 'What restores you deserves repeating.',
            w: 'Reflect on how the day went, so I learn what restores me and make it repeatable.',
            k: 'I\'ll know I\'m done when I\'ve written about the day and booked the next one.',
            d: [
              {
                t: "What I did, what restored me, what to drop noted", s: ['Note what I did', 'Note what restored me most', 'Note what I would drop'],
                w: 'Write about the day, so I capture what the day gave me.',
                k: 'I\'ll know I\'m done when I\'ve noted what I did, what restored me most and what I would drop.',
                sw: [
                  'Note what I did, so I have a record of the day.',
                  'Note what restored me most, so I know what to repeat.',
                  'Note what I would drop, so next time is better.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when one thing is written.',
                  'I\'ll know I\'m done when one thing is written.',
                ],
              },
              {
                t: "The best part of the day chosen and scheduled", s: ['Choose the best part of the day', 'Decide how often I can repeat it', 'Add it to my calendar'],
                w: 'Pick what to repeat, so the best of the day becomes a habit.',
                k: 'I\'ll know I\'m done when the best part is chosen and in my calendar.',
                sw: [
                  'Choose the best part of the day, so I know what to keep.',
                  'Decide how often I can repeat it, so the plan is realistic.',
                  'Add it to my calendar, so it happens.',
                ],
                sk: [
                  'I\'ll know I\'m done when one part is chosen.',
                  'I\'ll know I\'m done when a frequency is written.',
                  'I\'ll know I\'m done when it\'s in my calendar.',
                ],
              },
              {
                t: "Next month's date blocked for myself", s: ['Open the calendar for next month', 'Choose a date', 'Block it now'],
                w: 'Plan the next day for myself, so another day for myself is on the calendar.',
                k: 'I\'ll know I\'m done when next month\'s date is blocked.',
                sw: [
                  'Open the calendar for next month, so I can find a date.',
                  'Choose a date, so my next day has a place.',
                  'Block it now, so it\'s protected.',
                ],
                sk: [
                  'I\'ll know I\'m done when next month\'s calendar is open.',
                  'I\'ll know I\'m done when a date is chosen.',
                  'I\'ll know I\'m done when the date is blocked.',
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // ───────────────────────── 6. Having clarity on what I actually want from life
  {
    e: 'Having clarity on what I actually want from life', deity: 'elis',
    w: 'I\'m building clarity on what I actually want from life, so I spend my years on goals that are truly mine.',
    k: 'I\'ll know I\'m there when I can state my top wants, they are my own and I\'m acting on them.',
    g: [
      {
        t: 'Write a clear list of what I want in each area of life',
        w: 'Write a clear list of what I want in each area of life, so I have clarity on what I actually want.',
        k: 'I\'ll know I\'m done when a ranked list of my own wants for each area of life is saved.',
        a: [
          {
            t: 'Write for 15 minutes on what I want', c: '3×/week',
            p: 'You cannot aim at a target you have not named.',
            w: 'Write for 15 minutes on what I want, so I build a clear list of what I want in each area of life.',
            k: 'I\'ll know I\'m done when I\'ve written for 15 minutes on what I want on three days this week.',
            d: [
              {
                t: "15 minutes on work and money wants written", s: ['Set a 15 minute timer', 'Write what I want, without filtering', 'Circle the three strongest wants'],
                w: 'Write what I want from work and money, so my list covers work and money.',
                k: 'I\'ll know I\'m done when 15 minutes of wants are written and the three strongest are circled.',
                sw: [
                  'Set a 15 minute timer, so I write without overthinking.',
                  'Write what I want, without filtering, so my list is honest.',
                  'Circle the three strongest wants, so I see what matters most about work and money.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the timer ends with a full page.',
                  'I\'ll know I\'m done when three wants are circled.',
                ],
              },
              {
                t: "Wants for my relationships and my body written", s: ['Set a 15 minute timer', 'Write what I want for the people close to me', 'Write what I want for my body and energy'],
                w: 'Write what I want from relationships and health, so my list covers the people and body I care for.',
                k: 'I\'ll know I\'m done when I\'ve written wants for both the people close to me and my body.',
                sw: [
                  'Set a 15 minute timer, so I write with focus.',
                  'Write what I want for the people close to me, so relationships are on my list.',
                  'Write what I want for my body and energy, so health is on my list.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the wants are written.',
                  'I\'ll know I\'m done when the wants are written.',
                ],
              },
              {
                t: "Wants for my inner life written, repeating ones circled", s: ['Set a 15 minute timer', 'Write how I want to feel most days', 'Circle the wants that repeat'],
                w: 'Write what I want from my own inner life, so my list covers how I want to feel.',
                k: 'I\'ll know I\'m done when my wants for how I feel are written and the repeating ones are circled.',
                sw: [
                  'Set a 15 minute timer, so I write without overthinking.',
                  'Write how I want to feel most days, so my inner wants are on the page.',
                  'Circle the wants that repeat, so I see what matters most inside.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the timer ends with a full page.',
                  'I\'ll know I\'m done when the repeats are circled.',
                ],
              },
            ],
          },
          {
            t: 'Separate what I want from what others expect', c: 'weekly',
            p: 'Borrowed goals feel empty when reached.',
            w: 'Separate what I want from what others expect, so my list holds only my own wants.',
            k: 'I\'ll know I\'m done when my wants are marked as mine or borrowed and I\'ve saved a list of only my own.',
            d: [
              {
                t: "Every want marked mine, family's or society's, mine counted", s: ['Open my list of wants', 'Mark each one: mine, family\'s, society\'s', 'Count how many are truly mine'],
                w: 'Mark whose want each one is, so I see which wants are truly mine.',
                k: 'I\'ll know I\'m done when every want is marked mine, family\'s or society\'s and I\'ve counted mine.',
                sw: [
                  'Open my list of wants, so I can sort them.',
                  'Mark each one: mine, family\'s, society\'s, so I see where each want comes from.',
                  'Count how many are truly mine, so I know how much of my list is my own.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is open.',
                  'I\'ll know I\'m done when every want has a mark.',
                  'I\'ll know I\'m done when I have a number.',
                ],
              },
              {
                t: "One doubtful want kept or crossed out", s: ['Pick a want I am unsure about', 'Ask whether I would want it if no one knew', 'Keep it or cross it out'],
                w: 'Test the doubtful wants, so I know whether they\'re really mine.',
                k: 'I\'ll know I\'m done when one doubtful want is kept or crossed out.',
                sw: [
                  'Pick a want I am unsure about, so I test the right one.',
                  'Ask whether I would want it if no one knew, so I find if it\'s really mine.',
                  'Keep it or cross it out, so my list gets cleaner.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when I\'ve answered.',
                  'I\'ll know I\'m done when the want is kept or crossed out.',
                ],
              },
              {
                t: "A ranked list of only my own wants saved", s: ['Copy only the wants that are mine', 'Rank them by how much they matter', 'Save the new list'],
                w: 'Rewrite the list with only my own wants, so my list shows only what I truly want.',
                k: 'I\'ll know I\'m done when a ranked list of my own wants is saved.',
                sw: [
                  'Copy only the wants that are mine, so borrowed wants drop away.',
                  'Rank them by how much they matter, so I see what matters most.',
                  'Save the new list, so I keep it.',
                ],
                sk: [
                  'I\'ll know I\'m done when the new list is written.',
                  'I\'ll know I\'m done when the list is ranked.',
                  'I\'ll know I\'m done when it\'s saved.',
                ],
              },
            ],
          },
          {
            t: 'Rank my wants and choose my top three', c: 'weekly',
            p: 'Everything cannot come first.',
            w: 'Rank my wants and choose my top three, so I know what comes first.',
            k: 'I\'ll know I\'m done when my top three wants are written and the rest are marked not now.',
            d: [
              {
                t: "Every pair of wants compared and the wins tallied", s: ['List my wants in a column', 'Compare two at a time', 'Tally which one wins most'],
                w: 'Compare the wants in pairs, so I rank them fairly.',
                k: 'I\'ll know I\'m done when every pair is compared and the wins are tallied.',
                sw: [
                  'List my wants in a column, so I can compare them.',
                  'Compare two at a time, so I choose between them one by one.',
                  'Tally which one wins most, so I get a ranking.',
                ],
                sk: [
                  'I\'ll know I\'m done when the column is written.',
                  'I\'ll know I\'m done when I\'ve compared all pairs.',
                  'I\'ll know I\'m done when the tally is complete.',
                ],
              },
              {
                t: "Top three wants picked, each with a sentence", s: ['Look at the tally', 'Pick the three that lead', 'Write one sentence for each'],
                w: 'Choose my top three, so I know which three wants lead.',
                k: 'I\'ll know I\'m done when three wants are picked, each with a sentence.',
                sw: [
                  'Look at the tally, so I see the ranking.',
                  'Pick the three that lead, so I commit to my top three.',
                  'Write one sentence for each, so I say why each matters.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read the tally.',
                  'I\'ll know I\'m done when three are picked.',
                  'I\'ll know I\'m done when three sentences are written.',
                ],
              },
              {
                t: "Remaining wants marked not now, final list read", s: ['List the wants that did not make the top three', 'Write "not now" beside each', 'Read the final list once'],
                w: 'Say what I will not chase now, so my top three get my attention.',
                k: 'I\'ll know I\'m done when each remaining want is marked not now and I\'ve read the final list.',
                sw: [
                  'List the wants that did not make the top three, so I see what I\'m setting aside.',
                  'Write "not now" beside each, so I free myself from chasing them.',
                  'Read the final list once, so I lock in my choice.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when each is marked.',
                  'I\'ll know I\'m done when I\'ve read it through.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Describe my ideal day, year and decade in writing',
        w: 'Describe my ideal day, year and decade in writing, so I have clarity on what I actually want.',
        k: 'I\'ll know I\'m done when my ideal day, year and decade are each written down.',
        a: [
          {
            t: 'Write one section of my future vision', c: '2×/week',
            p: 'A vision written down starts to become a plan.',
            w: 'Write one section of my future vision, so I describe my ideal day, year and decade.',
            k: 'I\'ll know I\'m done when I\'ve written two sections of my vision this week.',
            d: [
              {
                t: "My ideal day written waking to sleep, differences underlined", s: ['Set a 15 minute timer', 'Write the day from waking to sleep', 'Underline what is different from today'],
                w: 'Describe my ideal ordinary day, so I see how I want to live day to day.',
                k: 'I\'ll know I\'m done when my ideal day is written from waking to sleep and the differences are underlined.',
                sw: [
                  'Set a 15 minute timer, so I write without overthinking.',
                  'Write the day from waking to sleep, so my ideal day is complete.',
                  'Underline what is different from today, so I see what to change.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the day is written.',
                  'I\'ll know I\'m done when the differences are underlined.',
                ],
              },
              {
                t: "My life one year from now described, proudest point noted", s: ['Set a 15 minute timer', 'Write where I live, work and spend time', 'Note what I would be proudest of'],
                w: 'Describe my life one year from now, so I see where I want to be next year.',
                k: 'I\'ll know I\'m done when my year-ahead description is written, with what I\'d be proudest of.',
                sw: [
                  'Set a 15 minute timer, so I write without overthinking.',
                  'Write where I live, work and spend time, so the year-ahead picture is concrete.',
                  'Note what I would be proudest of, so I see what matters.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the picture is written.',
                  'I\'ll know I\'m done when one thing is written.',
                ],
              },
              {
                t: "My life ten years from now described, with regrets noted", s: ['Set a 15 minute timer', 'Write what I hope to have built', 'Note what I would regret not doing'],
                w: 'Describe my life ten years from now, so I see the long arc of what I want.',
                k: 'I\'ll know I\'m done when my ten-year description and my regrets are written.',
                sw: [
                  'Set a 15 minute timer, so I write without overthinking.',
                  'Write what I hope to have built, so my decade has substance.',
                  'Note what I would regret not doing, so I see what I must not skip.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the description is written.',
                  'I\'ll know I\'m done when one regret is written.',
                ],
              },
            ],
          },
          {
            t: 'Find the gap between my ideal and my real life', c: 'weekly',
            p: 'Honest gaps point to the real work.',
            w: 'Find the gap between my ideal and my real life, so I see what my vision asks me to change.',
            k: 'I\'ll know I\'m done when I\'ve named the cause of one gap and chosen which to close first.',
            d: [
              {
                t: "Every ideal-versus-real difference marked, biggest three picked", s: ['Put both day descriptions side by side', 'Mark every difference', 'Pick the three biggest differences'],
                w: 'Compare my ideal day to my real day, so I see where my life differs from my vision.',
                k: 'I\'ll know I\'m done when every difference is marked and the three biggest are picked.',
                sw: [
                  'Put both day descriptions side by side, so I can compare them.',
                  'Mark every difference, so I see the full gap.',
                  'Pick the three biggest differences, so I focus on what matters.',
                ],
                sk: [
                  'I\'ll know I\'m done when both are in view.',
                  'I\'ll know I\'m done when the differences are marked.',
                  'I\'ll know I\'m done when three are picked.',
                ],
              },
              {
                t: "The cause of one gap written plainly", s: ['Choose one big difference', 'Ask what stops it from being real', 'Write the answer plainly'],
                w: 'Find the cause of one gap, so I understand what blocks my ideal.',
                k: 'I\'ll know I\'m done when the cause is written plainly.',
                sw: [
                  'Choose one big difference, so I look at one gap.',
                  'Ask what stops it from being real, so I find the cause.',
                  'Write the answer plainly, so the cause is clear.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when I\'ve asked it.',
                  'I\'ll know I\'m done when the answer is written.',
                ],
              },
              {
                t: "One gap chosen, with its first small step written", s: ['Look at the three biggest gaps', 'Pick the one I can act on soon', 'Write the first small step'],
                w: 'Choose one gap to close first, so I know where to start.',
                k: 'I\'ll know I\'m done when one gap is chosen and its first small step is written.',
                sw: [
                  'Look at the three biggest gaps, so I compare them.',
                  'Pick the one I can act on soon, so I start with a doable gap.',
                  'Write the first small step, so I know how to begin.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve reviewed all three.',
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when the step is written.',
                ],
              },
            ],
          },
          {
            t: 'Make one small change toward the ideal', c: 'weekly',
            p: 'One small change beats a grand intention.',
            w: 'Make one small change toward the ideal, so my vision starts shaping my real life.',
            k: 'I\'ll know I\'m done when I\'ve made one small change toward my ideal this week and decided whether to keep it.',
            d: [
              {
                t: "A change under an hour chosen and scheduled", s: ['Look at the gap I chose', 'Pick a change that takes under an hour', 'Write when I will do it'],
                w: 'Choose the small change, so I know what to do this week.',
                k: 'I\'ll know I\'m done when a change under an hour is chosen and scheduled.',
                sw: [
                  'Look at the gap I chose, so I know what I\'m closing.',
                  'Pick a change that takes under an hour, so it\'s easy to do.',
                  'Write when I will do it, so it happens.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve reviewed it.',
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when the time is written.',
                ],
              },
              {
                t: "The change made, with resistance and feelings noted", s: ['Do the change as planned', 'Note any resistance I felt', 'Note how it felt once done'],
                w: 'Make the change, so I move one step toward my ideal.',
                k: 'I\'ll know I\'m done when the change is done and I\'ve noted my resistance and how it felt.',
                sw: [
                  'Do the change as planned, so I act on my vision.',
                  'Note any resistance I felt, so I understand what holds me back.',
                  'Note how it felt once done, so I know if it\'s worth keeping.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s done.',
                  'I\'ll know I\'m done when the resistance is written.',
                  'I\'ll know I\'m done when one line is written.',
                ],
              },
              {
                t: "The change kept or adjusted and booked for next week", s: ['Decide whether it moved me closer', 'Adjust it if it did not', 'Repeat it next week'],
                w: 'Keep or adjust the change, so the change keeps moving me toward my ideal.',
                k: 'I\'ll know I\'m done when I\'ve decided to keep or adjust the change and booked it for next week.',
                sw: [
                  'Decide whether it moved me closer, so I judge its worth.',
                  'Adjust it if it did not, so I try something better.',
                  'Repeat it next week, so the change becomes steady.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve decided.',
                  'I\'ll know I\'m done when I\'ve adjusted or confirmed it.',
                  'I\'ll know I\'m done when it\'s in next week\'s plan.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Test three possible directions with small real experiments',
        w: 'Test three possible directions with small real experiments, so I get clear on what I actually want.',
        k: 'I\'ll know I\'m done when I\'ve run three small experiments and written what each taught me.',
        a: [
          {
            t: 'Run one small experiment toward a direction', c: 'weekly',
            p: 'You learn what you want by trying, not just thinking.',
            w: 'Run one small experiment toward a direction, so I learn what I want by trying it.',
            k: 'I\'ll know I\'m done when I\'ve run one experiment and judged its result this week.',
            d: [
              {
                t: "A direction, a one-day test and my question written", s: ['Pick a direction I am curious about', 'Choose a test that takes under a day', 'Write what I want to find out'],
                w: 'Design an experiment, so I have a clear test of one direction.',
                k: 'I\'ll know I\'m done when a direction, a test under a day and my question are written.',
                sw: [
                  'Pick a direction I am curious about, so I test something I care about.',
                  'Choose a test that takes under a day, so the experiment is small.',
                  'Write what I want to find out, so the test has a purpose.',
                ],
                sk: [
                  'I\'ll know I\'m done when one direction is chosen.',
                  'I\'ll know I\'m done when the test is chosen.',
                  'I\'ll know I\'m done when the question is written.',
                ],
              },
              {
                t: "The test done and what happened written", s: ['Do the test as planned', 'Notice my energy before and after', 'Write down what happened'],
                w: 'Run the experiment, so I learn from real experience.',
                k: 'I\'ll know I\'m done when the test is done and I\'ve written what happened.',
                sw: [
                  'Do the test as planned, so I get real results.',
                  'Notice my energy before and after, so I see how it affects me.',
                  'Write down what happened, so I keep the lesson.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s done.',
                  'I\'ll know I\'m done when both are noted.',
                  'I\'ll know I\'m done when it\'s written.',
                ],
              },
              {
                t: "A verdict of keep, change or drop written", s: ['Reread my notes', 'Ask whether I want more of this', 'Write keep, change or drop'],
                w: 'Judge the result, so I decide what the experiment says about what I want.',
                k: 'I\'ll know I\'m done when I\'ve written keep, change or drop.',
                sw: [
                  'Reread my notes, so I recall what happened.',
                  'Ask whether I want more of this, so I judge my own reaction.',
                  'Write keep, change or drop, so I decide what to do.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read them.',
                  'I\'ll know I\'m done when I\'ve answered.',
                  'I\'ll know I\'m done when the word is written.',
                ],
              },
            ],
          },
          {
            t: 'Talk to someone already living a path I am curious about', c: 'weekly',
            p: 'Talk to those already living the answer.',
            w: 'Talk to someone already living a path I am curious about, so I learn what a direction is really like.',
            k: 'I\'ll know I\'m done when I\'ve talked to one such person and written what I learned.',
            d: [
              {
                t: "A person picked, contact found, request written", s: ['Think of someone on a path I like', 'Find their contact details', 'Write a short, polite request'],
                w: 'Find the person, so I can talk to someone on a path I like.',
                k: 'I\'ll know I\'m done when I\'ve picked a person, found their contact and written my request.',
                sw: [
                  'Think of someone on a path I like, so I pick the right person.',
                  'Find their contact details, so I can reach them.',
                  'Write a short, polite request, so they can say yes easily.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve named them.',
                  'I\'ll know I\'m done when I have their contact.',
                  'I\'ll know I\'m done when the request is written.',
                ],
              },
              {
                t: "The conversation held, with notes", s: ['Prepare three questions about their daily life', 'Ask what they would warn me about', 'Take notes during the call'],
                w: 'Have the conversation, so I hear what their path is really like.',
                k: 'I\'ll know I\'m done when we\'ve talked and I have notes.',
                sw: [
                  'Prepare three questions about their daily life, so the talk is useful.',
                  'Ask what they would warn me about, so I learn the downsides.',
                  'Take notes during the call, so I keep what they say.',
                ],
                sk: [
                  'I\'ll know I\'m done when three questions are written.',
                  'I\'ll know I\'m done when I\'ve asked.',
                  'I\'ll know I\'m done when my notes are written.',
                ],
              },
              {
                t: "What attracts and puts me off written, thank-you sent", s: ['Reread my notes', 'Write what attracts and what puts me off', 'Thank them with a short message'],
                w: 'Sum up what I learned, so I know what their path means for my direction.',
                k: 'I\'ll know I\'m done when I\'ve written what attracts and puts me off and sent a thank-you.',
                sw: [
                  'Reread my notes, so I recall what they said.',
                  'Write what attracts and what puts me off, so I see how the path fits me.',
                  'Thank them with a short message, so they know I valued their time.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read them.',
                  'I\'ll know I\'m done when both are written.',
                  'I\'ll know I\'m done when the message is sent.',
                ],
              },
            ],
          },
          {
            t: 'Review what each experiment taught me', c: 'weekly',
            p: 'An experiment unreviewed teaches nothing.',
            w: 'Review what each experiment taught me, so my experiments add up to a clear direction.',
            k: 'I\'ll know I\'m done when I\'ve listed my results, chosen a direction and planned a next step.',
            d: [
              {
                t: "Every experiment listed with its result, enjoyed ones marked", s: ['Open my experiment notes', 'List each test with its result', 'Mark which ones I enjoyed'],
                w: 'List all experiments and results, so I see everything I\'ve tried.',
                k: 'I\'ll know I\'m done when every test is listed with its result and the enjoyed ones marked.',
                sw: [
                  'Open my experiment notes, so I can review them.',
                  'List each test with its result, so I see them all together.',
                  'Mark which ones I enjoyed, so I see what pulled me.',
                ],
                sk: [
                  'I\'ll know I\'m done when the notes are open.',
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when the enjoyed ones are marked.',
                ],
              },
              {
                t: "One direction chosen with a two-sentence reason", s: ['Compare my energy across experiments', 'Pick the direction that pulled me most', 'Write why in two sentences'],
                w: 'Choose the strongest direction, so I know which direction to follow.',
                k: 'I\'ll know I\'m done when one direction is chosen with a two-sentence reason.',
                sw: [
                  'Compare my energy across experiments, so I see where I felt most alive.',
                  'Pick the direction that pulled me most, so I commit to one.',
                  'Write why in two sentences, so my choice is clear.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve compared them.',
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when two sentences are written.',
                ],
              },
              {
                t: "A bigger test in my calendar and one person told", s: ['Write one bigger test for next month', 'Add it to my calendar', 'Tell one person about my plan'],
                w: 'Plan the next step in that direction, so my chosen direction becomes a real project.',
                k: 'I\'ll know I\'m done when a bigger test is in my calendar and one person knows.',
                sw: [
                  'Write one bigger test for next month, so I go deeper.',
                  'Add it to my calendar, so it happens.',
                  'Tell one person about my plan, so I\'m accountable.',
                ],
                sk: [
                  'I\'ll know I\'m done when the test is written.',
                  'I\'ll know I\'m done when it\'s in my calendar.',
                  'I\'ll know I\'m done when I\'ve told them.',
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // ───────────────────────── 7. Having a spiritual or reflective practice
  {
    e: 'Having a spiritual or reflective practice', deity: 'aidos',
    w: 'I\'m building a spiritual or reflective practice, so I have a steady source of meaning and peace that doesn\'t depend on circumstances.',
    k: 'I\'ll know I\'m there when quiet, reflection and gratitude are a regular part of my week and I draw on them.',
    g: [
      {
        t: 'Sit in silence, prayer or meditation on 20 days this month',
        w: 'Sit in silence, prayer or meditation on 20 days this month, so I have a spiritual or reflective practice.',
        k: 'I\'ll know I\'m done when I\'ve sat in silence, prayer or meditation on at least 20 days this month.',
        a: [
          {
            t: 'Sit in silence, prayer or meditation for 10 minutes', c: 'daily · 10 min',
            p: 'What you avoid defines you more than what you pursue.',
            w: 'Sit in silence, prayer or meditation for 10 minutes, so I sit on 20 days this month.',
            k: 'I\'ll know I\'m done when I\'ve sat for 10 minutes on at least five days this week.',
            d: [
              {
                t: "10 minutes of morning stillness sat before my phone", s: ['Wake and sit before checking my phone', 'Set a 10 minute timer', 'Return gently whenever my mind wanders'],
                w: 'Do the morning sit, so I start the day with silence, prayer or meditation.',
                k: 'I\'ll know I\'m done when I\'ve sat for 10 minutes this morning before checking my phone.',
                sw: [
                  'Wake and sit before checking my phone, so the sit comes first in my day.',
                  'Set a 10 minute timer, so I know when to stop.',
                  'Return gently whenever my mind wanders, so the full 10 minutes counts as practice.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'m seated with my phone untouched.',
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the timer ends.',
                ],
              },
              {
                t: "A quiet corner, a daily time and a seat set", s: ['Pick a quiet corner', 'Pick a fixed time each day', 'Place a cushion or chair there'],
                w: 'Choose a place and time that stay the same, so my sit becomes automatic.',
                k: 'I\'ll know I\'m done when a corner, a daily time and a seat are set.',
                sw: [
                  'Pick a quiet corner, so I have a place for my sit.',
                  'Pick a fixed time each day, so the sit has a regular slot.',
                  'Place a cushion or chair there, so the spot is ready for me.',
                ],
                sk: [
                  'I\'ll know I\'m done when the corner is chosen.',
                  'I\'ll know I\'m done when the time is written down.',
                  'I\'ll know I\'m done when the seat is in place.',
                ],
              },
              {
                t: "3 minutes of stillness sat, one thankfulness named", s: ['Set a 3 minute timer', 'Close my eyes and breathe slowly', 'Finish with one thing I am thankful for'],
                w: 'Do a shorter sit on a busy day, so I still sit on days when time is tight.',
                k: 'I\'ll know I\'m done when I\'ve sat for 3 minutes and named one thing I\'m thankful for.',
                sw: [
                  'Set a 3 minute timer, so a short sit is easy to start.',
                  'Close my eyes and breathe slowly, so I still settle in a short sit.',
                  'Finish with one thing I am thankful for, so even a short sit ends well.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the timer ends.',
                  'I\'ll know I\'m done when I\'ve named one thing.',
                ],
              },
            ],
          },
          {
            t: 'Read from a spiritual or wisdom text', c: '3×/week',
            p: 'Old wisdom is tested wisdom.',
            w: 'Read from a spiritual or wisdom text, so my sits are fed by something deeper than my own thoughts.',
            k: 'I\'ll know I\'m done when I\'ve read a passage slowly on three days this week.',
            d: [
              {
                t: "A text chosen and placed by my sitting spot", s: ['Pick a text that speaks to me', 'Find a short readable edition', 'Place it by my sitting spot'],
                w: 'Choose a text, so I have a wisdom text ready to read.',
                k: 'I\'ll know I\'m done when a text is chosen and sits by my sitting spot.',
                sw: [
                  'Pick a text that speaks to me, so I read something that matters to me.',
                  'Find a short readable edition, so reading it is easy.',
                  'Place it by my sitting spot, so it\'s always at hand.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when I have a copy.',
                  'I\'ll know I\'m done when it\'s there.',
                ],
              },
              {
                t: "A passage read twice, one line sat with two minutes", s: ['Read a page or paragraph twice', 'Underline the line that stays with me', 'Sit with that line for two minutes'],
                w: 'Read one short passage slowly, so I take in a bit of wisdom today.',
                k: 'I\'ll know I\'m done when I\'ve read a passage twice and sat with one line for two minutes.',
                sw: [
                  'Read a page or paragraph twice, so I take in what it says.',
                  'Underline the line that stays with me, so I find the part that speaks to me.',
                  'Sit with that line for two minutes, so it sinks in.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read it twice.',
                  'I\'ll know I\'m done when a line is underlined.',
                  'I\'ll know I\'m done when two minutes have passed.',
                ],
              },
              {
                t: "What the passage asks of me written and done", s: ['Reread the underlined line', 'Write what it asks me to do', 'Do that one thing today'],
                w: 'Write what the passage asks of me, so my reading changes what I do.',
                k: 'I\'ll know I\'m done when I\'ve written what it asks and done that one thing today.',
                sw: [
                  'Reread the underlined line, so I recall the part that spoke to me.',
                  'Write what it asks me to do, so I turn the line into an action.',
                  'Do that one thing today, so my reading shapes my day.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read it.',
                  'I\'ll know I\'m done when the action is written.',
                  'I\'ll know I\'m done when the action is done.',
                ],
              },
            ],
          },
          {
            t: 'Practise gratitude at the end of the day', c: 'daily · 3 min',
            p: 'What you notice, you grow.',
            w: 'Practise gratitude at the end of the day, so I close each day with a thankful mind.',
            k: 'I\'ll know I\'m done when I\'ve practised gratitude on every evening this week.',
            d: [
              {
                t: "Three good things from today named, with why each mattered", s: ['Sit down before bed', 'Name three good things from today', 'Say why each mattered'],
                w: 'Name three things I am thankful for, so I end today with gratitude.',
                k: 'I\'ll know I\'m done when three good things from today are named with why each mattered.',
                sw: [
                  'Sit down before bed, so I give gratitude a moment.',
                  'Name three good things from today, so I notice what went right.',
                  'Say why each mattered, so my gratitude goes deeper.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'m seated.',
                  'I\'ll know I\'m done when three are named.',
                  'I\'ll know I\'m done when each has a reason.',
                ],
              },
              {
                t: "A thank-you message sent to one person", s: ['Pick someone who helped me this week', 'Write a short thank-you message', 'Send it'],
                w: 'Thank one person directly, so my gratitude reaches the person who earned it.',
                k: 'I\'ll know I\'m done when a thank-you message has been sent.',
                sw: [
                  'Pick someone who helped me this week, so my thanks goes to a real person.',
                  'Write a short thank-you message, so my thanks is specific.',
                  'Send it, so they actually receive it.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve named them.',
                  'I\'ll know I\'m done when the message is written.',
                  'I\'ll know I\'m done when it\'s sent.',
                ],
              },
              {
                t: "A line of thanks for a hard moment's lesson written", s: ['Recall a hard moment from today', 'Ask what it taught me', 'Write one line of thanks for the lesson'],
                w: 'Find gratitude in something difficult, so I can be thankful even on a hard day.',
                k: 'I\'ll know I\'m done when I\'ve written a line of thanks for a lesson from a hard moment.',
                sw: [
                  'Recall a hard moment from today, so I have something real to work with.',
                  'Ask what it taught me, so I find its lesson.',
                  'Write one line of thanks for the lesson, so I finish with gratitude.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve picked one.',
                  'I\'ll know I\'m done when I\'ve answered.',
                  'I\'ll know I\'m done when the line is written.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Spend a full morning or afternoon in quiet or nature, twice',
        w: 'Spend a full morning or afternoon in quiet or nature, twice, so I deepen my spiritual or reflective practice.',
        k: 'I\'ll know I\'m done when I\'ve spent a full morning or afternoon in quiet or nature on two separate days.',
        a: [
          {
            t: 'Spend time in nature', c: 'weekly · 1 hr',
            p: 'Nature restores what busyness wears down.',
            w: 'Spend time in nature, so I am restored by quiet time outdoors each week.',
            k: 'I\'ll know I\'m done when I\'ve spent an hour in nature this week.',
            d: [
              {
                t: "An hour's slow walk outside done without headphones", s: ['Choose a park, wood or shoreline', 'Leave headphones at home', 'Walk slowly for an hour'],
                w: 'Walk outside without a destination, so I spend an unhurried hour in nature.',
                k: 'I\'ll know I\'m done when I\'ve walked slowly for an hour without headphones.',
                sw: [
                  'Choose a park, wood or shoreline, so I have a place to walk.',
                  'Leave headphones at home, so I hear the place itself.',
                  'Walk slowly for an hour, so I give the walk real time.',
                ],
                sk: [
                  'I\'ll know I\'m done when the place is chosen.',
                  'I\'ll know I\'m done when my headphones are left behind.',
                  'I\'ll know I\'m done when the hour is up.',
                ],
              },
              {
                t: "20 minutes sat outside with no phone", s: ['Find a place to sit', 'Sit for 20 minutes without my phone', 'Notice sounds, smells and light'],
                w: 'Sit still outside, so I take in nature without doing anything.',
                k: 'I\'ll know I\'m done when I\'ve sat outside for 20 minutes with no phone.',
                sw: [
                  'Find a place to sit, so I can settle outdoors.',
                  'Sit for 20 minutes without my phone, so the quiet can reach me.',
                  'Notice sounds, smells and light, so I pay attention to nature.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'m seated outside.',
                  'I\'ll know I\'m done when 20 minutes have passed.',
                  'I\'ll know I\'m done when I\'ve named a few of each.',
                ],
              },
              {
                t: "Five minutes of sky-watching done at sunrise or sunset", s: ['Step outside at sunrise or sunset', 'Look at the sky for five minutes', 'Breathe slowly the whole time'],
                w: 'Watch the sky at the start or end of the day, so I mark the day with a quiet outdoor moment.',
                k: 'I\'ll know I\'m done when I\'ve watched the sky for five minutes at sunrise or sunset.',
                sw: [
                  'Step outside at sunrise or sunset, so I catch the sky at its best.',
                  'Look at the sky for five minutes, so I take it in properly.',
                  'Breathe slowly the whole time, so my body settles with the sky.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'m outside at that time.',
                  'I\'ll know I\'m done when five minutes have passed.',
                  'I\'ll know I\'m done when I\'ve breathed slowly throughout.',
                ],
              },
            ],
          },
          {
            t: 'Plan a longer period of quiet', c: 'weekly',
            p: 'Silence has to be scheduled.',
            w: 'Plan a longer period of quiet, so my full morning or afternoon of quiet actually happens.',
            k: 'I\'ll know I\'m done when the date, place and purpose of my longer quiet are set.',
            d: [
              {
                t: "A date, place and calendar block set for quiet", s: ['Choose a morning or afternoon this month', 'Choose a quiet place', 'Block it in my calendar'],
                w: 'Pick the date and place, so my longer quiet has a time and a place.',
                k: 'I\'ll know I\'m done when a morning or afternoon, a place and a calendar block are set.',
                sw: [
                  'Choose a morning or afternoon this month, so I have a date.',
                  'Choose a quiet place, so I know where I\'ll go.',
                  'Block it in my calendar, so nothing else claims the time.',
                ],
                sk: [
                  'I\'ll know I\'m done when the date is chosen.',
                  'I\'ll know I\'m done when the place is chosen.',
                  'I\'ll know I\'m done when it\'s blocked.',
                ],
              },
              {
                t: "People told, bag packed, travel checked", s: ['Tell the people close to me', 'Pack water, a notebook and a snack', 'Check travel time and cost'],
                w: 'Arrange what I need, so nothing gets in the way of my quiet time.',
                k: 'I\'ll know I\'m done when the people close to me are told, my bag is packed and the travel is checked.',
                sw: [
                  'Tell the people close to me, so they know where I am.',
                  'Pack water, a notebook and a snack, so I\'m comfortable for hours.',
                  'Check travel time and cost, so I can get there easily.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve told them.',
                  'I\'ll know I\'m done when my bag is packed.',
                  'I\'ll know I\'m done when both are checked.',
                ],
              },
              {
                t: "My question for the quiet written and placed in view", s: ['Write one question I want to sit with', 'Decide I will have no agenda otherwise', 'Leave the question where I will see it'],
                w: 'Decide what the time is for, so my quiet time has a gentle purpose.',
                k: 'I\'ll know I\'m done when my question is written and placed where I\'ll see it.',
                sw: [
                  'Write one question I want to sit with, so my quiet time has a focus.',
                  'Decide I will have no agenda otherwise, so the time stays open.',
                  'Leave the question where I will see it, so it comes with me.',
                ],
                sk: [
                  'I\'ll know I\'m done when the question is written.',
                  'I\'ll know I\'m done when I\'ve decided.',
                  'I\'ll know I\'m done when the note is in place.',
                ],
              },
            ],
          },
          {
            t: 'Write down what comes up in the quiet', c: 'weekly',
            p: 'Quiet shows what noise hides.',
            w: 'Write down what comes up in the quiet, so my quiet time gives me lasting insight.',
            k: 'I\'ll know I\'m done when I\'ve written in the quiet, reviewed it and chosen an insight to carry.',
            d: [
              {
                t: "30 minutes of quiet sat, what rose written down", s: ['Bring only a notebook and pen', 'Sit quietly for 30 minutes', 'Write whatever rises'],
                w: 'Take a notebook into the quiet, so I capture what rises in the silence.',
                k: 'I\'ll know I\'m done when I\'ve sat quietly for 30 minutes and written what rose.',
                sw: [
                  'Bring only a notebook and pen, so nothing distracts me.',
                  'Sit quietly for 30 minutes, so the quiet has time to work.',
                  'Write whatever rises, so I record what the quiet shows.',
                ],
                sk: [
                  'I\'ll know I\'m done when my bag holds only those.',
                  'I\'ll know I\'m done when 30 minutes have passed.',
                  'I\'ll know I\'m done when the pages are written.',
                ],
              },
              {
                t: "Surprises underlined and my remaining question noted", s: ['Reread my notes slowly', 'Underline what surprised me', 'Note the question that remains'],
                w: 'Review what I wrote, so I find what the quiet showed me.',
                k: 'I\'ll know I\'m done when I\'ve underlined what surprised me and noted my remaining question.',
                sw: [
                  'Reread my notes slowly, so I take them in.',
                  'Underline what surprised me, so I find the insights.',
                  'Note the question that remains, so I know what to keep exploring.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read them all.',
                  'I\'ll know I\'m done when the surprises are underlined.',
                  'I\'ll know I\'m done when the question is written.',
                ],
              },
              {
                t: "One insight chosen, tied to this week and posted visibly", s: ['Choose the most useful insight', 'Write how it changes this week', 'Put the note on my mirror or desk'],
                w: 'Carry one insight into daily life, so the quiet changes how I live.',
                k: 'I\'ll know I\'m done when one insight is chosen, tied to this week and posted where I\'ll see it.',
                sw: [
                  'Choose the most useful insight, so I act on the one that matters.',
                  'Write how it changes this week, so it becomes practical.',
                  'Put the note on my mirror or desk, so I see it daily.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when the change is written.',
                  'I\'ll know I\'m done when the note is in place.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Join or build a community for my reflective life',
        w: 'Join or build a community for my reflective life, so I deepen my reflective practice alongside others.',
        k: 'I\'ll know I\'m done when I belong to a gathering or group I attend or hold regularly.',
        a: [
          {
            t: 'Attend one gathering, group or service', c: 'weekly',
            p: 'We deepen faster alongside others.',
            w: 'Attend one gathering, group or service, so I find a community for my reflective life.',
            k: 'I\'ll know I\'m done when I\'ve attended one gathering this week.',
            d: [
              {
                t: "A group picked that suits my schedule, next date noted", s: ['Search for local reflective or faith groups', 'Pick one whose time suits me', 'Note the date of the next gathering'],
                w: 'Find a group to try, so I know where to go.',
                k: 'I\'ll know I\'m done when I\'ve picked a group that suits my schedule and noted its next date.',
                sw: [
                  'Search for local reflective or faith groups, so I see what\'s near me.',
                  'Pick one whose time suits me, so I can really attend.',
                  'Note the date of the next gathering, so I know when to go.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve seen the options.',
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when the date is written.',
                ],
              },
              {
                t: "Gathering attended, one person met, more listening than talking", s: ['Arrive five minutes early', 'Introduce myself to one person', 'Listen more than I speak'],
                w: 'Attend the gathering, so I join a community in person.',
                k: 'I\'ll know I\'m done when I\'ve attended, met one person and listened more than I spoke.',
                sw: [
                  'Arrive five minutes early, so I\'m settled and can meet people.',
                  'Introduce myself to one person, so I begin to belong.',
                  'Listen more than I speak, so I learn what the group is about.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'m there before the start.',
                  'I\'ll know I\'m done when I\'ve spoken to one person.',
                  'I\'ll know I\'m done when the gathering ends.',
                ],
              },
              {
                t: "Impressions written, the person messaged, return decided", s: ['Write what I liked and did not like', 'Message the person I met', 'Decide whether to return'],
                w: 'Follow up afterwards, so I decide whether this community is for me.',
                k: 'I\'ll know I\'m done when I\'ve written my impressions, messaged the person and decided whether to return.',
                sw: [
                  'Write what I liked and did not like, so I judge the group clearly.',
                  'Message the person I met, so a connection can grow.',
                  'Decide whether to return, so I commit or move on.',
                ],
                sk: [
                  'I\'ll know I\'m done when both are written.',
                  'I\'ll know I\'m done when the message is sent.',
                  'I\'ll know I\'m done when I\'ve decided.',
                ],
              },
            ],
          },
          {
            t: 'Discuss one big question with a friend', c: 'weekly',
            p: 'Good questions are better shared.',
            w: 'Discuss one big question with a friend, so my reflective life has company.',
            k: 'I\'ll know I\'m done when I\'ve talked a big question through with a friend this week.',
            d: [
              {
                t: "A question written, a friend picked and a talk requested", s: ['Write a question I am wrestling with', 'Pick a friend who thinks deeply', 'Message them to ask for a talk'],
                w: 'Choose the question, so I know what I want to discuss.',
                k: 'I\'ll know I\'m done when I\'ve written a question, picked a friend and asked for a talk.',
                sw: [
                  'Write a question I am wrestling with, so I know what to talk about.',
                  'Pick a friend who thinks deeply, so the talk is worthwhile.',
                  'Message them to ask for a talk, so it\'s set up.',
                ],
                sk: [
                  'I\'ll know I\'m done when the question is written.',
                  'I\'ll know I\'m done when I\'ve named them.',
                  'I\'ll know I\'m done when the message is sent.',
                ],
              },
              {
                t: "The discussion held, with their view heard", s: ['Meet or call without distractions', 'Share my question and my view', 'Ask for theirs and listen'],
                w: 'Have the discussion, so I explore the question with someone else.',
                k: 'I\'ll know I\'m done when we\'ve talked and I\'ve heard their view.',
                sw: [
                  'Meet or call without distractions, so we can really talk.',
                  'Share my question and my view, so they know where I stand.',
                  'Ask for theirs and listen, so I learn how it looks to them.',
                ],
                sk: [
                  'I\'ll know I\'m done when we\'re talking undisturbed.',
                  'I\'ll know I\'m done when I\'ve shared both.',
                  'I\'ll know I\'m done when they\'ve finished.',
                ],
              },
              {
                t: "What stuck and what I see differently written, friend thanked", s: ['Write what they said that stuck', 'Write what I now see differently', 'Thank them with a message'],
                w: 'Note what changed in my thinking, so the discussion leaves something lasting.',
                k: 'I\'ll know I\'m done when I\'ve written what stuck and what I see differently, and thanked them.',
                sw: [
                  'Write what they said that stuck, so I keep it.',
                  'Write what I now see differently, so I see how I changed.',
                  'Thank them with a message, so they know it mattered.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when the message is sent.',
                ],
              },
            ],
          },
          {
            t: 'Serve someone without being asked', c: 'weekly',
            p: 'Service turns belief into practice.',
            w: 'Serve someone without being asked, so my reflective life shows up in action.',
            k: 'I\'ll know I\'m done when I\'ve done one act of service this week and reflected on it.',
            d: [
              {
                t: "A person, an act and a time chosen", s: ['Think of someone who needs help', 'Pick a small act I can do', 'Decide when to do it'],
                w: 'Choose an act of service, so I know how I\'ll serve.',
                k: 'I\'ll know I\'m done when I\'ve picked someone, an act and a time.',
                sw: [
                  'Think of someone who needs help, so I choose a real need.',
                  'Pick a small act I can do, so I can really do it.',
                  'Decide when to do it, so it happens.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve named them.',
                  'I\'ll know I\'m done when the act is chosen.',
                  'I\'ll know I\'m done when the time is set.',
                ],
              },
              {
                t: "The act of service done, with how I feel noted", s: ['Do the act quietly', 'Expect nothing in return', 'Note how I feel afterwards'],
                w: 'Do the act, so I serve someone without being asked.',
                k: 'I\'ll know I\'m done when the act is done and I\'ve noted how I feel.',
                sw: [
                  'Do the act quietly, so I serve without attention.',
                  'Expect nothing in return, so the service is a gift.',
                  'Note how I feel afterwards, so I see what service does for me.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s done.',
                  'I\'ll know I\'m done when I\'ve let go of any reward.',
                  'I\'ll know I\'m done when one line is written.',
                ],
              },
              {
                t: "What service asked and gave written, next act planned", s: ['Write what the act asked of me', 'Write what I received from it', 'Plan the next small act'],
                w: 'Reflect on what service taught me, so serving feeds my reflective life.',
                k: 'I\'ll know I\'m done when I\'ve written what it asked, what I received and planned the next act.',
                sw: [
                  'Write what the act asked of me, so I see its cost.',
                  'Write what I received from it, so I see its gift.',
                  'Plan the next small act, so serving continues.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when the next act is written.',
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // ───────────────────────── 8. Having self-respect and boundaries I hold
  {
    e: 'Having self-respect and boundaries I hold', deity: 'aidos',
    w: 'I\'m building self-respect and boundaries I hold, so other people\'s demands stop running my life and I treat myself with dignity.',
    k: 'I\'ll know I\'m there when I keep my own word, hold my boundaries calmly and speak to myself with respect.',
    g: [
      {
        t: 'Set and hold three clear personal boundaries this month',
        w: 'Set and hold three clear personal boundaries this month, so I have self-respect and boundaries I hold.',
        k: 'I\'ll know I\'m done when three boundaries are stated and I\'ve held each one in a real situation.',
        a: [
          {
            t: 'Name one boundary I need and state it plainly', c: 'weekly',
            p: 'A boundary is a promise to yourself.',
            w: 'Name one boundary I need and state it plainly, so I set three clear boundaries this month.',
            k: 'I\'ll know I\'m done when one boundary is written in a clear sentence and practised aloud this week.',
            d: [
              {
                t: "Resentful situations listed, the three repeating ones starred", s: ['Set a 10 minute timer', 'List situations that leave me resentful', 'Star the three that repeat'],
                w: 'List where I feel overstretched or resentful, so I find which boundaries I need.',
                k: 'I\'ll know I\'m done when the situations are listed and the three that repeat are starred.',
                sw: [
                  'Set a 10 minute timer, so I write without overthinking.',
                  'List situations that leave me resentful, so I see where I\'m overstretched.',
                  'Star the three that repeat, so I see where I most need a boundary.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when three are starred.',
                ],
              },
              {
                t: "My boundary written in one clear sentence", s: ['Pick a starred situation', 'Write what I will and will not accept', 'Keep it to one clear sentence'],
                w: 'Turn one situation into a boundary, so I state a boundary plainly.',
                k: 'I\'ll know I\'m done when my boundary is written in one clear sentence.',
                sw: [
                  'Pick a starred situation, so I choose one to work on.',
                  'Write what I will and will not accept, so the boundary is clear.',
                  'Keep it to one clear sentence, so it is easy to say.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when both are written.',
                  'I\'ll know I\'m done when it fits in one sentence.',
                ],
              },
              {
                t: "My words written, practised aloud twice, time chosen", s: ['Write the words I will use', 'Say them aloud twice', 'Choose when I will say them'],
                w: 'Plan how I will say it, so I can state my boundary plainly when it matters.',
                k: 'I\'ll know I\'m done when my words are written, practised aloud twice and a time chosen.',
                sw: [
                  'Write the words I will use, so I\'m ready.',
                  'Say them aloud twice, so they come out easily.',
                  'Choose when I will say them, so I follow through.',
                ],
                sk: [
                  'I\'ll know I\'m done when the words are written.',
                  'I\'ll know I\'m done when I\'ve said them twice.',
                  'I\'ll know I\'m done when the time is chosen.',
                ],
              },
            ],
          },
          {
            t: 'Say the boundary calmly when it is tested', c: '3×/week',
            p: 'Say it once, kindly, and mean it.',
            w: 'Say the boundary calmly when it is tested, so my boundary actually holds.',
            k: 'I\'ll know I\'m done when I\'ve held my boundary calmly in three situations this week.',
            d: [
              {
                t: "My boundary sentence said calmly once without over-explaining", s: ['Notice the moment the boundary is tested', 'Say my sentence calmly', 'Do not over-explain'],
                w: 'Hold the line in a small situation, so I practise my boundary where the stakes are low.',
                k: 'I\'ll know I\'m done when I\'ve said my sentence calmly once without over-explaining.',
                sw: [
                  'Notice the moment the boundary is tested, so I catch it in time.',
                  'Say my sentence calmly, so my boundary is stated.',
                  'Do not over-explain, so the boundary stays firm.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve spotted the moment.',
                  'I\'ll know I\'m done when I\'ve said it.',
                  'I\'ll know I\'m done when I\'ve stopped after my sentence.',
                ],
              },
              {
                t: "My sentence held calmly under pushback, or the talk ended", s: ['Breathe before answering', 'Repeat the sentence without raising my voice', 'End the conversation if needed'],
                w: 'Stay steady if someone pushes back, so my boundary survives pressure.',
                k: 'I\'ll know I\'m done when I\'ve repeated my sentence calmly, or ended the conversation, after a pushback.',
                sw: [
                  'Breathe before answering, so I stay calm.',
                  'Repeat the sentence without raising my voice, so I stay firm.',
                  'End the conversation if needed, so the boundary holds.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve breathed first.',
                  'I\'ll know I\'m done when I\'ve repeated it calmly.',
                  'I\'ll know I\'m done when I\'ve ended it, or didn\'t need to.',
                ],
              },
              {
                t: "The follow-through action I promised done", s: ['Do what I said I would do', 'Notice any guilt without acting on it', 'Remind myself why the boundary matters'],
                w: 'Follow through with action, so my boundary is real and not just words.',
                k: 'I\'ll know I\'m done when I\'ve done what I said I would do.',
                sw: [
                  'Do what I said I would do, so the boundary has teeth.',
                  'Notice any guilt without acting on it, so guilt doesn\'t undo the boundary.',
                  'Remind myself why the boundary matters, so I stay firm.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s done.',
                  'I\'ll know I\'m done when I\'ve noticed it and not given in.',
                  'I\'ll know I\'m done when I\'ve said the reason to myself.',
                ],
              },
            ],
          },
          {
            t: 'Review how my boundaries held', c: 'weekly',
            p: 'An unexamined pattern will repeat until it\'s named.',
            w: 'Review how my boundaries held, so I learn which boundaries need work.',
            k: 'I\'ll know I\'m done when I\'ve reviewed my week and refined one boundary.',
            d: [
              {
                t: "Each time I held firm listed, with what helped", s: ['Open my notes from the week', 'List each time I held firm', 'Note what made it easier'],
                w: 'List the times I held my boundaries, so I see what works.',
                k: 'I\'ll know I\'m done when each time I held firm is listed with what made it easier.',
                sw: [
                  'Open my notes from the week, so I have the facts.',
                  'List each time I held firm, so I see my wins.',
                  'Note what made it easier, so I can repeat it.',
                ],
                sk: [
                  'I\'ll know I\'m done when the notes are open.',
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when one thing is written.',
                ],
              },
              {
                t: "Each time I gave in listed, with feelings and changes", s: ['List each time I gave in', 'Note what I felt in those moments', 'Note what I will do differently'],
                w: 'List the times I gave way, so I see where my boundaries need work.',
                k: 'I\'ll know I\'m done when each time I gave in is listed with what I felt and what I\'ll change.',
                sw: [
                  'List each time I gave in, so I see where it slipped.',
                  'Note what I felt in those moments, so I understand why I gave way.',
                  'Note what I will do differently, so next time is better.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when feelings are written.',
                  'I\'ll know I\'m done when a change is written.',
                ],
              },
              {
                t: "The hardest boundary reworded and practised aloud", s: ['Pick the boundary that was hardest', 'Make the wording clearer', 'Practise the new wording aloud'],
                w: 'Refine one boundary, so my hardest boundary becomes easier to hold.',
                k: 'I\'ll know I\'m done when the wording is clearer and I\'ve practised it aloud.',
                sw: [
                  'Pick the boundary that was hardest, so I improve the right one.',
                  'Make the wording clearer, so it\'s easier to say.',
                  'Practise the new wording aloud, so it comes easily.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when the new wording is written.',
                  'I\'ll know I\'m done when I\'ve said it aloud.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Keep every promise I make to myself for 14 days in a row',
        w: 'Keep every promise I make to myself for 14 days in a row, so I build self-respect by keeping my word.',
        k: 'I\'ll know I\'m done when I\'ve kept my promise to myself on 14 days in a row.',
        a: [
          {
            t: 'Make one small promise to myself each day', c: 'daily',
            p: 'Self-respect is earned by keeping your word.',
            w: 'Make one small promise to myself each day, so I earn self-respect by keeping my word.',
            k: 'I\'ll know I\'m done when I\'ve made and kept a small promise on every day this week.',
            d: [
              {
                t: "Today's small promise written in one sentence with a time", s: ['Pick something small and doable', 'Write it down in one sentence', 'Set a time for it'],
                w: 'Choose today\'s promise, so I make a promise I can keep.',
                k: 'I\'ll know I\'m done when a small promise is written in one sentence with a time.',
                sw: [
                  'Pick something small and doable, so I can keep it.',
                  'Write it down in one sentence, so it\'s clear.',
                  'Set a time for it, so it has a slot.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when the time is set.',
                ],
              },
              {
                t: "Today's promise kept and ticked off", s: ['Do it at the time I chose', 'Tick it off in my tracker', 'Notice how that feels'],
                w: 'Keep today\'s promise, so I keep my word to myself.',
                k: 'I\'ll know I\'m done when my promise is done and ticked off.',
                sw: [
                  'Do it at the time I chose, so I follow my own plan.',
                  'Tick it off in my tracker, so I see my streak grow.',
                  'Notice how that feels, so I feel the self-respect.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s done at that time.',
                  'I\'ll know I\'m done when it\'s ticked.',
                  'I\'ll know I\'m done when I\'ve noticed it.',
                ],
              },
              {
                t: "A smaller version of the missed promise done", s: ['Note what got in the way', 'Do a smaller version today', 'Do not skip two days in a row'],
                w: 'Recover from a missed promise, so one miss doesn\'t end my streak of self-respect.',
                k: 'I\'ll know I\'m done when I\'ve done a smaller version today.',
                sw: [
                  'Note what got in the way, so I learn from the miss.',
                  'Do a smaller version today, so I keep my word to myself.',
                  'Do not skip two days in a row, so the habit survives.',
                ],
                sk: [
                  'I\'ll know I\'m done when the obstacle is written.',
                  'I\'ll know I\'m done when the smaller version is done.',
                  'I\'ll know I\'m done when tomorrow\'s promise is scheduled.',
                ],
              },
            ],
          },
          {
            t: 'Track my promises on one page', c: 'daily · 1 min',
            p: 'What you count, you keep.',
            w: 'Track my promises on one page, so I see my streak and keep my word.',
            k: 'I\'ll know I\'m done when my tracker is marked on every day this week.',
            d: [
              {
                t: "A 14-box tracker labelled and placed where I see it", s: ['Draw 14 boxes on a page', 'Label the page with my promise', 'Put it where I will see it'],
                w: 'Set up the tracker, so I have a page to track my promises.',
                k: 'I\'ll know I\'m done when a 14 box page is labelled and placed where I\'ll see it.',
                sw: [
                  'Draw 14 boxes on a page, so I have a box for each day.',
                  'Label the page with my promise, so I know what I\'m tracking.',
                  'Put it where I will see it, so I don\'t forget it.',
                ],
                sk: [
                  'I\'ll know I\'m done when 14 boxes are drawn.',
                  'I\'ll know I\'m done when the label is written.',
                  'I\'ll know I\'m done when it\'s in place.',
                ],
              },
              {
                t: "Today's box marked kept or missed, with one word why", s: ['Open the tracker', 'Mark today as kept or missed', 'Write one word on why'],
                w: 'Mark today\'s box, so my tracker is up to date.',
                k: 'I\'ll know I\'m done when today\'s box is marked kept or missed with one word on why.',
                sw: [
                  'Open the tracker, so I can mark today.',
                  'Mark today as kept or missed, so my record is honest.',
                  'Write one word on why, so I learn from it.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s in front of me.',
                  'I\'ll know I\'m done when today is marked.',
                  'I\'ll know I\'m done when the word is written.',
                ],
              },
              {
                t: "Kept days counted, hardest day found and planned for", s: ['Count my kept days', 'Spot the day that was hardest', 'Plan for that day next week'],
                w: 'Review the streak, so I see how my promises are going.',
                k: 'I\'ll know I\'m done when I\'ve counted my kept days, found the hardest day and planned for it.',
                sw: [
                  'Count my kept days, so I know how I\'m doing.',
                  'Spot the day that was hardest, so I know my weak spot.',
                  'Plan for that day next week, so I keep my streak.',
                ],
                sk: [
                  'I\'ll know I\'m done when I have a number.',
                  'I\'ll know I\'m done when I\'ve named the day.',
                  'I\'ll know I\'m done when a plan is written.',
                ],
              },
            ],
          },
          {
            t: 'Stop making promises I cannot keep', c: 'weekly',
            p: 'Fewer promises, kept, build trust.',
            w: 'Stop making promises I cannot keep, so every promise I make is one I can keep.',
            k: 'I\'ll know I\'m done when I\'ve renegotiated one promise and checked before agreeing to new things.',
            d: [
              {
                t: "All promises listed, doubtful ones marked, one chosen", s: ['Write every promise I have made', 'Mark the ones I am unlikely to keep', 'Choose one to renegotiate'],
                w: 'List my open promises, so I see which ones I might break.',
                k: 'I\'ll know I\'m done when all promises are written, the doubtful ones marked and one chosen.',
                sw: [
                  'Write every promise I have made, so I see them all.',
                  'Mark the ones I am unlikely to keep, so I know which are at risk.',
                  'Choose one to renegotiate, so I fix the biggest risk.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is complete.',
                  'I\'ll know I\'m done when they are marked.',
                  'I\'ll know I\'m done when one is chosen.',
                ],
              },
              {
                t: "The person told and a new plan agreed", s: ['Write what I can actually offer', 'Tell the person directly', 'Agree on a new plan'],
                w: 'Renegotiate one promise honestly, so I stop carrying a promise I can\'t keep.',
                k: 'I\'ll know I\'m done when the person knows and we\'ve agreed a new plan.',
                sw: [
                  'Write what I can actually offer, so my new promise is realistic.',
                  'Tell the person directly, so they hear it from me.',
                  'Agree on a new plan, so the matter is settled.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when I\'ve told them.',
                  'I\'ll know I\'m done when the plan is agreed.',
                ],
              },
              {
                t: "A pause taken, calendar and energy checked, clear answer given", s: ['Pause for a day before saying yes', 'Check my calendar and energy', 'Answer yes or no clearly'],
                w: 'Check before agreeing to new things, so I stop making promises I can\'t keep.',
                k: 'I\'ll know I\'m done when I\'ve paused, checked my calendar and energy, and answered yes or no clearly.',
                sw: [
                  'Pause for a day before saying yes, so I don\'t promise on impulse.',
                  'Check my calendar and energy, so I know if I can really do it.',
                  'Answer yes or no clearly, so I only keep promises I can keep.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve waited.',
                  'I\'ll know I\'m done when I\'ve checked both.',
                  'I\'ll know I\'m done when I\'ve answered.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Replace one habit of self-criticism with self-respect',
        w: 'Replace one habit of self-criticism with self-respect, so I treat myself with the dignity I give others.',
        k: 'I\'ll know I\'m done when my harsh self-talk is rarer and I can name three things I did well each day.',
        a: [
          {
            t: 'Catch my harsh self-talk', c: 'daily',
            p: 'Speak to yourself as you would to a friend.',
            w: 'Catch my harsh self-talk, so I can replace self-criticism with self-respect.',
            k: 'I\'ll know I\'m done when I\'ve caught and noted my harsh thoughts on every day this week.',
            d: [
              {
                t: "Three inner-voice checks done, harsh words noted", s: ['Set three reminders through the day', 'At each, check my inner voice', 'Note any harsh words I used'],
                w: 'Notice the harsh thoughts, so I see how I talk to myself.',
                k: 'I\'ll know I\'m done when I\'ve checked my inner voice three times and noted harsh words.',
                sw: [
                  'Set three reminders through the day, so I check in regularly.',
                  'At each, check my inner voice, so I catch harsh thoughts.',
                  'Note any harsh words I used, so I see them clearly.',
                ],
                sk: [
                  'I\'ll know I\'m done when three reminders are set.',
                  'I\'ll know I\'m done when I\'ve checked at each one.',
                  'I\'ll know I\'m done when they\'re written.',
                ],
              },
              {
                t: "The friend version of one harsh thought said aloud", s: ['Pick the harshest thought from today', 'Write what I would say to a friend', 'Say that version aloud'],
                w: 'Rewrite one harsh thought, so I practise speaking to myself kindly.',
                k: 'I\'ll know I\'m done when I\'ve said the friend version aloud.',
                sw: [
                  'Pick the harshest thought from today, so I work on the worst one.',
                  'Write what I would say to a friend, so I see the kinder version.',
                  'Say that version aloud, so I hear it.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when I\'ve said it.',
                ],
              },
              {
                t: "The pattern behind my harsh thoughts named in one line", s: ['Review today\'s harsh thoughts', 'Note what came right before each', 'Name the pattern in one line'],
                w: 'Notice what sets it off, so I can catch harsh self-talk earlier.',
                k: 'I\'ll know I\'m done when I\'ve named the pattern in one line.',
                sw: [
                  'Review today\'s harsh thoughts, so I see them together.',
                  'Note what came right before each, so I find the trigger.',
                  'Name the pattern in one line, so I can recognise it again.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read them.',
                  'I\'ll know I\'m done when triggers are written.',
                  'I\'ll know I\'m done when the line is written.',
                ],
              },
            ],
          },
          {
            t: 'Write down three things I did well', c: 'daily · 3 min',
            p: 'Respect grows on evidence.',
            w: 'Write down three things I did well, so my self-respect rests on evidence.',
            k: 'I\'ll know I\'m done when I\'ve written three things I did well on every day this week.',
            d: [
              {
                t: "Three things I did well written, one of them small", s: ['Sit down at the end of the day', 'Write three things I did well', 'Include one small thing'],
                w: 'Record today\'s wins, so I see what I did well today.',
                k: 'I\'ll know I\'m done when three things are written, including one small one.',
                sw: [
                  'Sit down at the end of the day, so I give it a moment.',
                  'Write three things I did well, so my wins are on paper.',
                  'Include one small thing, so I count small wins too.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'m seated with my notes.',
                  'I\'ll know I\'m done when three are written.',
                  'I\'ll know I\'m done when one small item is on the list.',
                ],
              },
              {
                t: "How I got through a hard moment written", s: ['Think of a hard moment today', 'Write how I got through it', 'Write what that says about me'],
                w: 'Record what I handled that was hard, so I see my strength.',
                k: 'I\'ll know I\'m done when I\'ve written how I got through a hard moment and what it says about me.',
                sw: [
                  'Think of a hard moment today, so I find a real example.',
                  'Write how I got through it, so I see how I coped.',
                  'Write what that says about me, so I build respect for myself.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve picked one.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when one line is written.',
                ],
              },
              {
                t: "The week's win lists read and proudest win picked", s: ['Open this week\'s lists', 'Read them all slowly', 'Pick the one I am proudest of'],
                w: 'Reread my wins from the week, so I see the evidence for self-respect.',
                k: 'I\'ll know I\'m done when I\'ve read the week\'s lists and picked the win I\'m proudest of.',
                sw: [
                  'Open this week\'s lists, so I have them in front of me.',
                  'Read them all slowly, so I take them in.',
                  'Pick the one I am proudest of, so I mark my best win.',
                ],
                sk: [
                  'I\'ll know I\'m done when they\'re open.',
                  'I\'ll know I\'m done when I\'ve read them all.',
                  'I\'ll know I\'m done when one is picked.',
                ],
              },
            ],
          },
          {
            t: 'Do one thing that shows I value myself', c: '3×/week',
            p: 'Self-respect shows up in small acts.',
            w: 'Do one thing that shows I value myself, so my self-respect shows in what I do.',
            k: 'I\'ll know I\'m done when I\'ve done something for myself on three days this week.',
            d: [
              {
                t: "One act of self-value picked and scheduled today", s: ['List things I do for others but not myself', 'Pick one to do for me', 'Schedule it today'],
                w: 'Choose the act, so I know what shows I value myself.',
                k: 'I\'ll know I\'m done when I\'ve listed my options, picked one and scheduled it today.',
                sw: [
                  'List things I do for others but not myself, so I find where I neglect myself.',
                  'Pick one to do for me, so I choose one act.',
                  'Schedule it today, so it happens.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when it\'s scheduled.',
                ],
              },
              {
                t: "The self-valuing act done on time, without apology", s: ['Do it at the time I set', 'Skip any excuse or apology', 'Note how I feel afterwards'],
                w: 'Do the act without apology, so I show I value myself.',
                k: 'I\'ll know I\'m done when the act is done at the time I set and noted how I feel.',
                sw: [
                  'Do it at the time I set, so I follow through.',
                  'Skip any excuse or apology, so I treat it as my right.',
                  'Note how I feel afterwards, so I see what it gives me.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s done.',
                  'I\'ll know I\'m done when I\'ve made no excuse.',
                  'I\'ll know I\'m done when one line is written.',
                ],
              },
              {
                t: "Next week's act chosen, in my calendar, a friend told", s: ['Choose the next act', 'Add it to my calendar', 'Tell a friend what I am doing'],
                w: 'Keep it going next week, so valuing myself becomes a habit.',
                k: 'I\'ll know I\'m done when next week\'s act is chosen, in my calendar and a friend knows.',
                sw: [
                  'Choose the next act, so I have a next one.',
                  'Add it to my calendar, so it happens.',
                  'Tell a friend what I am doing, so I stay accountable.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s chosen.',
                  'I\'ll know I\'m done when it\'s in my calendar.',
                  'I\'ll know I\'m done when I\'ve told them.',
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // ───────────────────────── 9. TELOS personal-v1
  {
    e: 'Having a mind that operates with clarity and intention in every situation.', deity: 'frosy',
    w: 'I\'m building a mind that operates with clarity and intention in every situation, so I act on purpose and not by reflex.',
    k: 'I\'ll know I\'m there when I plan my days, protect my focus and decide deliberately, and my results show it.',
    g: [
      {
        t: 'Plan every day in writing for 20 days this month',
        w: 'Plan every day in writing for 20 days this month, so my mind works with clarity and intention.',
        k: 'I\'ll know I\'m done when I\'ve written a plan for at least 20 days this month.',
        a: [
          {
            t: 'Write my top three priorities each morning', c: 'daily · 5 min',
            p: 'A day without a plan belongs to someone else.',
            w: 'Write my top three priorities each morning, so I plan each day in writing.',
            k: 'I\'ll know I\'m done when I\'ve written my top three on every morning this week.',
            d: [
              {
                t: "Today's top three priorities written", s: ['Open a notebook or notes app', 'List everything on my mind', 'Pick the three that matter most'],
                w: 'Write today\'s top three, so I start the day knowing what matters most.',
                k: 'I\'ll know I\'m done when three priorities for today are written.',
                sw: [
                  'Open a notebook or notes app, so I have a place to plan.',
                  'List everything on my mind, so I see all my options.',
                  'Pick the three that matter most, so my day has a clear focus.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s open.',
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when three are picked.',
                ],
              },
              {
                t: "Each priority given a calendar slot, hardest first", s: ['Open today\'s calendar', 'Block a slot for each priority', 'Put the hardest one first'],
                w: 'Set a time for each priority, so my top three get real slots in the day.',
                k: 'I\'ll know I\'m done when each priority has a calendar slot, with the hardest first.',
                sw: [
                  'Open today\'s calendar, so I can see the day.',
                  'Block a slot for each priority, so each has a time.',
                  'Put the hardest one first, so I tackle it while fresh.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s open.',
                  'I\'ll know I\'m done when three slots are blocked.',
                  'I\'ll know I\'m done when the hardest one has the earliest slot.',
                ],
              },
              {
                t: "Day checked against plan, unfinished items moved, slips explained", s: ['Check which priorities got done', 'Move unfinished ones to tomorrow', 'Note why anything slipped'],
                w: 'Close the day against the plan, so I learn how well my plan worked.',
                k: 'I\'ll know I\'m done when I\'ve checked what got done, moved the rest and noted why anything slipped.',
                sw: [
                  'Check which priorities got done, so I see my results.',
                  'Move unfinished ones to tomorrow, so nothing is lost.',
                  'Note why anything slipped, so I plan better tomorrow.',
                ],
                sk: [
                  'I\'ll know I\'m done when each is checked.',
                  'I\'ll know I\'m done when they\'re moved.',
                  'I\'ll know I\'m done when the reasons are written.',
                ],
              },
            ],
          },
          {
            t: 'Do one deep work block with no distractions', c: 'daily · 45 min',
            p: 'Attention is the raw material of every result.',
            w: 'Do one deep work block with no distractions, so my written priorities actually get done.',
            k: 'I\'ll know I\'m done when I\'ve done a 45 minute focus block on every working day this week.',
            d: [
              {
                t: "45 minutes of undisturbed work on my top priority done", s: ['Close every tab and app I do not need', 'Put my phone in another room', 'Work on the top priority until the timer ends'],
                w: 'Do a 45 minute focus block, so my top priority gets my full attention.',
                k: 'I\'ll know I\'m done when I\'ve worked on my top priority for 45 minutes undisturbed.',
                sw: [
                  'Close every tab and app I do not need, so nothing pulls my attention.',
                  'Put my phone in another room, so it can\'t distract me.',
                  'Work on the top priority until the timer ends, so I complete the full block.',
                ],
                sk: [
                  'I\'ll know I\'m done when only what I need is open.',
                  'I\'ll know I\'m done when it\'s out of the room.',
                  'I\'ll know I\'m done when the timer ends.',
                ],
              },
              {
                t: "Desk cleared, single task on a note, timer set", s: ['Clear my desk', 'Write the single task on a note', 'Set a 45 minute timer'],
                w: 'Set up the focus space, so my focus block starts without friction.',
                k: 'I\'ll know I\'m done when my desk is clear, my task is on a note and the timer is set.',
                sw: [
                  'Clear my desk, so my space supports focus.',
                  'Write the single task on a note, so I know exactly what to do.',
                  'Set a 45 minute timer, so I have a clear block.',
                ],
                sk: [
                  'I\'ll know I\'m done when the desk is clear.',
                  'I\'ll know I\'m done when the note is written.',
                  'I\'ll know I\'m done when the timer is set.',
                ],
              },
              {
                t: "Focus block reviewed, one distraction removed for tomorrow", s: ['Note what I finished', 'Note what pulled my attention', 'Remove one distraction for tomorrow'],
                w: 'Review the focus block, so my next block is better.',
                k: 'I\'ll know I\'m done when I\'ve noted what I finished, what distracted me and removed one distraction.',
                sw: [
                  'Note what I finished, so I see my progress.',
                  'Note what pulled my attention, so I see my distractions.',
                  'Remove one distraction for tomorrow, so tomorrow\'s block is better.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when they\'re written.',
                  'I\'ll know I\'m done when one is removed.',
                ],
              },
            ],
          },
          {
            t: 'Review my week and set next week\'s focus', c: 'weekly',
            p: 'What you do not review, you repeat.',
            w: 'Review my week and set next week\'s focus, so my daily plans add up to progress.',
            k: 'I\'ll know I\'m done when I\'ve reviewed the week and placed next week\'s three priorities in my calendar.',
            d: [
              {
                t: "Finished and undone items from the week both listed", s: ['Open my daily plans from this week', 'List what I finished', 'List what I left undone'],
                w: 'Look back at the week, so I see what my daily plans produced.',
                k: 'I\'ll know I\'m done when what I finished and what I left undone are both listed.',
                sw: [
                  'Open my daily plans from this week, so I can review them.',
                  'List what I finished, so I see my results.',
                  'List what I left undone, so I see what slipped.',
                ],
                sk: [
                  'I\'ll know I\'m done when they\'re open.',
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when the list is written.',
                ],
              },
              {
                t: "Cause of my biggest undone item and one fix written", s: ['Pick the biggest thing left undone', 'Write what got in the way', 'Write one fix'],
                w: 'Find what blocked me, so I know what to fix next week.',
                k: 'I\'ll know I\'m done when I\'ve named the cause of my biggest undone item and written one fix.',
                sw: [
                  'Pick the biggest thing left undone, so I focus on what mattered.',
                  'Write what got in the way, so I find the cause.',
                  'Write one fix, so next week is better.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when the fix is written.',
                ],
              },
              {
                t: "Next week's three priorities in my calendar and saved", s: ['Pick three outcomes for next week', 'Place each in my calendar', 'Print or save the plan'],
                w: 'Set next week\'s three priorities, so next week starts with focus.',
                k: 'I\'ll know I\'m done when three outcomes are in my calendar and the plan is saved.',
                sw: [
                  'Pick three outcomes for next week, so I know what matters.',
                  'Place each in my calendar, so each has a time.',
                  'Print or save the plan, so I can find it on Monday.',
                ],
                sk: [
                  'I\'ll know I\'m done when three are picked.',
                  'I\'ll know I\'m done when all three are placed.',
                  'I\'ll know I\'m done when it\'s saved.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Cut my reaction time to distractions: phone-free 2 hours a day',
        w: 'Cut my reaction time to distractions: phone-free 2 hours a day, so my mind runs on intention, not on pings.',
        k: 'I\'ll know I\'m done when I\'ve kept a 2 hour phone-free block on most days this month.',
        a: [
          {
            t: 'Keep my phone out of reach for a 2 hour block', c: 'daily · 2 hr',
            p: 'What is always available is always interrupting.',
            w: 'Keep my phone out of reach for a 2 hour block, so I get phone-free hours every day.',
            k: 'I\'ll know I\'m done when I\'ve kept a 2 hour phone-free block on most days this week.',
            d: [
              {
                t: "Two hours done with my phone in another room", s: ['Choose the 2 hour window', 'Put my phone in another room', 'Set a timer to end the block'],
                w: 'Start the phone-free block, so I get 2 phone-free hours today.',
                k: 'I\'ll know I\'m done when my phone has been in another room for 2 hours.',
                sw: [
                  'Choose the 2 hour window, so the block has a clear time.',
                  'Put my phone in another room, so it\'s out of reach.',
                  'Set a timer to end the block, so I know when it\'s over.',
                ],
                sk: [
                  'I\'ll know I\'m done when the window is chosen.',
                  'I\'ll know I\'m done when it\'s in another room.',
                  'I\'ll know I\'m done when the timer is set.',
                ],
              },
              {
                t: "Every urge to check noted and not acted on", s: ['Notice the urge without acting', 'Take one slow breath', 'Return to what I was doing'],
                w: 'Handle the urge to check, so I get through the block without giving in.',
                k: 'I\'ll know I\'m done when I\'ve noticed each urge and not reached for my phone.',
                sw: [
                  'Notice the urge without acting, so I stay in control.',
                  'Take one slow breath, so the urge passes.',
                  'Return to what I was doing, so I keep my focus.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve noticed it.',
                  'I\'ll know I\'m done when I\'ve taken the breath.',
                  'I\'ll know I\'m done when I\'m back at work.',
                ],
              },
              {
                t: "Block results and urges noted, tomorrow's block planned", s: ['Note what I got done', 'Note when the urge was strongest', 'Plan tomorrow\'s block'],
                w: 'Review the block, so tomorrow\'s phone-free block goes better.',
                k: 'I\'ll know I\'m done when I\'ve noted my results and urges and planned tomorrow\'s block.',
                sw: [
                  'Note what I got done, so I see the benefit.',
                  'Note when the urge was strongest, so I know my weak spot.',
                  'Plan tomorrow\'s block, so the habit continues.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when tomorrow\'s window is set.',
                ],
              },
            ],
          },
          {
            t: 'Turn off non-essential notifications', c: 'weekly',
            p: 'Every ping is a request for your attention.',
            w: 'Turn off non-essential notifications, so my phone stops pulling at my attention.',
            k: 'I\'ll know I\'m done when only essential apps can notify me.',
            d: [
              {
                t: "Heaviest notifiers listed, non-essential ones marked", s: ['Open my phone notification settings', 'List the apps that notify most', 'Mark those that are not essential'],
                w: 'Audit my notifications, so I see what pings me.',
                k: 'I\'ll know I\'m done when the heaviest notifiers are listed and the non-essential ones marked.',
                sw: [
                  'Open my phone notification settings, so I can see them.',
                  'List the apps that notify most, so I see the biggest interrupters.',
                  'Mark those that are not essential, so I know what to switch off.',
                ],
                sk: [
                  'I\'ll know I\'m done when the settings are open.',
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when they\'re marked.',
                ],
              },
              {
                t: "Marked apps silenced, key people let through, do-not-disturb on", s: ['Turn off notifications for marked apps', 'Keep calls and messages from key people', 'Turn on do-not-disturb for focus hours'],
                w: 'Switch them off, so non-essential pings stop.',
                k: 'I\'ll know I\'m done when marked apps are off, key people get through and do-not-disturb is on.',
                sw: [
                  'Turn off notifications for marked apps, so they stop interrupting.',
                  'Keep calls and messages from key people, so I stay reachable for what matters.',
                  'Turn on do-not-disturb for focus hours, so my focus is protected.',
                ],
                sk: [
                  'I\'ll know I\'m done when they\'re off.',
                  'I\'ll know I\'m done when key people are allowed.',
                  'I\'ll know I\'m done when it\'s scheduled.',
                ],
              },
              {
                t: "Phone checks and misses noted, one more app switched off", s: ['Note how often I reached for my phone', 'Note what I missed, if anything', 'Switch off one more app'],
                w: 'Check how it feels after a day, so I see if the change works.',
                k: 'I\'ll know I\'m done when I\'ve noted my phone checks and any misses and switched off one more app.',
                sw: [
                  'Note how often I reached for my phone, so I see the change.',
                  'Note what I missed, if anything, so I know if the change cost me.',
                  'Switch off one more app, so my phone gets quieter.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when one more is off.',
                ],
              },
            ],
          },
          {
            t: 'Check messages at set times only', c: 'daily · 3×',
            p: 'Answer when you choose, not when summoned.',
            w: 'Check messages at set times only, so I answer when I choose and not when pinged.',
            k: 'I\'ll know I\'m done when I\'ve checked messages only at my three set times on every day this week.',
            d: [
              {
                t: "Three check times in my calendar, close contacts told", s: ['Pick three times that suit my day', 'Add them to my calendar', 'Tell close contacts how to reach me urgently'],
                w: 'Set my three message-check times, so I know when I will check messages.',
                k: 'I\'ll know I\'m done when three times are in my calendar and close contacts know how to reach me urgently.',
                sw: [
                  'Pick three times that suit my day, so the schedule works for me.',
                  'Add them to my calendar, so I remember them.',
                  'Tell close contacts how to reach me urgently, so they aren\'t worried.',
                ],
                sk: [
                  'I\'ll know I\'m done when three are picked.',
                  'I\'ll know I\'m done when they\'re added.',
                  'I\'ll know I\'m done when I\'ve told them.',
                ],
              },
              {
                t: "Messages opened only at my set times today", s: ['Open messages only at those times', 'Reply to what needs a reply', 'Close the app again'],
                w: 'Check messages at the set times, so I answer when I choose.',
                k: 'I\'ll know I\'m done when I\'ve opened messages only at my set times today.',
                sw: [
                  'Open messages only at those times, so I stay undisturbed between them.',
                  'Reply to what needs a reply, so I stay responsive.',
                  'Close the app again, so I return to my work.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve kept to the times.',
                  'I\'ll know I\'m done when I\'ve replied.',
                  'I\'ll know I\'m done when it\'s closed.',
                ],
              },
              {
                t: "Inbox tidied, three items flagged for tomorrow, inbox closed", s: ['Delete or archive what I have read', 'Flag three items for tomorrow', 'Close my inbox for the night'],
                w: 'Tidy my inbox at the last check, so tomorrow begins clean.',
                k: 'I\'ll know I\'m done when I\'ve archived what I\'ve read, flagged three items and closed my inbox.',
                sw: [
                  'Delete or archive what I have read, so my inbox is clean.',
                  'Flag three items for tomorrow, so I know where to start.',
                  'Close my inbox for the night, so work doesn\'t follow me.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s cleared.',
                  'I\'ll know I\'m done when three are flagged.',
                  'I\'ll know I\'m done when it\'s closed.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Build a decision habit: write down and review 8 decisions',
        w: 'Build a decision habit: write down and review 8 decisions, so my mind decides on purpose.',
        k: 'I\'ll know I\'m done when I\'ve written down and reviewed eight decisions this month.',
        a: [
          {
            t: 'Write my reasoning before an important decision', c: '2×/week',
            p: 'Decide on purpose, not by default.',
            w: 'Write my reasoning before an important decision, so I decide on purpose and not by default.',
            k: 'I\'ll know I\'m done when I\'ve written my reasoning for two important decisions this week.',
            d: [
              {
                t: "Decision, options and best and worst outcomes written", s: ['State the decision in one sentence', 'List at least two options', 'List the best and worst outcome of each'],
                w: 'Write the decision and my options, so I see my choice clearly.',
                k: 'I\'ll know I\'m done when the decision, two or more options and their best and worst outcomes are written.',
                sw: [
                  'State the decision in one sentence, so I know what I\'m deciding.',
                  'List at least two options, so I have real choices.',
                  'List the best and worst outcome of each, so I see the stakes.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when two are listed.',
                  'I\'ll know I\'m done when each has both.',
                ],
              },
              {
                t: "An option picked, with reason written and review date set", s: ['Pick the option that fits my values', 'Write why I chose it', 'Write the date I will review it'],
                w: 'Choose and commit, so my decision is made on purpose.',
                k: 'I\'ll know I\'m done when I\'ve picked an option, written why and set a review date.',
                sw: [
                  'Pick the option that fits my values, so my choice is deliberate.',
                  'Write why I chose it, so I can review it later.',
                  'Write the date I will review it, so I learn from it.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when the reason is written.',
                  'I\'ll know I\'m done when the date is written.',
                ],
              },
              {
                t: "First step of the decision done and one person told", s: ['Write the first step of the decision', 'Do it within the hour', 'Tell one person about my choice'],
                w: 'Take the first action, so my decision becomes real.',
                k: 'I\'ll know I\'m done when the first step is done and one person knows.',
                sw: [
                  'Write the first step of the decision, so I know where to begin.',
                  'Do it within the hour, so I move while I\'m committed.',
                  'Tell one person about my choice, so I stay accountable.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s done.',
                  'I\'ll know I\'m done when I\'ve told them.',
                ],
              },
            ],
          },
          {
            t: 'Review past decisions after a set time', c: 'weekly',
            p: 'Judgement is built by looking back.',
            w: 'Review past decisions after a set time, so my decision habit teaches me.',
            k: 'I\'ll know I\'m done when I\'ve reviewed one past decision and written a rule from it.',
            d: [
              {
                t: "A past decision picked and my reasoning reread", s: ['Open my decision notes', 'Pick one made a week or more ago', 'Reread my reasoning'],
                w: 'Pick a past decision to review, so I know which decision I\'m learning from.',
                k: 'I\'ll know I\'m done when a decision from a week or more ago is picked and my reasoning is reread.',
                sw: [
                  'Open my decision notes, so I can find my past decisions.',
                  'Pick one made a week or more ago, so enough time has passed to judge it.',
                  'Reread my reasoning, so I recall why I decided.',
                ],
                sk: [
                  'I\'ll know I\'m done when they\'re open.',
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when I\'ve read it.',
                ],
              },
              {
                t: "What happened compared with what I expected, misses noted", s: ['Write what actually happened', 'Mark where I was right or wrong', 'Note what I missed'],
                w: 'Compare what happened with what I expected, so I see how well I judged.',
                k: 'I\'ll know I\'m done when I\'ve written what happened, marked where I was right or wrong and noted what I missed.',
                sw: [
                  'Write what actually happened, so I have the facts.',
                  'Mark where I was right or wrong, so I see my accuracy.',
                  'Note what I missed, so I learn my blind spots.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s marked.',
                  'I\'ll know I\'m done when it\'s written.',
                ],
              },
              {
                t: "One rule of thumb added to my list of rules", s: ['Draw one lesson from the decision', 'Write it as a rule of thumb', 'Add it to a list of my rules'],
                w: 'Write a rule from the lesson, so my judgement improves.',
                k: 'I\'ll know I\'m done when one rule of thumb is added to my list of rules.',
                sw: [
                  'Draw one lesson from the decision, so I learn something.',
                  'Write it as a rule of thumb, so I can reuse it.',
                  'Add it to a list of my rules, so I keep it.',
                ],
                sk: [
                  'I\'ll know I\'m done when the lesson is written.',
                  'I\'ll know I\'m done when the rule is written.',
                  'I\'ll know I\'m done when it\'s on the list.',
                ],
              },
            ],
          },
          {
            t: 'Pause before saying yes to new demands', c: 'daily',
            p: 'A pause is the cheapest protection against regret.',
            w: 'Pause before saying yes to new demands, so I decide on purpose and not on impulse.',
            k: 'I\'ll know I\'m done when I\'ve paused before every yes this week.',
            d: [
              {
                t: "Small request answered after an hour's wait and priority check", s: ['Say I will get back to them', 'Wait at least an hour', 'Answer after checking my priorities'],
                w: 'Use the pause in a small request, so I practise pausing on something easy.',
                k: 'I\'ll know I\'m done when I\'ve waited an hour and answered after checking my priorities.',
                sw: [
                  'Say I will get back to them, so I buy time.',
                  'Wait at least an hour, so I don\'t answer on impulse.',
                  'Answer after checking my priorities, so my answer fits my plans.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve said it.',
                  'I\'ll know I\'m done when the hour has passed.',
                  'I\'ll know I\'m done when I\'ve answered.',
                ],
              },
              {
                t: "A bigger commitment weighed for a day, clear answer given", s: ['Ask for a day to decide', 'Write pros and cons', 'Answer with a clear yes or no'],
                w: 'Use the pause on a bigger commitment, so I decide big commitments on purpose.',
                k: 'I\'ll know I\'m done when I\'ve taken a day, weighed pros and cons and given a clear yes or no.',
                sw: [
                  'Ask for a day to decide, so I have time to think.',
                  'Write pros and cons, so I see the trade-offs.',
                  'Answer with a clear yes or no, so the matter is settled.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve asked.',
                  'I\'ll know I\'m done when both are written.',
                  'I\'ll know I\'m done when I\'ve answered.',
                ],
              },
              {
                t: "Today's agreements checked against my top three, one undo planned", s: ['List what I agreed to today', 'Check each against my top three', 'Plan to undo one that does not fit'],
                w: 'Review today\'s yes and no answers, so I see if my pauses are working.',
                k: 'I\'ll know I\'m done when I\'ve checked each agreement against my top three and planned to undo one that doesn\'t fit.',
                sw: [
                  'List what I agreed to today, so I see my commitments.',
                  'Check each against my top three, so I see if it fits.',
                  'Plan to undo one that does not fit, so I fix my mistakes.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when each is checked.',
                  'I\'ll know I\'m done when a plan is written.',
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // ───────────────────────── 10. TELOS personal-v2
  {
    e: 'Having a character that requires no explanation.', deity: 'aidos',
    w: 'I\'m building a character that requires no explanation, so people can trust me and I can trust myself without having to justify anything.',
    k: 'I\'ll know I\'m there when my actions match my words and others rely on me without checking.',
    g: [
      {
        t: 'Choose three virtues and practise each one on purpose',
        w: 'Choose three virtues and practise each one on purpose, so I build a character that requires no explanation.',
        k: 'I\'ll know I\'m done when I\'ve named three virtues and practised each one deliberately many times this month.',
        a: [
          {
            t: 'Do one deliberate act of my chosen virtue', c: 'daily',
            p: 'Identity is built by what you repeatedly do.',
            w: 'Do one deliberate act of my chosen virtue, so I practise my three virtues on purpose.',
            k: 'I\'ll know I\'m done when I\'ve done a deliberate act of virtue on every day this week.',
            d: [
              {
                t: "My three virtues circled, each defined in one line", s: ['List ten qualities of the person I respect', 'Circle the three I want most', 'Write one line defining each'],
                w: 'Choose my three virtues, so I know which virtues I\'m practising.',
                k: 'I\'ll know I\'m done when three virtues are circled, each with a one-line definition.',
                sw: [
                  'List ten qualities of the person I respect, so I see what I admire.',
                  'Circle the three I want most, so I commit to three.',
                  'Write one line defining each, so each virtue is clear.',
                ],
                sk: [
                  'I\'ll know I\'m done when ten qualities are listed.',
                  'I\'ll know I\'m done when three are circled.',
                  'I\'ll know I\'m done when three definitions are written.',
                ],
              },
              {
                t: "At least one act of today's virtue done", s: ['Pick the virtue for today', 'Find one moment to act on it', 'Do it, however small'],
                w: 'Practise one virtue today, so I act on a virtue today.',
                k: 'I\'ll know I\'m done when I\'ve acted on today\'s virtue at least once.',
                sw: [
                  'Pick the virtue for today, so I know my focus.',
                  'Find one moment to act on it, so I have a real chance.',
                  'Do it, however small, so I practise it.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when I\'ve named the moment.',
                  'I\'ll know I\'m done when it\'s done.',
                ],
              },
              {
                t: "What I did, what it cost and gave others written", s: ['Write what I did', 'Write what it cost me', 'Write what it gave others'],
                w: 'Note the result of the act, so I learn what practising the virtue does.',
                k: 'I\'ll know I\'m done when I\'ve written what I did, what it cost me and what it gave others.',
                sw: [
                  'Write what I did, so I have a record.',
                  'Write what it cost me, so I know the price.',
                  'Write what it gave others, so I see the effect.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                ],
              },
            ],
          },
          {
            t: 'Keep a virtue log each evening', c: 'daily · 3 min',
            p: 'What you measure, you improve.',
            w: 'Keep a virtue log each evening, so I can see how well I\'m practising each virtue.',
            k: 'I\'ll know I\'m done when I\'ve scored each virtue on every evening this week.',
            d: [
              {
                t: "Each virtue scored 1 to 5 with an example", s: ['Open my log', 'Score each virtue from 1 to 5', 'Write one example behind each score'],
                w: 'Rate myself on each virtue, so I see how I did today.',
                k: 'I\'ll know I\'m done when each virtue has a score from 1 to 5 and an example.',
                sw: [
                  'Open my log, so I can record today.',
                  'Score each virtue from 1 to 5, so I measure my day.',
                  'Write one example behind each score, so my scores are honest.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s open.',
                  'I\'ll know I\'m done when three scores are written.',
                  'I\'ll know I\'m done when three examples are written.',
                ],
              },
              {
                t: "My weakest moment written, with what I should have done", s: ['Recall the day\'s weakest moment', 'Write what I did', 'Write what I should have done'],
                w: 'Note where I fell short, so I learn from my weakest moment.',
                k: 'I\'ll know I\'m done when I\'ve written what I did and what I should have done.',
                sw: [
                  'Recall the day\'s weakest moment, so I find a real example.',
                  'Write what I did, so I face it honestly.',
                  'Write what I should have done, so I know the better response.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve picked one.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                ],
              },
              {
                t: "Lowest virtue, its test and my planned action written", s: ['Choose the virtue that scored lowest', 'Name one place it will be tested', 'Write what I will do there'],
                w: 'Plan tomorrow\'s practice, so tomorrow I practise my weakest virtue.',
                k: 'I\'ll know I\'m done when the lowest virtue, its test and my planned action are written.',
                sw: [
                  'Choose the virtue that scored lowest, so I work on my weakest.',
                  'Name one place it will be tested, so I\'m ready.',
                  'Write what I will do there, so I have a plan.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when the place is named.',
                  'I\'ll know I\'m done when it\'s written.',
                ],
              },
            ],
          },
          {
            t: 'Study one person who shows these virtues', c: 'weekly',
            p: 'Character is caught as well as taught.',
            w: 'Study one person who shows these virtues, so I learn how my virtues look in action.',
            k: 'I\'ll know I\'m done when I\'ve studied one role model and used one behaviour of theirs this week.',
            d: [
              {
                t: "A role model named, story found, 30 minutes set aside", s: ['Name someone who lives these virtues', 'Find a biography, talk or story about them', 'Set 30 minutes aside'],
                w: 'Choose a role model, so I know who I\'m learning from.',
                k: 'I\'ll know I\'m done when I\'ve named a person, found a story about them and set aside 30 minutes.',
                sw: [
                  'Name someone who lives these virtues, so I pick a good teacher.',
                  'Find a biography, talk or story about them, so I have material.',
                  'Set 30 minutes aside, so the study happens.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve named them.',
                  'I\'ll know I\'m done when I\'ve found one.',
                  'I\'ll know I\'m done when it\'s in my calendar.',
                ],
              },
              {
                t: "What they did under pressure and what it asks written", s: ['Read or watch about a hard moment of theirs', 'Write what they did', 'Write what that asks of me'],
                w: 'Study how they act under pressure, so I see virtue when it\'s hard.',
                k: 'I\'ll know I\'m done when I\'ve written what they did and what it asks of me.',
                sw: [
                  'Read or watch about a hard moment of theirs, so I see them tested.',
                  'Write what they did, so I record it.',
                  'Write what that asks of me, so I connect it to my life.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve finished it.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                ],
              },
              {
                t: "One behaviour chosen, scheduled and tried once", s: ['Pick one behaviour to copy', 'Write when I will use it', 'Use it once this week'],
                w: 'Borrow one behaviour, so I learn from my role model by acting like them.',
                k: 'I\'ll know I\'m done when one behaviour is chosen, scheduled and tried once.',
                sw: [
                  'Pick one behaviour to copy, so I focus on one.',
                  'Write when I will use it, so I know when to act.',
                  'Use it once this week, so I practise it.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when I\'ve used it.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Keep every commitment I make for 30 days',
        w: 'Keep every commitment I make for 30 days, so my word is my character in public.',
        k: 'I\'ll know I\'m done when I\'ve kept every commitment I made for 30 days, or renegotiated it early.',
        a: [
          {
            t: 'Write down every commitment I make', c: 'daily',
            p: 'Your word is your character in public.',
            w: 'Write down every commitment I make, so I know what I\'ve promised and keep every one.',
            k: 'I\'ll know I\'m done when every commitment this week is on my list with a date.',
            d: [
              {
                t: "Commitments list created with everything promised and due dates", s: ['Open a note called Commitments', 'Add everything I have promised', 'Add due dates to each'],
                w: 'Set up one commitments list, so I have one place for all my promises.',
                k: 'I\'ll know I\'m done when the note exists with everything I\'ve promised and a due date for each.',
                sw: [
                  'Open a note called Commitments, so my promises have a home.',
                  'Add everything I have promised, so the list is complete.',
                  'Add due dates to each, so I know when each is due.',
                ],
                sk: [
                  'I\'ll know I\'m done when the note exists.',
                  'I\'ll know I\'m done when the list is complete.',
                  'I\'ll know I\'m done when each has a date.',
                ],
              },
              {
                t: "Every promise made today on the list with a reminder", s: ['Add any promise I make today', 'Add who it is to and when', 'Set a reminder for it'],
                w: 'Add new promises as they happen, so no promise slips by unrecorded.',
                k: 'I\'ll know I\'m done when every promise I made today is on the list with a reminder.',
                sw: [
                  'Add any promise I make today, so it\'s recorded.',
                  'Add who it is to and when, so I know the details.',
                  'Set a reminder for it, so I don\'t forget.',
                ],
                sk: [
                  'I\'ll know I\'m done when each is added.',
                  'I\'ll know I\'m done when both are added.',
                  'I\'ll know I\'m done when the reminder is set.',
                ],
              },
              {
                t: "Kept items ticked, unmet ones messaged, tomorrow's items planned", s: ['Tick off what I kept', 'Message anyone I cannot satisfy in time', 'Plan tomorrow\'s items'],
                w: 'Check the list tonight, so I know where I stand on my promises.',
                k: 'I\'ll know I\'m done when I\'ve ticked off what I kept, messaged anyone I can\'t satisfy and planned tomorrow\'s items.',
                sw: [
                  'Tick off what I kept, so I see my record.',
                  'Message anyone I cannot satisfy in time, so I renegotiate early.',
                  'Plan tomorrow\'s items, so I\'m ready.',
                ],
                sk: [
                  'I\'ll know I\'m done when kept items are ticked.',
                  'I\'ll know I\'m done when each message is sent.',
                  'I\'ll know I\'m done when tomorrow\'s items are listed.',
                ],
              },
            ],
          },
          {
            t: 'Do the commitment on time, or renegotiate early', c: 'daily',
            p: 'Better a late honest word than a silent miss.',
            w: 'Do the commitment on time, or renegotiate early, so I keep my word or fix it early.',
            k: 'I\'ll know I\'m done when every commitment this week was done on time or renegotiated early.',
            d: [
              {
                t: "Today's earliest commitment done and ticked off", s: ['Pick today\'s earliest commitment', 'Do it before anything optional', 'Tick it off'],
                w: 'Do the first commitment of the day, so my day starts with my word kept.',
                k: 'I\'ll know I\'m done when today\'s earliest commitment is done and ticked off.',
                sw: [
                  'Pick today\'s earliest commitment, so I know where to start.',
                  'Do it before anything optional, so my promises come first.',
                  'Tick it off, so I see my record.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when it\'s done.',
                  'I\'ll know I\'m done when it\'s ticked.',
                ],
              },
              {
                t: "The person given a new date and my list updated", s: ['Tell the person early', 'Offer a new date', 'Update my list'],
                w: 'Renegotiate a commitment I cannot keep, so I fix it early and honestly.',
                k: 'I\'ll know I\'m done when the person has a new date and my list is updated.',
                sw: [
                  'Tell the person early, so they have time to adjust.',
                  'Offer a new date, so they have a plan.',
                  'Update my list, so my records are true.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve told them.',
                  'I\'ll know I\'m done when I\'ve offered one.',
                  'I\'ll know I\'m done when the list is updated.',
                ],
              },
              {
                t: "25 minutes on the avoided commitment done, next step set", s: ['Pick the one I most dread', 'Work on it for 25 minutes', 'Finish it or set the next step'],
                w: 'Finish a commitment I have been avoiding, so I stop letting it hang over me.',
                k: 'I\'ll know I\'m done when I\'ve worked on it for 25 minutes and finished it or set the next step.',
                sw: [
                  'Pick the one I most dread, so I tackle the hardest.',
                  'Work on it for 25 minutes, so I make real progress.',
                  'Finish it or set the next step, so it keeps moving.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when 25 minutes have passed.',
                  'I\'ll know I\'m done when it\'s finished or the step is scheduled.',
                ],
              },
            ],
          },
          {
            t: 'Review my reliability at the end of the week', c: 'weekly',
            p: 'Reliability is a record, not a mood.',
            w: 'Review my reliability at the end of the week, so I see whether my word is good.',
            k: 'I\'ll know I\'m done when I\'ve counted my kept and missed commitments, found causes and made amends.',
            d: [
              {
                t: "Kept and missed commitments counted for the week", s: ['Open my list for the week', 'Count how many I kept', 'Count how many I missed'],
                w: 'Count kept and broken commitments, so I see my reliability in numbers.',
                k: 'I\'ll know I\'m done when I have counts for kept and missed commitments this week.',
                sw: [
                  'Open my list for the week, so I have the facts.',
                  'Count how many I kept, so I see my successes.',
                  'Count how many I missed, so I see my failures.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s open.',
                  'I\'ll know I\'m done when I have a number.',
                  'I\'ll know I\'m done when I have a number.',
                ],
              },
              {
                t: "Cause of my biggest miss written, with a prevention", s: ['Pick the biggest miss', 'Write what actually caused it', 'Write how to prevent it'],
                w: 'Find the cause of misses, so I know how to stop missing.',
                k: 'I\'ll know I\'m done when I\'ve written the cause of my biggest miss and how to prevent it.',
                sw: [
                  'Pick the biggest miss, so I focus on what mattered.',
                  'Write what actually caused it, so I find the real reason.',
                  'Write how to prevent it, so it doesn\'t repeat.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when a fix is written.',
                ],
              },
              {
                t: "Everyone I let down given an apology and new date", s: ['List anyone I let down', 'Send an honest apology and a new plan', 'Add the new date to my list'],
                w: 'Make amends where needed, so I repair trust where I let people down.',
                k: 'I\'ll know I\'m done when each person I let down has an honest apology and a new date.',
                sw: [
                  'List anyone I let down, so I know who to repair things with.',
                  'Send an honest apology and a new plan, so I repair trust.',
                  'Add the new date to my list, so I keep my new word.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when each is sent.',
                  'I\'ll know I\'m done when it\'s on my list.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Act with honesty even when it is awkward: five times',
        w: 'Act with honesty even when it is awkward: five times, so my character needs no explanation.',
        k: 'I\'ll know I\'m done when I\'ve acted honestly in five awkward moments this month.',
        a: [
          {
            t: 'Tell the truth in one difficult moment', c: '2×/week',
            p: 'Honesty that costs something is the only kind that counts.',
            w: 'Tell the truth in one difficult moment, so I practise honesty where it costs me.',
            k: 'I\'ll know I\'m done when I\'ve told the truth in a difficult moment twice this week.',
            d: [
              {
                t: "A small lie named, its reason found, stopping decided", s: ['Recall a small lie or exaggeration', 'Note why I told it', 'Decide to stop that one'],
                w: 'Spot a place where I bend the truth, so I know where honesty needs work.',
                k: 'I\'ll know I\'m done when I\'ve named a small lie, why I told it and decided to stop it.',
                sw: [
                  'Recall a small lie or exaggeration, so I find a real example.',
                  'Note why I told it, so I understand the pull.',
                  'Decide to stop that one, so I commit to change.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve named one.',
                  'I\'ll know I\'m done when the reason is written.',
                  'I\'ll know I\'m done when I\'ve decided.',
                ],
              },
              {
                t: "The plain, kind version said to the person", s: ['Write the plain, kind version', 'Say it to the person', 'Stay calm after I say it'],
                w: 'Say the honest thing kindly, so I tell the truth in a difficult moment.',
                k: 'I\'ll know I\'m done when I\'ve said the plain, kind version to the person.',
                sw: [
                  'Write the plain, kind version, so I know what to say.',
                  'Say it to the person, so they hear the truth.',
                  'Stay calm after I say it, so the truth lands well.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when I\'ve said it.',
                  'I\'ll know I\'m done when the conversation ends calmly.',
                ],
              },
              {
                t: "My mistake admitted, with an offer to fix it", s: ['Pick a mistake I have not admitted', 'Tell the person involved', 'Offer to put it right'],
                w: 'Own up to a mistake, so I tell the truth when it costs me most.',
                k: 'I\'ll know I\'m done when I\'ve told the person and offered to put it right.',
                sw: [
                  'Pick a mistake I have not admitted, so I own a real one.',
                  'Tell the person involved, so they know.',
                  'Offer to put it right, so I repair the damage.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when I\'ve told them.',
                  'I\'ll know I\'m done when I\'ve made the offer.',
                ],
              },
            ],
          },
          {
            t: 'Give credit and take responsibility openly', c: 'weekly',
            p: 'Credit given freely, blame owned quickly.',
            w: 'Give credit and take responsibility openly, so I\'m honest about who did what.',
            k: 'I\'ll know I\'m done when I\'ve given credit and owned a failure openly this week.',
            d: [
              {
                t: "Someone thanked in front of others for what they did", s: ['Think of someone whose help I took', 'Thank them in front of others', 'Say what exactly they did'],
                w: 'Give credit to someone, so I\'m honest about who helped me.',
                k: 'I\'ll know I\'m done when I\'ve thanked someone in front of others and said exactly what they did.',
                sw: [
                  'Think of someone whose help I took, so I find who deserves credit.',
                  'Thank them in front of others, so the credit is public.',
                  'Say what exactly they did, so my thanks is specific.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve named them.',
                  'I\'ll know I\'m done when I\'ve done it.',
                  'I\'ll know I\'m done when I\'ve said it.',
                ],
              },
              {
                t: "A failure owned aloud, with what I'll do differently", s: ['Pick something that went wrong that was mine', 'Say "that was my mistake"', 'Say what I will do differently'],
                w: 'Own a failure without excuses, so I take responsibility openly.',
                k: 'I\'ll know I\'m done when I\'ve said it was my mistake and what I\'ll do differently.',
                sw: [
                  'Pick something that went wrong that was mine, so I own a real failure.',
                  'Say "that was my mistake", so I own it plainly.',
                  'Say what I will do differently, so I show I\'ll change.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when I\'ve said it.',
                  'I\'ll know I\'m done when I\'ve said it.',
                ],
              },
              {
                t: "Each excuse marked true or false, false ones rewritten", s: ['List the excuses I used this week', 'Mark which ones were true', 'Rewrite the false ones honestly'],
                w: 'Check my excuses, so I stop hiding behind them.',
                k: 'I\'ll know I\'m done when I\'ve marked each excuse true or false and rewritten the false ones.',
                sw: [
                  'List the excuses I used this week, so I see them.',
                  'Mark which ones were true, so I separate reasons from excuses.',
                  'Rewrite the false ones honestly, so I say what really happened.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when each is marked.',
                  'I\'ll know I\'m done when each is rewritten.',
                ],
              },
            ],
          },
          {
            t: 'Do the right thing when no one is watching', c: 'daily',
            p: 'Character is what you do unobserved.',
            w: 'Do the right thing when no one is watching, so my honesty is real.',
            k: 'I\'ll know I\'m done when I\'ve done the right thing unobserved on every day this week.',
            d: [
              {
                t: "A shortcut declined and the task done properly", s: ['Spot a shortcut I could take unseen', 'Decide not to take it', 'Do the task properly'],
                w: 'Find a private test of integrity, so I do the right thing when it\'s easy to cheat.',
                k: 'I\'ll know I\'m done when I\'ve spotted a shortcut, declined it and done the task properly.',
                sw: [
                  'Spot a shortcut I could take unseen, so I find the test.',
                  'Decide not to take it, so I choose integrity.',
                  'Do the task properly, so I do the right thing unobserved.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve spotted one.',
                  'I\'ll know I\'m done when I\'ve decided.',
                  'I\'ll know I\'m done when the task is done.',
                ],
              },
              {
                t: "A small unseen flaw fully fixed, told to no one", s: ['Pick a small flaw in my work', 'Fix it fully', 'Tell no one'],
                w: 'Fix a small thing no one would notice, so I act with integrity unseen.',
                k: 'I\'ll know I\'m done when a small flaw is fully fixed and I\'ve told no one.',
                sw: [
                  'Pick a small flaw in my work, so I find something to fix.',
                  'Fix it fully, so it\'s done properly.',
                  'Tell no one, so the act is for its own sake.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when it\'s fixed.',
                  'I\'ll know I\'m done when I\'ve kept quiet.',
                ],
              },
              {
                t: "Today's private choices listed, proud ones and changeable ones marked", s: ['List private choices I made today', 'Mark those I am proud of', 'Mark those I would change'],
                w: 'Review the day\'s private choices, so I see how I acted when unobserved.',
                k: 'I\'ll know I\'m done when I\'ve listed my private choices and marked those I\'m proud of and those I\'d change.',
                sw: [
                  'List private choices I made today, so I see them.',
                  'Mark those I am proud of, so I see my integrity.',
                  'Mark those I would change, so I know what to improve.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when they\'re marked.',
                  'I\'ll know I\'m done when they\'re marked.',
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // ───────────────────────── 11. TELOS personal-v3
  {
    e: 'Having an inner life that drives everything else forward.', deity: 'elis',
    w: 'I\'m building an inner life that drives everything else forward, so my energy, work and relationships come from something deep and steady.',
    k: 'I\'ll know I\'m there when my mornings are grounded, I can state my purpose and my days reflect it.',
    g: [
      {
        t: 'Keep a daily morning practice for 20 days this month',
        w: 'Keep a daily morning practice for 20 days this month, so I have an inner life that drives everything else.',
        k: 'I\'ll know I\'m done when I\'ve done my morning practice on at least 20 days this month.',
        a: [
          {
            t: 'Start the day with 15 minutes of quiet', c: 'daily · 15 min',
            p: 'How the morning begins, the day follows.',
            w: 'Start the day with 15 minutes of quiet, so my morning practice begins with stillness.',
            k: 'I\'ll know I\'m done when I\'ve had 15 minutes of morning quiet on every day this week.',
            d: [
              {
                t: "15 minutes of screen-free morning quiet sat", s: ['Wake before the household if I can', 'Sit with a drink and no screens', 'Stay for 15 minutes'],
                w: 'Do the morning quiet time, so I start today with 15 minutes of stillness.',
                k: 'I\'ll know I\'m done when I\'ve sat for 15 minutes this morning with no screens.',
                sw: [
                  'Wake before the household if I can, so I get the quiet to myself.',
                  'Sit with a drink and no screens, so my quiet time is calm.',
                  'Stay for 15 minutes, so I get the full quiet time.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'m up before the others, or as early as I can.',
                  'I\'ll know I\'m done when I\'m seated with my drink and no screen.',
                  'I\'ll know I\'m done when 15 minutes have passed.',
                ],
              },
              {
                t: "Earlier alarm set, morning things out, phone out of bedroom", s: ['Set my alarm 15 minutes earlier', 'Lay out what I need for the morning', 'Leave my phone out of the bedroom'],
                w: 'Prepare the night before, so tomorrow\'s quiet time is easy to start.',
                k: 'I\'ll know I\'m done when the earlier alarm is set, my morning things are laid out and my phone is out of the bedroom.',
                sw: [
                  'Set my alarm 15 minutes earlier, so I have the time.',
                  'Lay out what I need for the morning, so I can start right away.',
                  'Leave my phone out of the bedroom, so I\'m not pulled in at dawn.',
                ],
                sk: [
                  'I\'ll know I\'m done when the alarm is set.',
                  'I\'ll know I\'m done when it\'s laid out.',
                  'I\'ll know I\'m done when it\'s charging elsewhere.',
                ],
              },
              {
                t: "One sentence of intention written and read before leaving", s: ['Ask who I want to be today', 'Write one sentence of intention', 'Read it before leaving'],
                w: 'Set my intention for the day, so my morning quiet time points my day somewhere.',
                k: 'I\'ll know I\'m done when one sentence of intention is written and read before I leave.',
                sw: [
                  'Ask who I want to be today, so my intention is personal.',
                  'Write one sentence of intention, so it\'s clear.',
                  'Read it before leaving, so I carry it into the day.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve answered.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when I\'ve read it.',
                ],
              },
            ],
          },
          {
            t: 'Write three pages or ten lines each morning', c: 'daily · 10 min',
            p: 'Clearing the mind makes room for the day.',
            w: 'Write three pages or ten lines each morning, so I clear my mind for the day.',
            k: 'I\'ll know I\'m done when I\'ve written in my notebook every morning this week.',
            d: [
              {
                t: "10 minutes of unedited morning writing done", s: ['Open a notebook', 'Write whatever is on my mind', 'Do not stop to edit'],
                w: 'Write freely for 10 minutes, so I clear my mind this morning.',
                k: 'I\'ll know I\'m done when I\'ve written for 10 minutes without editing.',
                sw: [
                  'Open a notebook, so I\'m ready to write.',
                  'Write whatever is on my mind, so I clear my head.',
                  'Do not stop to edit, so the writing flows.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s open.',
                  'I\'ll know I\'m done when I\'ve written steadily.',
                  'I\'ll know I\'m done when the 10 minutes are done.',
                ],
              },
              {
                t: "What I want from today, blocks and my response written", s: ['Write what I most want from today', 'Write what might get in the way', 'Write how I want to respond'],
                w: 'Write about what matters today, so my writing points my day.',
                k: 'I\'ll know I\'m done when I\'ve written what I want from today, what may block me and how I\'ll respond.',
                sw: [
                  'Write what I most want from today, so I know my aim.',
                  'Write what might get in the way, so I\'m ready for obstacles.',
                  'Write how I want to respond, so I\'m prepared.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                ],
              },
              {
                t: "One useful line underlined and copied to my task list", s: ['Reread what I wrote', 'Underline one useful line', 'Copy it to my task list'],
                w: 'Pick out a line to carry forward, so my morning writing feeds my day.',
                k: 'I\'ll know I\'m done when one useful line is underlined and copied to my task list.',
                sw: [
                  'Reread what I wrote, so I see what\'s there.',
                  'Underline one useful line, so I find what to carry.',
                  'Copy it to my task list, so I see it through the day.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read it.',
                  'I\'ll know I\'m done when one is underlined.',
                  'I\'ll know I\'m done when it\'s copied.',
                ],
              },
            ],
          },
          {
            t: 'Do a short movement or stretch routine', c: 'daily · 5 min',
            p: 'The body carries the mind.',
            w: 'Do a short movement or stretch routine, so my body carries my morning practice.',
            k: 'I\'ll know I\'m done when I\'ve done my morning movement on every day this week.',
            d: [
              {
                t: "5 minutes of stretching and slow breathing done", s: ['Stand up and roll my shoulders', 'Stretch my back, hips and legs', 'Breathe slowly throughout'],
                w: 'Do a 5 minute stretch, so I wake my body this morning.',
                k: 'I\'ll know I\'m done when I\'ve stretched for 5 minutes and breathed slowly.',
                sw: [
                  'Stand up and roll my shoulders, so I loosen up.',
                  'Stretch my back, hips and legs, so my whole body wakes.',
                  'Breathe slowly throughout, so the stretch calms me.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve rolled them.',
                  'I\'ll know I\'m done when each area is stretched.',
                  'I\'ll know I\'m done when 5 minutes are done.',
                ],
              },
              {
                t: "Two minutes of daylight outside taken", s: ['Step outside', 'Look at the sky', 'Take five slow breaths'],
                w: 'Go outside for two minutes of daylight, so my body and mind wake with the day.',
                k: 'I\'ll know I\'m done when I\'ve spent two minutes outside looking at the sky and breathing.',
                sw: [
                  'Step outside, so I\'m in daylight.',
                  'Look at the sky, so I take in the morning.',
                  'Take five slow breaths, so I settle.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'m outside.',
                  'I\'ll know I\'m done when I\'ve looked.',
                  'I\'ll know I\'m done when I\'ve taken five.',
                ],
              },
              {
                t: "Stretch done right after my cue, repeat planned for tomorrow", s: ['Pick the cue, like the kettle boiling', 'Do the stretch right after the cue', 'Repeat it tomorrow'],
                w: 'Tie movement to my morning cue, so my stretch becomes automatic.',
                k: 'I\'ll know I\'m done when I\'ve stretched right after my cue and planned to repeat it tomorrow.',
                sw: [
                  'Pick the cue, like the kettle boiling, so my stretch has a trigger.',
                  'Do the stretch right after the cue, so the two link.',
                  'Repeat it tomorrow, so the habit forms.',
                ],
                sk: [
                  'I\'ll know I\'m done when a cue is picked.',
                  'I\'ll know I\'m done when I\'ve done it.',
                  'I\'ll know I\'m done when tomorrow\'s stretch is planned.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Name my driving purpose in one sentence and test it',
        w: 'Name my driving purpose in one sentence and test it, so I know what drives my inner life.',
        k: 'I\'ll know I\'m done when my purpose is one sentence long and I\'ve tested it against real choices.',
        a: [
          {
            t: 'Write about what energises me', c: '3×/week',
            p: 'Energy shows where purpose lives.',
            w: 'Write about what energises me, so I find the purpose that drives me.',
            k: 'I\'ll know I\'m done when I\'ve written about what energises me on three days this week.',
            d: [
              {
                t: "Recent alive moments listed and what they share marked", s: ['Set a 10 minute timer', 'List moments from the last month', 'Mark what they have in common'],
                w: 'List when I felt most alive recently, so I find clues to my purpose.',
                k: 'I\'ll know I\'m done when a list of recent moments is written and what they share is marked.',
                sw: [
                  'Set a 10 minute timer, so I write without overthinking.',
                  'List moments from the last month, so I find real examples.',
                  'Mark what they have in common, so I spot the pattern.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when the common thread is marked.',
                ],
              },
              {
                t: "Ten free-of-charge activities written, helpful and growing ones circled", s: ['Write ten activities I would do for free', 'Circle those that help others', 'Circle those that grow me'],
                w: 'List what I would do without payment, so I find what I\'m drawn to for its own sake.',
                k: 'I\'ll know I\'m done when ten activities are written and those that help others or grow me are circled.',
                sw: [
                  'Write ten activities I would do for free, so I find what pulls me.',
                  'Circle those that help others, so I see my service side.',
                  'Circle those that grow me, so I see my growth side.',
                ],
                sk: [
                  'I\'ll know I\'m done when ten are written.',
                  'I\'ll know I\'m done when they\'re circled.',
                  'I\'ll know I\'m done when they\'re circled.',
                ],
              },
              {
                t: "A one-sentence purpose draft that feels true aloud", s: ['Combine what energises me and what helps others', 'Write a one-sentence draft', 'Say it aloud and check it feels true'],
                w: 'Draft my purpose sentence, so I put my purpose into words.',
                k: 'I\'ll know I\'m done when a one-sentence draft is written and feels true when said aloud.',
                sw: [
                  'Combine what energises me and what helps others, so my purpose uses both.',
                  'Write a one-sentence draft, so my purpose is short and clear.',
                  'Say it aloud and check it feels true, so I know it\'s mine.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve combined them.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when I\'ve said it.',
                ],
              },
            ],
          },
          {
            t: 'Test my purpose sentence against real choices', c: 'weekly',
            p: 'Purpose is proven in choices, not statements.',
            w: 'Test my purpose sentence against real choices, so I know it\'s true to how I live.',
            k: 'I\'ll know I\'m done when I\'ve checked my week against it and improved its wording.',
            d: [
              {
                t: "This week's items marked as serving my purpose or not", s: ['Open this week\'s calendar', 'Mark what serves my purpose', 'Mark what does not'],
                w: 'Check this week\'s plans against it, so I see if my plans serve my purpose.',
                k: 'I\'ll know I\'m done when this week\'s items are marked as serving my purpose or not.',
                sw: [
                  'Open this week\'s calendar, so I see the week.',
                  'Mark what serves my purpose, so I see what fits.',
                  'Mark what does not, so I see what doesn\'t fit.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s open.',
                  'I\'ll know I\'m done when those are marked.',
                  'I\'ll know I\'m done when those are marked.',
                ],
              },
              {
                t: "The option my purpose favours chosen, cost and gift written", s: ['Pick a decision I face', 'Choose the option my purpose favours', 'Write what it cost and gave'],
                w: 'Make one choice because of it, so my purpose proves itself in a real decision.',
                k: 'I\'ll know I\'m done when I\'ve chosen the option my purpose favours and written its cost and gift.',
                sw: [
                  'Pick a decision I face, so I test it on something real.',
                  'Choose the option my purpose favours, so I act on it.',
                  'Write what it cost and gave, so I learn from it.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when I\'ve chosen.',
                  'I\'ll know I\'m done when both are written.',
                ],
              },
              {
                t: "False words cut from my purpose sentence, new version saved", s: ['Reread my purpose sentence', 'Cut any word that feels false', 'Save the new version'],
                w: 'Improve the wording, so my sentence says what\'s true.',
                k: 'I\'ll know I\'m done when false words are cut and the new version is saved.',
                sw: [
                  'Reread my purpose sentence, so I judge it.',
                  'Cut any word that feels false, so it\'s honest.',
                  'Save the new version, so I keep it.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read it.',
                  'I\'ll know I\'m done when false words are gone.',
                  'I\'ll know I\'m done when it\'s saved.',
                ],
              },
            ],
          },
          {
            t: 'Share my purpose with one person', c: 'weekly',
            p: 'Saying it aloud makes it real.',
            w: 'Share my purpose with one person, so saying it aloud makes my purpose real.',
            k: 'I\'ll know I\'m done when I\'ve told one person my purpose and adjusted it from their answer.',
            d: [
              {
                t: "An honest listener picked, 15 minutes asked, topic shared", s: ['Pick someone who will be honest', 'Ask for 15 minutes', 'Tell them what I want to share'],
                w: 'Choose the listener, so I know who hears my purpose.',
                k: 'I\'ll know I\'m done when I\'ve picked an honest person, asked for 15 minutes and said what I want to share.',
                sw: [
                  'Pick someone who will be honest, so I get a real response.',
                  'Ask for 15 minutes, so the talk is set.',
                  'Tell them what I want to share, so they come prepared.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve named them.',
                  'I\'ll know I\'m done when I\'ve asked.',
                  'I\'ll know I\'m done when I\'ve told them.',
                ],
              },
              {
                t: "My purpose sentence said and their answer heard", s: ['Say my purpose sentence', 'Ask whether it sounds like me', 'Listen to their answer'],
                w: 'Say it and ask for a response, so I hear whether my purpose sounds like me.',
                k: 'I\'ll know I\'m done when I\'ve said my sentence and heard their answer.',
                sw: [
                  'Say my purpose sentence, so it\'s said aloud.',
                  'Ask whether it sounds like me, so I get honest feedback.',
                  'Listen to their answer, so I take it in.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve said it.',
                  'I\'ll know I\'m done when I\'ve asked.',
                  'I\'ll know I\'m done when they\'ve finished.',
                ],
              },
              {
                t: "Their view noted, my sentence edited, them thanked", s: ['Note where they agreed or doubted', 'Edit my sentence', 'Thank them'],
                w: 'Adjust based on what they said, so my purpose gets sharper.',
                k: 'I\'ll know I\'m done when I\'ve noted their view, edited my sentence and thanked them.',
                sw: [
                  'Note where they agreed or doubted, so I see what to fix.',
                  'Edit my sentence, so it reflects what I learned.',
                  'Thank them, so they know I valued it.',
                ],
                sk: [
                  'I\'ll know I\'m done when both are noted.',
                  'I\'ll know I\'m done when it\'s edited.',
                  'I\'ll know I\'m done when I\'ve thanked them.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Connect my daily work to what matters most to me',
        w: 'Connect my daily work to what matters most to me, so my inner life drives my daily effort.',
        k: 'I\'ll know I\'m done when I can link my daily work to my purpose and I\'m spending time on what matters most.',
        a: [
          {
            t: 'Link each day\'s top task to my purpose', c: 'daily · 2 min',
            p: 'Meaning makes effort lighter.',
            w: 'Link each day\'s top task to my purpose, so my daily work feels meaningful.',
            k: 'I\'ll know I\'m done when I\'ve linked my top task to my purpose on every day this week.',
            d: [
              {
                t: "Today's top task linked to my purpose before starting", s: ['Look at today\'s top task', 'Write how it serves what matters', 'Read it before I start'],
                w: 'Write the link for today\'s top task, so today\'s work connects to what matters.',
                k: 'I\'ll know I\'m done when the link is written and read before I start.',
                sw: [
                  'Look at today\'s top task, so I know what to link.',
                  'Write how it serves what matters, so I see the link.',
                  'Read it before I start, so I begin with meaning.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve picked it out.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when I\'ve read it.',
                ],
              },
              {
                t: "A disliked task reframed by who it helps, and done", s: ['Pick a task I dislike', 'Write who or what it helps', 'Do the task with that in mind'],
                w: 'Reframe a boring task, so I find meaning in it.',
                k: 'I\'ll know I\'m done when I\'ve written who or what it helps and done the task.',
                sw: [
                  'Pick a task I dislike, so I choose a real example.',
                  'Write who or what it helps, so I see its purpose.',
                  'Do the task with that in mind, so it feels worthwhile.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when the task is done.',
                ],
              },
              {
                t: "A pointless task deleted or delegated", s: ['Review my task list', 'Find a task that serves nothing important', 'Delete or delegate it'],
                w: 'Drop a task with no link, so I stop spending effort on what doesn\'t matter.',
                k: 'I\'ll know I\'m done when a pointless task is deleted or delegated.',
                sw: [
                  'Review my task list, so I see all my tasks.',
                  'Find a task that serves nothing important, so I find what to drop.',
                  'Delete or delegate it, so it\'s off my plate.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve reviewed it.',
                  'I\'ll know I\'m done when one is found.',
                  'I\'ll know I\'m done when it\'s gone.',
                ],
              },
            ],
          },
          {
            t: 'Spend one hour on what matters most', c: '3×/week · 1 hr',
            p: 'What matters most must never be at the mercy of what matters least.',
            w: 'Spend one hour on what matters most, so my time goes to my purpose.',
            k: 'I\'ll know I\'m done when I\'ve spent an hour on what matters most on three days this week.',
            d: [
              {
                t: "One hour on what matters most done before urgent tasks", s: ['Choose the activity that matters most', 'Block one hour for it', 'Do it before the urgent tasks'],
                w: 'Do my important hour, so I spend an hour on what matters most.',
                k: 'I\'ll know I\'m done when I\'ve done one hour on my chosen activity before the urgent tasks.',
                sw: [
                  'Choose the activity that matters most, so I know what the hour is for.',
                  'Block one hour for it, so it has a slot.',
                  'Do it before the urgent tasks, so it isn\'t crowded out.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s chosen.',
                  'I\'ll know I\'m done when the hour is blocked.',
                  'I\'ll know I\'m done when the hour is done.',
                ],
              },
              {
                t: "A lower task moved, alerts silent, hour started on time", s: ['Move one lower-value task', 'Silence all alerts', 'Start on time'],
                w: 'Clear the way for the hour, so nothing blocks my important hour.',
                k: 'I\'ll know I\'m done when a lower-value task is moved, alerts are silent and I start on time.',
                sw: [
                  'Move one lower-value task, so I free the time.',
                  'Silence all alerts, so I\'m not interrupted.',
                  'Start on time, so I get the whole hour.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s moved.',
                  'I\'ll know I\'m done when alerts are off.',
                  'I\'ll know I\'m done when I begin at the set time.',
                ],
              },
              {
                t: "Progress and avoidance written, next important hour booked", s: ['Write what I moved forward', 'Note what I avoided', 'Book the next important hour'],
                w: 'Review progress on what matters, so I see if my hours are paying off.',
                k: 'I\'ll know I\'m done when I\'ve written what moved forward and what I avoided, and booked the next hour.',
                sw: [
                  'Write what I moved forward, so I see progress.',
                  'Note what I avoided, so I see my blind spots.',
                  'Book the next important hour, so it keeps happening.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s booked.',
                ],
              },
            ],
          },
          {
            t: 'Recharge in a way that feeds my inner life', c: 'weekly',
            p: 'An empty well drives nothing.',
            w: 'Recharge in a way that feeds my inner life, so I have the energy to drive my days.',
            k: 'I\'ll know I\'m done when I\'ve done one real recharge this week.',
            d: [
              {
                t: "A restoring activity chosen and scheduled this week", s: ['List activities that restore me', 'Choose the most restoring one', 'Schedule it for this week'],
                w: 'Pick a recharging activity, so I know how I\'ll recharge.',
                k: 'I\'ll know I\'m done when a restoring activity is chosen and scheduled this week.',
                sw: [
                  'List activities that restore me, so I see my options.',
                  'Choose the most restoring one, so I pick what works best.',
                  'Schedule it for this week, so it happens.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when it\'s scheduled.',
                ],
              },
              {
                t: "A full hour of recharging done, energy change noted", s: ['Switch off notifications', 'Give it a full hour', 'Note how my energy changes'],
                w: 'Do the recharging activity, so I refill my energy.',
                k: 'I\'ll know I\'m done when I\'ve done it for a full hour and noted how my energy changed.',
                sw: [
                  'Switch off notifications, so I\'m not interrupted.',
                  'Give it a full hour, so it really restores me.',
                  'Note how my energy changes, so I see what it does.',
                ],
                sk: [
                  'I\'ll know I\'m done when they\'re off.',
                  'I\'ll know I\'m done when the hour is up.',
                  'I\'ll know I\'m done when one line is written.',
                ],
              },
              {
                t: "Recharged energy spent on one hard task, next recharge planned", s: ['Note what I feel ready to do', 'Use that energy on one hard task', 'Plan the next recharge'],
                w: 'Carry the energy into the week, so my recharge fuels my work.',
                k: 'I\'ll know I\'m done when I\'ve used the energy on one hard task and planned the next recharge.',
                sw: [
                  'Note what I feel ready to do, so I see where my energy points.',
                  'Use that energy on one hard task, so it\'s put to work.',
                  'Plan the next recharge, so I keep refilling.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when the task is started or done.',
                  'I\'ll know I\'m done when it\'s scheduled.',
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // ───────────────────────── 12. TELOS personal-v4
  {
    e: 'Having complete ownership of who I am and who I\'m becoming.', deity: 'meta',
    w: 'I\'m building complete ownership of who I am and who I\'m becoming, so I stop waiting on others and steer my own life.',
    k: 'I\'ll know I\'m there when I own my results, live by my own code and keep changing on purpose.',
    g: [
      {
        t: 'Take full responsibility for three areas I have been blaming others on',
        w: 'Take full responsibility for three areas I have been blaming others on, so I own who I am and who I\'m becoming.',
        k: 'I\'ll know I\'m done when I\'ve owned my part and acted in three areas I used to blame others on.',
        a: [
          {
            t: 'Catch myself blaming and reword it as my part', c: 'daily',
            p: 'You can only change what you own.',
            w: 'Catch myself blaming and reword it as my part, so I start owning the areas I blame others on.',
            k: 'I\'ll know I\'m done when I\'ve caught and reworded my blaming thoughts on every day this week.',
            d: [
              {
                t: "Each blame sentence from today reworded as my part", s: ['Note each time I blame someone or something', 'Write the blame sentence', 'Rewrite it as my part in it'],
                w: 'Notice blame in my thinking today, so I see where I hand away responsibility.',
                k: 'I\'ll know I\'m done when each blame sentence from today is written and reworded as my part.',
                sw: [
                  'Note each time I blame someone or something, so I catch it.',
                  'Write the blame sentence, so I see my exact words.',
                  'Rewrite it as my part in it, so I own my share.',
                ],
                sk: [
                  'I\'ll know I\'m done when each instance is noted.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s rewritten.',
                ],
              },
              {
                t: "Blame areas, targets and my strongest three written", s: ['List areas where I feel stuck', 'Write who I blame in each', 'Pick the three with the strongest blame'],
                w: 'List the areas where I blame others, so I know where to take responsibility.',
                k: 'I\'ll know I\'m done when areas, targets of blame and my three strongest areas are written.',
                sw: [
                  'List areas where I feel stuck, so I find where I blame.',
                  'Write who I blame in each, so I name the pattern.',
                  'Pick the three with the strongest blame, so I choose my three areas.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when each has a name.',
                  'I\'ll know I\'m done when three are picked.',
                ],
              },
              {
                t: "My share in one area written, plus a different action", s: ['Pick one area', 'Write what I have done or not done', 'Write what I can do differently'],
                w: 'Find my share in one area, so I see what I can change.',
                k: 'I\'ll know I\'m done when I\'ve written what I did or didn\'t do and what I can do differently.',
                sw: [
                  'Pick one area, so I start with one.',
                  'Write what I have done or not done, so I see my part.',
                  'Write what I can do differently, so I know my next move.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                ],
              },
            ],
          },
          {
            t: 'Take one action in each area I own', c: '3×/week',
            p: 'Ownership begins with the next action.',
            w: 'Take one action in each area I own, so owning an area leads to change.',
            k: 'I\'ll know I\'m done when I\'ve taken an action in my areas on three days this week.',
            d: [
              {
                t: "An area and an under-30-minute action chosen for today", s: ['Pick the area I will work on', 'Choose one action under 30 minutes', 'Schedule it today'],
                w: 'Choose a small action in my area, so I know what to do.',
                k: 'I\'ll know I\'m done when an area and an action under 30 minutes are chosen and scheduled today.',
                sw: [
                  'Pick the area I will work on, so I focus.',
                  'Choose one action under 30 minutes, so it\'s doable.',
                  'Schedule it today, so it happens.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when it\'s scheduled.',
                ],
              },
              {
                t: "The action done and its result noted", s: ['Do the action as planned', 'Skip any excuses', 'Note the result'],
                w: 'Do the action, so I act on what I own.',
                k: 'I\'ll know I\'m done when the action is done and the result is noted.',
                sw: [
                  'Do the action as planned, so I follow through.',
                  'Skip any excuses, so nothing blocks me.',
                  'Note the result, so I learn from it.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s done.',
                  'I\'ll know I\'m done when I\'ve done it without excuses.',
                  'I\'ll know I\'m done when it\'s written.',
                ],
              },
              {
                t: "The next action chosen and in my calendar", s: ['Review what the action changed', 'Choose the next action', 'Add it to my calendar'],
                w: 'Plan the next action, so my ownership keeps moving.',
                k: 'I\'ll know I\'m done when the next action is chosen and in my calendar.',
                sw: [
                  'Review what the action changed, so I see its effect.',
                  'Choose the next action, so I keep going.',
                  'Add it to my calendar, so it happens.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve reviewed it.',
                  'I\'ll know I\'m done when it\'s chosen.',
                  'I\'ll know I\'m done when it\'s in my calendar.',
                ],
              },
            ],
          },
          {
            t: 'Tell someone what I am taking responsibility for', c: 'weekly',
            p: 'A public commitment is harder to drop.',
            w: 'Tell someone what I am taking responsibility for, so my commitment is public and harder to drop.',
            k: 'I\'ll know I\'m done when I\'ve told someone what I own and reported back.',
            d: [
              {
                t: "A person picked and contacted, my three points ready", s: ['Pick someone who will keep me honest', 'Message them to arrange a talk', 'Prepare my three points'],
                w: 'Choose who to tell, so I have someone to hold me to my word.',
                k: 'I\'ll know I\'m done when a person is picked, contacted and my three points are ready.',
                sw: [
                  'Pick someone who will keep me honest, so I\'m held accountable.',
                  'Message them to arrange a talk, so it\'s set up.',
                  'Prepare my three points, so the talk is clear.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve named them.',
                  'I\'ll know I\'m done when the message is sent.',
                  'I\'ll know I\'m done when three points are written.',
                ],
              },
              {
                t: "What I own and will do told, check-in requested", s: ['Tell them what I am owning', 'Tell them what I will do this week', 'Ask them to check in with me'],
                w: 'Share my commitment, so what I own becomes public.',
                k: 'I\'ll know I\'m done when I\'ve told them what I own, what I\'ll do this week and asked for a check-in.',
                sw: [
                  'Tell them what I am owning, so they know my commitment.',
                  'Tell them what I will do this week, so there\'s a concrete plan.',
                  'Ask them to check in with me, so I\'m held to it.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve told them.',
                  'I\'ll know I\'m done when I\'ve told them.',
                  'I\'ll know I\'m done when they\'ve agreed.',
                ],
              },
              {
                t: "What I did and didn't do reported, next date set", s: ['Message them what I did', 'Be honest about what I did not do', 'Set the next report date'],
                w: 'Report back, so my commitment is kept honestly.',
                k: 'I\'ll know I\'m done when I\'ve told them what I did and didn\'t do, and set the next date.',
                sw: [
                  'Message them what I did, so they know my progress.',
                  'Be honest about what I did not do, so my report is true.',
                  'Set the next report date, so the accountability continues.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s sent.',
                  'I\'ll know I\'m done when I\'ve said it.',
                  'I\'ll know I\'m done when the date is set.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Write and follow a personal code of five rules',
        w: 'Write and follow a personal code of five rules, so I own who I am with rules I chose.',
        k: 'I\'ll know I\'m done when five rules are written and I\'ve followed them in real situations for the month.',
        a: [
          {
            t: 'Read my five rules every morning', c: 'daily · 2 min',
            p: 'Rules chosen in calm guide you in storms.',
            w: 'Read my five rules every morning, so my code is fresh when the day tests me.',
            k: 'I\'ll know I\'m done when I\'ve read my five rules on every morning this week.',
            d: [
              {
                t: "Five rules written, each in one short sentence", s: ['Think of five times I acted against myself', 'Write a rule that would have prevented each', 'Make each rule one short sentence'],
                w: 'Draft my five rules, so I have a code written in calm.',
                k: 'I\'ll know I\'m done when five rules are written, each in one short sentence.',
                sw: [
                  'Think of five times I acted against myself, so my rules come from real life.',
                  'Write a rule that would have prevented each, so each rule has a purpose.',
                  'Make each rule one short sentence, so they\'re easy to remember.',
                ],
                sk: [
                  'I\'ll know I\'m done when five times are listed.',
                  'I\'ll know I\'m done when five rules are written.',
                  'I\'ll know I\'m done when each is one sentence.',
                ],
              },
              {
                t: "Rules card written, placed in view and read aloud once", s: ['Write the rules on a card', 'Place the card on my desk or mirror', 'Read it aloud once'],
                w: 'Put the rules where I see them, so I read my rules every morning.',
                k: 'I\'ll know I\'m done when the card is written, in place and read aloud once.',
                sw: [
                  'Write the rules on a card, so I can carry them.',
                  'Place the card on my desk or mirror, so I see them daily.',
                  'Read it aloud once, so my morning reading starts.',
                ],
                sk: [
                  'I\'ll know I\'m done when the card is written.',
                  'I\'ll know I\'m done when the card is in place.',
                  'I\'ll know I\'m done when I\'ve read it.',
                ],
              },
              {
                t: "The rule most likely tested picked, with a plan", s: ['Read the rules', 'Pick the one most likely to be tested today', 'Plan how I will follow it'],
                w: 'Apply a rule to today, so my code shapes my day.',
                k: 'I\'ll know I\'m done when I\'ve picked the rule most likely to be tested and planned how to follow it.',
                sw: [
                  'Read the rules, so they\'re fresh.',
                  'Pick the one most likely to be tested today, so I\'m ready.',
                  'Plan how I will follow it, so I act on it.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read them.',
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when the plan is written.',
                ],
              },
            ],
          },
          {
            t: 'Follow one rule in a real situation', c: 'daily',
            p: 'A rule is only real once it costs you something.',
            w: 'Follow one rule in a real situation, so my code proves itself in real life.',
            k: 'I\'ll know I\'m done when I\'ve followed a rule in a real situation on every day this week.',
            d: [
              {
                t: "A rule followed in one real moment, feelings noted", s: ['Spot a moment that tests a rule', 'Act as the rule says', 'Note how it felt'],
                w: 'Follow the rule when it is tested, so my rule holds in a real moment.',
                k: 'I\'ll know I\'m done when I\'ve acted as a rule says in one real moment and noted how it felt.',
                sw: [
                  'Spot a moment that tests a rule, so I know where to act.',
                  'Act as the rule says, so I follow my code.',
                  'Note how it felt, so I learn from it.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve spotted one.',
                  'I\'ll know I\'m done when I\'ve done so.',
                  'I\'ll know I\'m done when one line is written.',
                ],
              },
              {
                t: "A hard rule followed today, with its cost noted", s: ['Find a rule I find hard to follow', 'Follow it anyway today', 'Note what it cost'],
                w: 'Follow it when it is inconvenient, so my rule holds even when it\'s hard.',
                k: 'I\'ll know I\'m done when I\'ve followed a hard rule today and noted what it cost.',
                sw: [
                  'Find a rule I find hard to follow, so I test myself.',
                  'Follow it anyway today, so my code holds.',
                  'Note what it cost, so I see the price of integrity.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is found.',
                  'I\'ll know I\'m done when I\'ve followed it.',
                  'I\'ll know I\'m done when the cost is written.',
                ],
              },
              {
                t: "Rules marked kept or broken, weakest one planned for", s: ['Go through each rule at night', 'Mark kept or broken', 'Plan tomorrow around the weakest one'],
                w: 'Review the day against the rules, so I see how well I kept my code.',
                k: 'I\'ll know I\'m done when each rule is marked kept or broken and tomorrow is planned around the weakest.',
                sw: [
                  'Go through each rule at night, so I check them all.',
                  'Mark kept or broken, so my record is honest.',
                  'Plan tomorrow around the weakest one, so I improve.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve gone through all five.',
                  'I\'ll know I\'m done when each is marked.',
                  'I\'ll know I\'m done when a plan is written.',
                ],
              },
            ],
          },
          {
            t: 'Revise my code once a week', c: 'weekly',
            p: 'A code should grow with the person.',
            w: 'Revise my code once a week, so my rules grow with me.',
            k: 'I\'ll know I\'m done when I\'ve reviewed my broken rules and updated my card this week.',
            d: [
              {
                t: "Each broken rule kept, reworded or replaced", s: ['List rules I broke this week', 'Ask whether the rule or I was wrong', 'Decide to keep, reword or replace it'],
                w: 'Review the rules I broke, so I learn whether the rule or I was wrong.',
                k: 'I\'ll know I\'m done when each broken rule is kept, reworded or replaced.',
                sw: [
                  'List rules I broke this week, so I see them.',
                  'Ask whether the rule or I was wrong, so I judge fairly.',
                  'Decide to keep, reword or replace it, so my code improves.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when I\'ve answered for each.',
                  'I\'ll know I\'m done when I\'ve decided for each.',
                ],
              },
              {
                t: "The weakest rule rewritten and said aloud", s: ['Choose the weakest rule', 'Rewrite it more clearly', 'Say the new wording aloud'],
                w: 'Test a rule\'s wording, so my weakest rule gets clearer.',
                k: 'I\'ll know I\'m done when the weakest rule is rewritten and said aloud.',
                sw: [
                  'Choose the weakest rule, so I improve it first.',
                  'Rewrite it more clearly, so it\'s easier to follow.',
                  'Say the new wording aloud, so I hear if it works.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when it\'s rewritten.',
                  'I\'ll know I\'m done when I\'ve said it.',
                ],
              },
              {
                t: "The updated card in place of the old one", s: ['Rewrite the card with any changes', 'Replace the old card', 'Read it once'],
                w: 'Update my card, so my card shows my current code.',
                k: 'I\'ll know I\'m done when the new card replaces the old one.',
                sw: [
                  'Rewrite the card with any changes, so it\'s up to date.',
                  'Replace the old card, so only the current code remains.',
                  'Read it once, so the new wording sinks in.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s rewritten.',
                  'I\'ll know I\'m done when the old card is gone.',
                  'I\'ll know I\'m done when I\'ve read it.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Make three changes in who I am becoming, one per week',
        w: 'Make three changes in who I am becoming, one per week, so I own my growth on purpose.',
        k: 'I\'ll know I\'m done when I\'ve practised three deliberate changes, one for each week.',
        a: [
          {
            t: 'Choose and practise one change per week', c: 'daily',
            p: 'Identity is built by what you repeatedly do.',
            w: 'Choose and practise one change per week, so I change who I am becoming.',
            k: 'I\'ll know I\'m done when I\'ve practised this week\'s change on every day.',
            d: [
              {
                t: "The easiest of three behaviours of my future self picked", s: ['Ask who I want to become', 'List three behaviours that person has', 'Pick the easiest one to start'],
                w: 'Choose the change for this week, so I know what I\'m becoming.',
                k: 'I\'ll know I\'m done when I\'ve picked the easiest of three behaviours of the person I want to be.',
                sw: [
                  'Ask who I want to become, so I have a direction.',
                  'List three behaviours that person has, so I have candidates.',
                  'Pick the easiest one to start, so I begin well.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve answered.',
                  'I\'ll know I\'m done when three are listed.',
                  'I\'ll know I\'m done when one is picked.',
                ],
              },
              {
                t: "The chosen behaviour done once today and ticked off", s: ['Do the behaviour once today', 'Tick it off in my tracker', 'Note how it felt'],
                w: 'Practise the change today, so I act as who I\'m becoming.',
                k: 'I\'ll know I\'m done when I\'ve done the behaviour once today and ticked it off.',
                sw: [
                  'Do the behaviour once today, so I practise it.',
                  'Tick it off in my tracker, so I keep a record.',
                  'Note how it felt, so I learn from it.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve done it.',
                  'I\'ll know I\'m done when it\'s ticked.',
                  'I\'ll know I\'m done when one line is written.',
                ],
              },
              {
                t: "One obstacle removed and one cue added", s: ['Spot what makes it hard', 'Remove one obstacle', 'Add one cue to remind me'],
                w: 'Make the change easier, so the change is easy to keep.',
                k: 'I\'ll know I\'m done when one obstacle is removed and one cue is added.',
                sw: [
                  'Spot what makes it hard, so I know the obstacle.',
                  'Remove one obstacle, so it\'s easier.',
                  'Add one cue to remind me, so I remember.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve named it.',
                  'I\'ll know I\'m done when it\'s removed.',
                  'I\'ll know I\'m done when the cue is in place.',
                ],
              },
            ],
          },
          {
            t: 'Track whether I did it', c: 'daily · 1 min',
            p: 'What you count, you keep.',
            w: 'Track whether I did it, so I know whether I\'m really becoming who I chose.',
            k: 'I\'ll know I\'m done when my tracker is marked on every day this week.',
            d: [
              {
                t: "A seven-day grid labelled and placed where I see it", s: ['Draw a grid of seven days', 'Label it with my change', 'Place it where I see it'],
                w: 'Set up a daily tracker, so I have a place to track my change.',
                k: 'I\'ll know I\'m done when a seven-day grid is labelled and placed where I see it.',
                sw: [
                  'Draw a grid of seven days, so I have a box for each day.',
                  'Label it with my change, so I know what I\'m tracking.',
                  'Place it where I see it, so I don\'t forget.',
                ],
                sk: [
                  'I\'ll know I\'m done when the grid is drawn.',
                  'I\'ll know I\'m done when it\'s labelled.',
                  'I\'ll know I\'m done when it\'s in place.',
                ],
              },
              {
                t: "Today marked done or missed, with a reason if missed", s: ['Open the tracker', 'Mark done or missed', 'Write why if missed'],
                w: 'Mark today honestly, so my tracker tells the truth.',
                k: 'I\'ll know I\'m done when today is marked done or missed, with a reason if missed.',
                sw: [
                  'Open the tracker, so I can record today.',
                  'Mark done or missed, so my record is true.',
                  'Write why if missed, so I learn from slips.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s open.',
                  'I\'ll know I\'m done when today is marked.',
                  'I\'ll know I\'m done when the reason is written, or I didn\'t miss.',
                ],
              },
              {
                t: "Days done counted, miss pattern spotted, next week adjusted", s: ['Count the days done', 'Spot the pattern of misses', 'Adjust next week\'s plan'],
                w: 'Review the week\'s tracker, so I see how my change is going.',
                k: 'I\'ll know I\'m done when I\'ve counted the days done, spotted the pattern of misses and adjusted next week.',
                sw: [
                  'Count the days done, so I know my result.',
                  'Spot the pattern of misses, so I find the cause.',
                  'Adjust next week\'s plan, so I improve.',
                ],
                sk: [
                  'I\'ll know I\'m done when I have a number.',
                  'I\'ll know I\'m done when the pattern is named.',
                  'I\'ll know I\'m done when the plan is adjusted.',
                ],
              },
            ],
          },
          {
            t: 'Reflect on who I am becoming', c: 'weekly',
            p: 'Notice the person emerging.',
            w: 'Reflect on who I am becoming, so I notice the person emerging.',
            k: 'I\'ll know I\'m done when I\'ve written a weekly reflection and chosen next week\'s change.',
            d: [
              {
                t: "What I did differently, others' responses and my feelings written", s: ['Write what I did differently', 'Write how people responded', 'Write how I felt about myself'],
                w: 'Write about this week\'s change, so I see how I\'m changing.',
                k: 'I\'ll know I\'m done when I\'ve written what I did differently, how people responded and how I felt.',
                sw: [
                  'Write what I did differently, so I see the change.',
                  'Write how people responded, so I see the effect.',
                  'Write how I felt about myself, so I see how I\'m growing.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                ],
              },
              {
                t: "What has and hasn't changed in me in a month", s: ['Reread an old journal entry', 'Note what has changed in me', 'Note what has not'],
                w: 'Compare myself with a month ago, so I see how far I\'ve come.',
                k: 'I\'ll know I\'m done when I\'ve written what has changed in me and what has not.',
                sw: [
                  'Reread an old journal entry, so I see my past self.',
                  'Note what has changed in me, so I see my growth.',
                  'Note what has not, so I see what still needs work.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read one.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                ],
              },
              {
                t: "Next week's behaviour picked, scheduled and shared", s: ['Pick the next behaviour', 'Write when I will do it', 'Share it with someone'],
                w: 'Choose next week\'s change, so my becoming continues.',
                k: 'I\'ll know I\'m done when the next behaviour is picked, scheduled and shared.',
                sw: [
                  'Pick the next behaviour, so I know what\'s next.',
                  'Write when I will do it, so it happens.',
                  'Share it with someone, so I\'m accountable.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when the time is written.',
                  'I\'ll know I\'m done when I\'ve told them.',
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // ───────────────────────── 13. TELOS personal-v5
  {
    e: 'Having a self I built on purpose, not by accident.', deity: 'meta',
    w: 'I\'m building a self I built on purpose, not by accident, so my habits, company and choices all point at the person I want to be.',
    k: 'I\'ll know I\'m there when my daily habits and surroundings match my written design and I keep refining it.',
    g: [
      {
        t: 'Design the person I want to be in one page and live by it',
        w: 'Design the person I want to be in one page and live by it, so I build my self on purpose.',
        k: 'I\'ll know I\'m done when a one-page design is written and I\'ve acted on it every week this month.',
        a: [
          {
            t: 'Write for 15 minutes on who I want to be', c: '3×/week',
            p: 'What is not designed gets decided by default.',
            w: 'Write for 15 minutes on who I want to be, so I design the person I want to be in one page.',
            k: 'I\'ll know I\'m done when I\'ve written for 15 minutes on three days this week.',
            d: [
              {
                t: "15 minutes on my ideal self written, top traits underlined", s: ['Set a 15 minute timer', 'Write how this person acts, thinks and speaks', 'Underline the five strongest traits'],
                w: 'Describe my ideal self, so I put the person I want to be into words.',
                k: 'I\'ll know I\'m done when 15 minutes of description are written and the five strongest traits are underlined.',
                sw: [
                  'Set a 15 minute timer, so I write without overthinking.',
                  'Write how this person acts, thinks and speaks, so my ideal self is concrete.',
                  'Underline the five strongest traits, so I see what defines them.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when the description is written.',
                  'I\'ll know I\'m done when five are underlined.',
                ],
              },
              {
                t: "My ideal self's morning and evening written and compared", s: ['Set a 15 minute timer', 'Write the morning and evening of this person', 'Compare them with mine'],
                w: 'Describe my daily routines, so I see how my ideal self lives each day.',
                k: 'I\'ll know I\'m done when their morning and evening are written and compared with mine.',
                sw: [
                  'Set a 15 minute timer, so I write with focus.',
                  'Write the morning and evening of this person, so their routines are clear.',
                  'Compare them with mine, so I see the gap.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when both are written.',
                  'I\'ll know I\'m done when I\'ve noted the differences.',
                ],
              },
              {
                t: "Three expected hard moments and my ideal responses written", s: ['Set a 15 minute timer', 'Write three hard moments I expect', 'Write how this person responds to each'],
                w: 'Describe how this person handles hard moments, so my design covers how I act under pressure.',
                k: 'I\'ll know I\'m done when three hard moments and the responses of my ideal self are written.',
                sw: [
                  'Set a 15 minute timer, so I write with focus.',
                  'Write three hard moments I expect, so I use real situations.',
                  'Write how this person responds to each, so my design includes tough moments.',
                ],
                sk: [
                  'I\'ll know I\'m done when the timer is running.',
                  'I\'ll know I\'m done when three are written.',
                  'I\'ll know I\'m done when three responses are written.',
                ],
              },
            ],
          },
          {
            t: 'Do one thing my ideal self would do', c: 'daily',
            p: 'Act like the person you intend to be.',
            w: 'Do one thing my ideal self would do, so I live by my one-page design.',
            k: 'I\'ll know I\'m done when I\'ve done something my ideal self would do on every day this week.',
            d: [
              {
                t: "One action from my ideal-self description done before noon", s: ['Read my one-page description', 'Pick one action from it', 'Do it before noon'],
                w: 'Ask what my ideal self would do today, so my design shapes my day.',
                k: 'I\'ll know I\'m done when I\'ve picked one action from my description and done it before noon.',
                sw: [
                  'Read my one-page description, so my ideal self is fresh in mind.',
                  'Pick one action from it, so I know what to do.',
                  'Do it before noon, so I act early.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read it.',
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when it\'s done.',
                ],
              },
              {
                t: "A usually avoided action done for 10 minutes", s: ['Choose an action I tend to avoid', 'Do it for 10 minutes', 'Note how it felt'],
                w: 'Do the harder version, so I stretch toward my ideal self.',
                k: 'I\'ll know I\'m done when I\'ve done an action I usually avoid for 10 minutes.',
                sw: [
                  'Choose an action I tend to avoid, so I stretch myself.',
                  'Do it for 10 minutes, so I make a real start.',
                  'Note how it felt, so I learn from it.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when 10 minutes are done.',
                  'I\'ll know I\'m done when one line is written.',
                ],
              },
              {
                t: "The action, a lesson and tomorrow's action written", s: ['Write the action in my notes', 'Write one thing I learned', 'Pick tomorrow\'s action'],
                w: 'Record what I did, so I keep a record of living by my design.',
                k: 'I\'ll know I\'m done when the action, a lesson and tomorrow\'s action are written.',
                sw: [
                  'Write the action in my notes, so there\'s a record.',
                  'Write one thing I learned, so I get something from it.',
                  'Pick tomorrow\'s action, so I keep going.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s picked.',
                ],
              },
            ],
          },
          {
            t: 'Review the gap between design and reality', c: 'weekly',
            p: 'Honest review keeps the design alive.',
            w: 'Review the gap between design and reality, so my design and my life keep matching.',
            k: 'I\'ll know I\'m done when I\'ve scored myself against the page and updated it this week.',
            d: [
              {
                t: "Each trait scored 1 to 5 against my page", s: ['Reread my one-page design', 'Score each trait from 1 to 5', 'Note one example behind each score'],
                w: 'Score myself against the page, so I see how close I am.',
                k: 'I\'ll know I\'m done when each trait is scored from 1 to 5 with an example.',
                sw: [
                  'Reread my one-page design, so it\'s fresh.',
                  'Score each trait from 1 to 5, so I measure the gap.',
                  'Note one example behind each score, so my scores are honest.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve read it.',
                  'I\'ll know I\'m done when each trait has a score.',
                  'I\'ll know I\'m done when each score has an example.',
                ],
              },
              {
                t: "Lowest score, its cause and one change found", s: ['Find the lowest score', 'Write what keeps it low', 'Write one change to make'],
                w: 'Pick the biggest gap, so I know what to change first.',
                k: 'I\'ll know I\'m done when I\'ve found the lowest score, its cause and one change to make.',
                sw: [
                  'Find the lowest score, so I see the biggest gap.',
                  'Write what keeps it low, so I find the cause.',
                  'Write one change to make, so I know what to do.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s found.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                ],
              },
              {
                t: "Design page edited, dated copy saved, new version reread", s: ['Edit the page to match what I have learned', 'Save a dated copy', 'Reread the new version'],
                w: 'Update the design, so my page stays true.',
                k: 'I\'ll know I\'m done when the page is edited, a dated copy is saved and I\'ve reread it.',
                sw: [
                  'Edit the page to match what I have learned, so it stays true.',
                  'Save a dated copy, so I keep a history.',
                  'Reread the new version, so I take it in.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s edited.',
                  'I\'ll know I\'m done when it\'s saved.',
                  'I\'ll know I\'m done when I\'ve read it.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Remove three automatic habits that do not serve who I want to be',
        w: 'Remove three automatic habits that do not serve who I want to be, so I build my self on purpose.',
        k: 'I\'ll know I\'m done when three unhelpful automatic habits are replaced or under control.',
        a: [
          {
            t: 'Notice an automatic habit as it happens', c: 'daily',
            p: 'A habit seen is a habit half changed.',
            w: 'Notice an automatic habit as it happens, so I can start removing habits that don\'t serve me.',
            k: 'I\'ll know I\'m done when I\'ve caught my automatic habit as it happened on every day this week.',
            d: [
              {
                t: "My automatic habits listed, ones to drop marked", s: ['Walk through a typical day in my mind', 'List habits I do without thinking', 'Mark those I would like to drop'],
                w: 'List my automatic habits, so I see which habits run me.',
                k: 'I\'ll know I\'m done when my habits are listed and the ones I want to drop are marked.',
                sw: [
                  'Walk through a typical day in my mind, so I find my habits.',
                  'List habits I do without thinking, so I name them.',
                  'Mark those I would like to drop, so I know my targets.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve gone through the day.',
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when they\'re marked.',
                ],
              },
              {
                t: "One habit chosen, with its trigger and payoff written", s: ['Choose the habit with the most cost', 'Write what triggers it', 'Write what I get from it'],
                w: 'Pick the habit to change first, so I start with the habit that costs me most.',
                k: 'I\'ll know I\'m done when I\'ve chosen one habit and written its trigger and what I get from it.',
                sw: [
                  'Choose the habit with the most cost, so I tackle what matters.',
                  'Write what triggers it, so I know when it starts.',
                  'Write what I get from it, so I understand why it persists.',
                ],
                sk: [
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when the trigger is written.',
                  'I\'ll know I\'m done when it\'s written.',
                ],
              },
              {
                t: "Every start of the habit today noted and counted", s: ['Watch for the trigger today', 'Note each time the habit starts', 'Count how many times it happened'],
                w: 'Catch the habit in the act, so I see how often it runs.',
                k: 'I\'ll know I\'m done when I\'ve noted each time it started today and counted them.',
                sw: [
                  'Watch for the trigger today, so I\'m alert.',
                  'Note each time the habit starts, so I record it.',
                  'Count how many times it happened, so I have a baseline.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve watched all day.',
                  'I\'ll know I\'m done when each instance is noted.',
                  'I\'ll know I\'m done when I have a number.',
                ],
              },
            ],
          },
          {
            t: 'Replace the habit with a better action', c: 'daily',
            p: 'You cannot remove a habit, only replace it.',
            w: 'Replace the habit with a better action, so the habit I remove is replaced by something useful.',
            k: 'I\'ll know I\'m done when I\'ve used my replacement at the trigger on every day this week.',
            d: [
              {
                t: "An easy replacement for the trigger chosen and set up", s: ['Write what I could do at the same trigger', 'Choose the easiest option', 'Set it up in advance'],
                w: 'Choose the replacement, so I know what to do instead.',
                k: 'I\'ll know I\'m done when I\'ve picked an easy replacement for the trigger and set it up.',
                sw: [
                  'Write what I could do at the same trigger, so I have options.',
                  'Choose the easiest option, so it\'s easy to start.',
                  'Set it up in advance, so it\'s ready.',
                ],
                sk: [
                  'I\'ll know I\'m done when options are listed.',
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when it\'s set up.',
                ],
              },
              {
                t: "The replacement done at the trigger, feelings noted", s: ['Wait for the trigger', 'Do the replacement instead', 'Note how it felt'],
                w: 'Use the replacement, so I do the new action in place of the old habit.',
                k: 'I\'ll know I\'m done when I\'ve done the replacement at the trigger and noted how it felt.',
                sw: [
                  'Wait for the trigger, so I\'m ready when it comes.',
                  'Do the replacement instead, so I act differently.',
                  'Note how it felt, so I learn from it.',
                ],
                sk: [
                  'I\'ll know I\'m done when the trigger has come.',
                  'I\'ll know I\'m done when I\'ve done it.',
                  'I\'ll know I\'m done when one line is written.',
                ],
              },
              {
                t: "A barrier added, a cue removed, someone told", s: ['Add a barrier to the old habit', 'Remove a cue that starts it', 'Tell someone what I am changing'],
                w: 'Make the old habit harder, so the old habit is less likely to run.',
                k: 'I\'ll know I\'m done when a barrier is added, a cue is removed and someone knows.',
                sw: [
                  'Add a barrier to the old habit, so it takes effort.',
                  'Remove a cue that starts it, so it fires less.',
                  'Tell someone what I am changing, so I\'m accountable.',
                ],
                sk: [
                  'I\'ll know I\'m done when the barrier is in place.',
                  'I\'ll know I\'m done when the cue is gone.',
                  'I\'ll know I\'m done when I\'ve told them.',
                ],
              },
            ],
          },
          {
            t: 'Review progress on the habit', c: 'weekly',
            p: 'Progress is easier to see on paper.',
            w: 'Review progress on the habit, so I see whether the habit is really going.',
            k: 'I\'ll know I\'m done when I\'ve counted this week, found what worked and decided what comes next.',
            d: [
              {
                t: "Old-habit uses counted and compared with last week", s: ['Open my tally', 'Count times I used the old habit', 'Compare with last week'],
                w: 'Count this week\'s results, so I see the habit\'s trend.',
                k: 'I\'ll know I\'m done when I\'ve counted old-habit uses this week and compared them with last week.',
                sw: [
                  'Open my tally, so I have the data.',
                  'Count times I used the old habit, so I know this week\'s number.',
                  'Compare with last week, so I see the trend.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s open.',
                  'I\'ll know I\'m done when I have a number.',
                  'I\'ll know I\'m done when I\'ve compared them.',
                ],
              },
              {
                t: "Best and worst day compared, one adjustment written", s: ['Pick my best day and worst day', 'Write what differed', 'Write one adjustment'],
                w: 'Find what made it work or fail, so I learn what to repeat.',
                k: 'I\'ll know I\'m done when I\'ve compared my best and worst day and written one adjustment.',
                sw: [
                  'Pick my best day and worst day, so I compare extremes.',
                  'Write what differed, so I find the cause.',
                  'Write one adjustment, so next week is better.',
                ],
                sk: [
                  'I\'ll know I\'m done when both are picked.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                ],
              },
              {
                t: "Habit control decided and next week's approach planned", s: ['Decide if this habit is under control', 'Choose the next habit or double down', 'Plan next week\'s approach'],
                w: 'Move on to the next habit or reinforce this one, so my habit work keeps moving.',
                k: 'I\'ll know I\'m done when I\'ve decided if this habit is under control and planned next week.',
                sw: [
                  'Decide if this habit is under control, so I know where I stand.',
                  'Choose the next habit or double down, so I know my focus.',
                  'Plan next week\'s approach, so I keep going.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve decided.',
                  'I\'ll know I\'m done when I\'ve chosen.',
                  'I\'ll know I\'m done when the plan is written.',
                ],
              },
            ],
          },
        ],
      },
      {
        t: 'Surround myself with people and inputs that shape the self I want',
        w: 'Surround myself with people and inputs that shape the self I want, so my surroundings help build my self on purpose.',
        k: 'I\'ll know I\'m done when my regular company and inputs include people and sources that raise my standards.',
        a: [
          {
            t: 'Spend time with someone who lifts my standards', c: 'weekly',
            p: 'You become the average of your surroundings.',
            w: 'Spend time with someone who lifts my standards, so the people around me pull me up.',
            k: 'I\'ll know I\'m done when I\'ve spent time with someone who lifts my standards this week.',
            d: [
              {
                t: "An inspiring person picked and messaged to arrange time", s: ['List people who inspire me', 'Pick one I have not seen lately', 'Message them to arrange time'],
                w: 'Choose the person, so I know who I\'ll spend time with.',
                k: 'I\'ll know I\'m done when I\'ve picked an inspiring person and messaged them to arrange time.',
                sw: [
                  'List people who inspire me, so I find candidates.',
                  'Pick one I have not seen lately, so I reconnect.',
                  'Message them to arrange time, so it\'s set up.',
                ],
                sk: [
                  'I\'ll know I\'m done when the list is written.',
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when the message is sent.',
                ],
              },
              {
                t: "Undisturbed talk held, asking their work and what helped them", s: ['Meet or call without distractions', 'Ask what they are working on', 'Ask what has helped them most'],
                w: 'Spend the time well, so our time together lifts my standards.',
                k: 'I\'ll know I\'m done when we\'ve talked undisturbed and I\'ve asked what they\'re working on and what helped them most.',
                sw: [
                  'Meet or call without distractions, so we can really talk.',
                  'Ask what they are working on, so I learn from their work.',
                  'Ask what has helped them most, so I learn their method.',
                ],
                sk: [
                  'I\'ll know I\'m done when we\'re talking undisturbed.',
                  'I\'ll know I\'m done when I\'ve asked.',
                  'I\'ll know I\'m done when I\'ve asked.',
                ],
              },
              {
                t: "What I learned written, one thing chosen, them thanked", s: ['Write what I learned from them', 'Choose one thing to try', 'Thank them with a message'],
                w: 'Take one thing from the time together, so the time changes how I live.',
                k: 'I\'ll know I\'m done when I\'ve written what I learned, chosen one thing to try and thanked them.',
                sw: [
                  'Write what I learned from them, so I keep it.',
                  'Choose one thing to try, so I act on it.',
                  'Thank them with a message, so they know it mattered.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when it\'s sent.',
                ],
              },
            ],
          },
          {
            t: 'Replace a low-value input with a high-value one', c: 'daily',
            p: 'What you feed your mind becomes your mind.',
            w: 'Replace a low-value input with a high-value one, so what I read and watch builds the self I want.',
            k: 'I\'ll know I\'m done when I\'ve swapped a low-value input for a better one on every day this week.',
            d: [
              {
                t: "Top three inputs listed, the one adding nothing marked", s: ['Check my screen-time report', 'List the top three inputs', 'Mark the one that adds nothing'],
                w: 'Audit what I read, watch and scroll, so I see what I\'m feeding my mind.',
                k: 'I\'ll know I\'m done when my top three inputs are listed and the one that adds nothing is marked.',
                sw: [
                  'Check my screen-time report, so I see the facts.',
                  'List the top three inputs, so I see the biggest ones.',
                  'Mark the one that adds nothing, so I know what to replace.',
                ],
                sk: [
                  'I\'ll know I\'m done when the report is open.',
                  'I\'ll know I\'m done when three are listed.',
                  'I\'ll know I\'m done when one is marked.',
                ],
              },
              {
                t: "Low-value input removed, a better one used for 20 minutes", s: ['Remove the low-value input from my phone', 'Choose a better book, podcast or channel', 'Use it for 20 minutes today'],
                w: 'Swap one input, so I feed my mind something better.',
                k: 'I\'ll know I\'m done when the low-value input is gone from my phone and I\'ve used a better one for 20 minutes.',
                sw: [
                  'Remove the low-value input from my phone, so it\'s out of reach.',
                  'Choose a better book, podcast or channel, so I have a replacement.',
                  'Use it for 20 minutes today, so the swap begins.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s removed.',
                  'I\'ll know I\'m done when one is chosen.',
                  'I\'ll know I\'m done when 20 minutes are done.',
                ],
              },
              {
                t: "Feelings and lessons from the swap written, swap kept", s: ['Write how I feel after the better input', 'Write what I learned', 'Keep the swap for next week'],
                w: 'Note the effect of the swap, so I see whether the swap is worth keeping.',
                k: 'I\'ll know I\'m done when I\'ve written how I feel, what I learned and kept the swap for next week.',
                sw: [
                  'Write how I feel after the better input, so I see the effect.',
                  'Write what I learned, so I see the gain.',
                  'Keep the swap for next week, so it lasts.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when it\'s in next week\'s plan.',
                ],
              },
            ],
          },
          {
            t: 'Join one group or project that stretches me', c: 'weekly',
            p: 'Growth happens where standards are higher than your own.',
            w: 'Join one group or project that stretches me, so I grow among people with higher standards.',
            k: 'I\'ll know I\'m done when I\'ve taken part in one stretching group or project this week.',
            d: [
              {
                t: "A group meeting soon picked, with date and place noted", s: ['Search for groups in my area of growth', 'Pick one that meets soon', 'Note the date and place'],
                w: 'Find a group or project, so I know where I\'ll be stretched.',
                k: 'I\'ll know I\'m done when I\'ve picked a group that meets soon and noted its date and place.',
                sw: [
                  'Search for groups in my area of growth, so I see my options.',
                  'Pick one that meets soon, so I can start quickly.',
                  'Note the date and place, so I can go.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'ve searched.',
                  'I\'ll know I\'m done when one is picked.',
                  'I\'ll know I\'m done when both are written.',
                ],
              },
              {
                t: "Group attended, one person met, help offered on one thing", s: ['Go to the meeting or open the project', 'Introduce myself to one person', 'Offer to help with one thing'],
                w: 'Attend or take part, so I join a group that stretches me.',
                k: 'I\'ll know I\'m done when I\'ve attended, met one person and offered to help with one thing.',
                sw: [
                  'Go to the meeting or open the project, so I take part.',
                  'Introduce myself to one person, so I start to belong.',
                  'Offer to help with one thing, so I contribute.',
                ],
                sk: [
                  'I\'ll know I\'m done when I\'m there.',
                  'I\'ll know I\'m done when I\'ve spoken to one person.',
                  'I\'ll know I\'m done when I\'ve offered.',
                ],
              },
              {
                t: "Gains written, continue decided, next date in my calendar", s: ['Write what I gained', 'Decide whether to keep going', 'Put the next date in my calendar'],
                w: 'Decide how to continue, so I choose whether this group keeps stretching me.',
                k: 'I\'ll know I\'m done when I\'ve written what I gained, decided whether to continue and set the next date.',
                sw: [
                  'Write what I gained, so I judge its value.',
                  'Decide whether to keep going, so I commit or move on.',
                  'Put the next date in my calendar, so I keep going.',
                ],
                sk: [
                  'I\'ll know I\'m done when it\'s written.',
                  'I\'ll know I\'m done when I\'ve decided.',
                  'I\'ll know I\'m done when the date is in my calendar.',
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];
